import { logger } from '../../utils/logger.js'
import { LitElement, html, nothing, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { ref, createRef, Ref } from 'lit/directives/ref.js'
import maplibregl from 'maplibre-gl'
import { compassService } from '../../services/compass/compass-provider'
import {
  locationService,
  type LocationServiceView,
} from '../../services/location/location-recording-service.js'
import { LocationPersistenceService } from '../../services/location/location-persistence'
import { SettingsStorage } from '../../storage/settings-storage.js'
import { type UnitSystem } from '../../utils/units.js'
import { type CoordinateFormat, type CoordinatePrecision } from '../../lib/coordinates.js'
import { router } from '../../utils/router.js'
import {
  tripFeatureManager,
  type LatLng as FeatureLatLng,
} from '../../services/trip-feature-manager.js'
import { planStorage } from '../../storage/plan-storage.js'
import { featureStorage } from '../../storage/feature-storage.js'
import { activityStorage } from '../../storage/activity-storage.js'
import { elevationPointService } from '../../services/terrain/elevation-point-service.js'
import type { MapFeature } from '../../schemas/map-feature.js'
import { slopeAngleService } from '../../services/terrain/slope-angle-service.js'
import { sunExposureService } from '../../services/terrain/sun-exposure-service.js'
import { hillshadeService } from '../../services/terrain/hillshade-service.js'
import { DEM_URL, DEM_TILE_SIZE, TERRAIN_MESH_MAX_ZOOM } from '../../services/terrain/dem-config.js'
import { landCoverService } from '../../services/terrain/land-cover-service.js'
import { tileCompositeService } from '../../services/composite/tile-composite-service.js'
import { contourService } from '../../services/terrain/contour-service.js'
import { snowDepthService } from '../../services/terrain/snow-depth-service.js'
import { ALL_NWM_SERVICES } from '../../services/terrain/nwm-service.js'
import {
  registerOmLayers,
  unregisterOmLayers,
  reAddOmLayers,
} from '../../services/terrain/om-layer-service.js'
import { lightningUnifiedService } from '../../services/terrain/lightning-unified-service.js'
import { landOwnershipService } from '../../services/land/land-ownership-service.js'
import { overpassToGeoJSON } from '../../lib/overpass-geometry.js'
import { cellularCoverageService } from '../../services/cellular/cellular-coverage-service.js'
import { emergencyLayerService } from '../../services/emergency/emergency-layer-service.js'
import { iNaturalistService } from '../../services/wildlife/inaturalist-service.js'
import { criticalHabitatService } from '../../services/wildlife/critical-habitat-service.js'
import { cotrexLayerService } from '../../services/cotrex/cotrex-layer-service.js'
import { cotrexGeometryExtractor } from '../../services/cotrex/cotrex-geometry-extractor.js'
import { cotrexClosureService } from '../../services/cotrex/cotrex-closure-service.js'
import { fireLayerService } from '../../services/fire/fire-layer-service.js'
import { avalancheLayerService } from '../../services/avalanche/avalanche-layer-service.js'
import { npsAlertsService } from '../../services/wildlife/nps-alerts-service.js'
import { backcountryOverlayService } from '../../services/backcountry/backcountry-overlay-service.js'
import { openBetaLayerService } from '../../services/openbeta/openbeta-layer-service.js'
import { openBetaSearchService } from '../../services/openbeta/openbeta-search-service.js'
import { routePlannerService } from '../../services/routing/route-planner-service.js'
import type { RouteResult, RouteStrategy } from '../../services/routing/route-planner-types.js'
import { satelliteConstellationService } from '../../services/satellite/satellite-constellation-service.js'
import { losCoverageService } from '../../services/satellite/los-coverage-service.js'
import { graticuleService } from '../../services/reference/graticule-service.js'
import { utmGridService } from '../../services/reference/utm-grid-service.js'
import { mgrsGridService } from '../../services/reference/mgrs-grid-service.js'
import { declinationService } from '../../services/reference/declination-service.js'
import { rangeRingsService } from '../../services/reference/range-rings-service.js'
import { plssService } from '../../services/reference/plss-service.js'
import './cairn-context-bar'
import './map-context-popup'

const log = logger.create('map')
import { showToast } from '../common/cairn-toast.js'
import {
  undoStack,
  executeUndo,
  executeRedo,
  type UndoDeps,
  type UndoOperation,
  type EdgeSnapshot,
} from '../../services/undo-stack.js'
import '../common/confirm-dialog'
import '../common/plan-picker-dialog'
import type { ConfirmDialog } from '../common/confirm-dialog.js'
import './mode-fab'
import './layer-key-fab'
import './shapes/shape-point'
import { buildMarkerHtml } from './shapes/shape-point.js'
import {
  computeLabelStyle,
  computeSplitSegments,
  sliceMetadataForSegments,
  mergeLineCoordinates,
  getJoinEndpoints,
  joinMetadataArrays,
} from '../../lib/shape-utils.js'
import './shapes/shape-circle'
import './shapes/shape-rectangle'
import './shapes/shape-line'
import './shapes/shape-polygon'
import type { BaseLayer, OverlayLayer, LayerSwitcher } from './layer-switcher'
import { baseLayers as baseLayerConfigs } from '../../lib/layer-configs.js'
import type { Feature } from '../../schemas/feature.js'
import { BaseShape } from './shapes/base-shape.js'
import { ShapeLine } from './shapes/shape-line.js'
import { db } from '../../database/schema.js'
import { layerGroupManager } from '../../services/layer-group-manager.js'
import '../common/import-dialog.js'
import type { ImportResult, ImportDialog } from '../common/import-dialog.js'
import { processGpxImport, type GpxImportDeps } from '../../lib/gpx-import-processor.js'
import { isImportableFile } from '../../services/import/import-service.js'
import { authService } from '../../services/auth/auth-service.js'
import { syncChildrenOnNavigate } from '../../storage/sync-manager.js'
import { cairnMapStyles } from './cairn-map-styles.js'
import maplibreCssText from 'maplibre-gl/dist/maplibre-gl.css?inline'
import materialSymbolsCssText from '@material-symbols/font-400/outlined.css?inline'
import { MeasurementManager, type MeasureData } from './managers/measurement-manager.js'
import type { ProfilePoint, ProfileWaypoint } from './elevation-profile.js'
import { LinePolygonDrawingManager } from './managers/line-polygon-drawing-manager.js'
import { DragDrawingManager } from './managers/drag-drawing-manager.js'
import {
  GestureController,
  type GesturePoint,
  type GestureModifiers,
} from './managers/gesture-controller.js'
import { StyleEditorManager } from './managers/style-editor-manager.js'
import { EditHandleManager } from './managers/edit-handle-manager.js'
import { timelineService, type TimelineState } from '../../services/timeline/timeline-service.js'
import { GibsCapabilitiesService } from '../../services/wmts/gibs-capabilities.js'
import { formatWmsTime, formatWmtsSubdailyTime, buildWmsTileUrl } from '../../lib/overlay-utils.js'
import { MapStateManager } from '../../lib/map-state-manager.js'
import {
  cairnifyTileUrl,
  cairnProtocolHandler,
  registerColorKey,
} from '../../services/tile-cache/cairn-protocol.js'
import { tileCacheService } from '../../services/tile-cache/tile-cache-service.js'
import {
  vectorStyleCacheService,
  CACHED_GLYPHS,
} from '../../services/tile-cache/vector-style-cache-service.js'
import { selectionService } from '../../services/selection/selection-service.js'
import { highlightManager } from '../../services/selection/highlight-manager.js'
import { UserFeatureAdapter } from '../../services/selection/adapters/user-feature-adapter.js'
import { CotrexAdapter } from '../../services/selection/adapters/cotrex-adapter.js'
import { BackcountryAdapter } from '../../services/selection/adapters/backcountry-adapter.js'
import {
  getSelectableOverlayLayerIds,
  mapGeoJSONFeatureToRef,
  registerSelectableLayerProvider,
  unregisterSelectableLayerProvider,
} from '../../services/selection/query-helpers.js'
import {
  ensureAnchors,
  addOverlayLayer as addOverlayLayerOrdered,
  addShapeLayer as addShapeLayerOrdered,
  clearOverlayTracking,
  reorderAllOverlays,
  ANCHOR_OVERLAYS,
} from './layer-order-manager.js'

// Register the cairn:// MapLibre protocol once at module load. Safe to call
// multiple times because addProtocol is a no-op if the scheme is already set,
// but we guard anyway in case of HMR.
let cairnProtocolRegistered = false
function ensureCairnProtocolRegistered(): void {
  if (cairnProtocolRegistered) return
  maplibregl.addProtocol('cairn', cairnProtocolHandler)
  registerColorKey('vegetation-disturbance', [{ r: 18, g: 18, b: 18, tolerance: 3 }])
  cairnProtocolRegistered = true
  // Kick off the cache init so the budget counters are ready by the time the
  // first tile is fetched. Fire-and-forget — getTile awaits init internally.
  void tileCacheService.init()
}

// Material Web Components
import '@material/web/button/outlined-button.js'

@customElement('cairn-map')
export class CairnMap extends LitElement {
  static override styles = [
    unsafeCSS(maplibreCssText),
    unsafeCSS(materialSymbolsCssText),
    cairnMapStyles,
  ]

  // ─── Plan context properties (scope-aware map) ─────────────────────
  @property({ type: String }) planId?: string
  @property({ type: String }) routeId?: string
  @property({ type: Object }) initialBounds?: { sw: [number, number]; ne: [number, number] }

  /** Preview mode: read-only, no tools, no interaction */
  @property({ type: Boolean }) preview = false

  /**
   * Opt out of all GPS/compass/location work. When true the map will not
   * request permission, start the foreground location service, subscribe to
   * compass updates, or touch the user's current position in any way. Use
   * this on screens where the map is purely a spatial editor for a
   * pre-defined target (e.g. the plan-map editor reached from the create
   * form) and the user's actual location is irrelevant.
   */
  @property({ type: Boolean }) disableLocation = false

  /** View-only mode: show plan features and overlays but disable drawing tools */
  @property({ type: Boolean }) viewOnly = false

  /** Lock the timeline to a specific date range (e.g. activity recording window) */
  @property({ type: Object }) timelineRange?: { start: Date; end: Date }

  /** External follow position override (e.g. activity timeline position) */
  followPosition?: [number, number]
  private smoothedBearing: number | null = null

  /** Internal: is this map allowed to touch the GPS/compass stack at all? */
  private get locationEnabled(): boolean {
    return !this.preview && !this.disableLocation
  }

  private mapRef: Ref<HTMLDivElement> = createRef()
  private map?: maplibregl.Map
  private mapReadyFired = false
  private resizeObserver?: ResizeObserver
  private searchResultOutlineSourceId = 'search-outline'
  private searchResultOutlineFillLayerId = 'search-outline-fill'
  private searchResultOutlineStrokeLayerId = 'search-outline-stroke'
  private searchResultLabel?: maplibregl.Marker | undefined
  private searchResultMarker?: maplibregl.Marker | undefined
  private searchResultLineLayerId = 'search-outline-line'
  // Shared singleton — same instance the app-level locationService uses, so
  // location/compass native state isn't split across multiple wrappers.
  private compassService = compassService

  private deviceHeading: number | undefined

  // ─── State manager (pure state, no DOM/MapLibre deps) ──────────────
  readonly mapState = new MapStateManager()

  /** Reactive trigger — Lit re-renders when this changes. Read in render(). */
  @state()
  private _v = 0

  private _bump(): void {
    this._v++
  }

  /** Touch _v so Lit tracks it as a render dependency and TS sees it as read. */
  protected get stateVersion(): number {
    return this._v
  }

  // ─── Getters/setters proxying to mapState ──────────────────────────
  private get isLoading(): boolean {
    return this.mapState.state.isLoading
  }
  private set isLoading(v: boolean) {
    this.mapState.setIsLoading(v)
    this._bump()
  }

  private get currentLocation():
    | { lat: number; lng: number; heading: number | undefined; accuracy: number | undefined }
    | undefined {
    return this.mapState.state.currentLocation
  }
  private set currentLocation(v: typeof this.mapState.state.currentLocation) {
    this.mapState.setCurrentLocation(v)
    this._bump()
  }

  private get currentElevation(): number | undefined {
    return this.mapState.state.currentElevation
  }
  private set currentElevation(v: number | undefined) {
    this.mapState.setCurrentElevation(v)
    this._bump()
  }

  private get currentVelocity(): number | undefined {
    return this.mapState.state.currentVelocity
  }
  private set currentVelocity(v: number | undefined) {
    this.mapState.setCurrentVelocity(v)
    this._bump()
  }

  private get currentActivity(): import('cairn-compass').ActivityType | undefined {
    return this.mapState.state.currentActivity
  }
  private set currentActivity(v: import('cairn-compass').ActivityType | undefined) {
    this.mapState.setCurrentActivity(v)
    this._bump()
  }

  private get isRecording(): boolean {
    return this.mapState.state.isRecording
  }
  private set isRecording(v: boolean) {
    this.mapState.setIsRecording(v)
    this._bump()
  }

  private get hasLocationData(): boolean {
    return this.mapState.state.hasLocationData
  }
  private set hasLocationData(v: boolean) {
    this.mapState.setHasLocationData(v)
    this._bump()
  }

  private get locationPermissionGranted(): boolean {
    return this.mapState.state.locationPermissionGranted
  }
  private set locationPermissionGranted(v: boolean) {
    this.mapState.setLocationPermissionGranted(v)
    this._bump()
  }

  private get showLayerSwitcher(): boolean {
    return this.mapState.state.showLayerSwitcher
  }
  private set showLayerSwitcher(v: boolean) {
    if (v && !this.mapState.state.showLayerSwitcher) this.mapState.toggleLayerSwitcher()
    else if (!v && this.mapState.state.showLayerSwitcher) this.mapState.toggleLayerSwitcher()
    this._bump()
  }

  private get unitSystem(): UnitSystem {
    return this.mapState.state.unitSystem
  }
  private set unitSystem(v: UnitSystem) {
    this.mapState.applySettings({ unitSystem: v })
    this._bump()
  }

  private get selectedFeatures(): Feature[] {
    return this.mapState.state.selectedFeatures
  }
  private set selectedFeatures(v: Feature[]) {
    this.mapState.setSelectedFeatures(v)
    this._bump()
  }

  private get contextMode(): 'location' | 'drawing' | 'editing' | 'extending' {
    return this.mapState.state.contextMode
  }
  private set contextMode(v: 'location' | 'drawing' | 'editing' | 'extending') {
    this.mapState.setContextMode(v)
    this._bump()
  }

  private get mapMode(): import('../../services/trip-feature-manager.js').MapMode {
    return this.mapState.state.mapMode
  }
  private set mapMode(v: import('../../services/trip-feature-manager.js').MapMode) {
    this.mapState.setMapMode(v)
    this._bump()
  }

  private get drawSubContext(): 'tools' | 'focused' | 'extending' {
    return this.mapState.state.drawSubContext
  }
  private set drawSubContext(v: 'tools' | 'focused' | 'extending') {
    this.mapState.setDrawSubContext(v)
    this._bump()
  }

  private get selectedDrawingTool(): string | null {
    return this.mapState.state.selectedDrawingTool
  }
  private set selectedDrawingTool(v: string | null) {
    this.mapState.setSelectedDrawingTool(v)
    this._bump()
  }

  private currentBaseSourceId = 'base-tiles'
  private currentBaseLayerId = 'base-tiles-layer'
  private isVectorBase = false
  private overlaySourceIds: Map<string, string> = new Map()
  private overlayLayerIds: Map<string, string> = new Map()
  // Active WMS/tile overlays that carry a gradient legend (e.g. snow coverage), so
  // the Key FAB can show their colour ramp. Keyed by overlay id. Reactive via _v.
  @state() private legendOverlays: Map<string, OverlayLayer> = new Map()
  private timeAwareOverlays: Map<string, OverlayLayer> = new Map()
  private lastOverlayTimePerLayer: Map<string, string> = new Map()
  private lastOverlaySwapTime: Map<string, number> = new Map() // epoch ms of last swap per layer
  private pendingOverlaySwap: Map<string, ReturnType<typeof setTimeout>> = new Map() // deferred swap timers
  private static readonly OVERLAY_SWAP_MIN_INTERVAL_MS = 5000
  private settingsStorage = new SettingsStorage()

  // Drawing functionality
  private featureLayerGroupReady = false
  private featureMarkers: Map<string, maplibregl.Marker> = new Map()
  private shapeComponents: Map<string, BaseShape> = new Map()

  // While set, the next map tap picks the vertex to split this line at.
  private splitModeFeatureId: string | null = null

  private get splitEnabled(): boolean {
    if (this.selectedFeatures.length !== 1) return false
    const f = this.selectedFeatures[0]
    if (!['line', 'track', 'route'].includes(f.type)) return false
    const coords = (f.geometry as GeoJSON.LineString).coordinates
    return Array.isArray(coords) && coords.length >= 3
  }
  private userFeatureAdapter = new UserFeatureAdapter(() => this.shapeComponents)
  private cotrexAdapter = new CotrexAdapter()
  private backcountryAdapter = new BackcountryAdapter()

  // Keyboard handler reference for cleanup
  private boundKeyHandler?: (event: KeyboardEvent) => void

  // Context popup
  private contextPopup: maplibregl.Popup | null = null
  // Whether the pointer cursor we set for an overlay-feature hover is active, so
  // we only clear a cursor we own (and not one a user shape set).
  private overlayHovered = false
  // The mode to return to after finishing an edit entered from the popup (so
  // editing a shape from Explore returns you to Explore, not Draw).
  private editReturnMode: import('../../services/trip-feature-manager.js').MapMode | null = null

  // Mobile context overlay
  private static readonly MOBILE_BREAKPOINT = 768
  @state() private contextOverlayEl: HTMLElement | null = null

  // Mobile detail drawer (pull-up sheet) state.
  @state() private overlayTranslateY = 0
  @state() private overlayExpanded = false
  @state() private overlayDragging = false
  private overlayPeekT = 0
  private overlayDragStartY: number | null = null
  private overlayDragBaseT = 0
  private overlayLastY = 0
  private overlayLastT = 0
  private overlayVY = 0

  // Route planning state
  private routePlanObjective: [number, number] | null = null
  private routePlanStart: [number, number] | null = null
  private routePlanWaypoints: [number, number][] = []
  private routePlanMarkers: maplibregl.Marker[] = []
  private routePlanSourceIds: string[] = []
  private routePlanLayerIds: string[] = []
  @state() private routePlanningActive = false
  @state() private routePlanAddingWaypoints = false
  @state() private routePlanComputing = false
  @state() private routePlanResults: RouteResult[] = []
  @state() private routePlanSelectedIndex = -1

  private get measureDistance(): string | undefined {
    return this.mapState.state.measureDistance
  }
  private get measureBearing(): string | undefined {
    return this.mapState.state.measureBearing
  }
  private get measureArea(): string | undefined {
    return this.mapState.state.measureArea
  }
  private get measurePerimeter(): string | undefined {
    return this.mapState.state.measurePerimeter
  }
  private get measureType(): 'none' | 'line' | 'shape' | 'point' {
    return this.mapState.state.measureType
  }
  /** Measure is a transient state (not a mode): active while a measure exists. */
  private get measureActive(): boolean {
    return this.measureType !== 'none'
  }
  private get measureLocation(): string | undefined {
    return this.mapState.state.measureLocation
  }

  private get coordinateFormat(): CoordinateFormat {
    return this.mapState.state.coordinateFormat
  }
  private set coordinateFormat(v: CoordinateFormat) {
    this.mapState.applySettings({ coordinateFormat: v })
    this._bump()
  }

  private get coordinatePrecision(): CoordinatePrecision {
    return this.mapState.state.coordinatePrecision
  }
  private set coordinatePrecision(v: CoordinatePrecision) {
    this.mapState.applySettings({ coordinatePrecision: v })
    this._bump()
  }

  private get timelineState(): TimelineState {
    return this.mapState.state.timelineState
  }
  private set timelineState(v: TimelineState) {
    this.mapState.setTimelineState(v)
    this._bump()
  }

  private get is3dMode(): boolean {
    return this.mapState.state.is3dMode
  }
  private set is3dMode(v: boolean) {
    this.mapState.setIs3dMode(v)
    this._bump()
  }

  private get isFollowingUser(): boolean {
    return this.mapState.state.isFollowingUser
  }
  private set isFollowingUser(v: boolean) {
    this.mapState.setIsFollowingUser(v)
    this._bump()
  }

  @state() private showImportPanel = false
  @state() private showDropOverlay = false
  // Mid-draw draft feature backing the "create shape" toolbar (null when not drawing
  // a line/polygon, or before the shape is valid enough to commit).
  @state() private creatingFeature: Feature | null = null
  private dragCounter = 0

  // Elevation profile data (from track recording manager)
  @state() private routeProfile: ProfilePoint[] = []
  @state() private trackProfile: ProfilePoint[] = []
  @state() private currentRouteDistance: number | undefined
  @state() private routeWaypoints: ProfileWaypoint[] = []
  @state() private distanceTime: import('./distance-time-chart.js').DistanceTimePoint[] = []
  @state() private aspectCounts: import('./track-aspect-rose.js').AspectCounts = [
    0, 0, 0, 0, 0, 0, 0, 0,
  ]

  // Managers
  private measurementManager = new MeasurementManager({
    onMeasureUpdated: (data: MeasureData) => {
      this.mapState.updateMeasurement(data)
      this._bump()
    },
    onShapeSelected: (shapeId: string, selected: boolean) => {
      const shape = this.shapeComponents.get(shapeId)
      if (shape) {
        shape.setSelected(selected)
      }
    },
    onMeasureAttribute: (attr: string, set: boolean) => {
      if (set) {
        this.setAttribute(attr, '')
      } else {
        this.removeAttribute(attr)
      }
    },
    getUnitSystem: () => this.unitSystem,
    getCoordinateFormat: () => this.coordinateFormat,
    getCoordinatePrecision: () => this.coordinatePrecision,
    disableMapPanning: () => this.disableMapPanning(),
    enableMapPanning: () => this.enableMapPanning(),
  })

  // View bound to the app-level locationService while this map is mounted.
  // These are pure rendering / UI-state callbacks; the GPS service lifecycle
  // and recording state machine live in locationService, so navigating away
  // from the map no longer tears them down. Safe to leave unbound (no-op).
  private locationView: LocationServiceView = {
    onLocationUpdated: location => {
      this.currentLocation = location
      if (this.isFollowingUser && !this.followPosition) {
        locationService.followToCurrentLocation()
      }
    },
    onElevationUpdated: elevation => {
      this.currentElevation = elevation
    },
    onVelocityUpdated: velocity => {
      this.currentVelocity = velocity
    },
    onHasLocationData: () => {
      if (!this.hasLocationData) {
        this.hasLocationData = true
        // Do not auto-enable follow mode here — the user turns it on
        // explicitly via the "zoom to me" / follow button. Auto-enabling
        // caused the map to pan away from a plan's saved location when
        // the first GPS fix arrived.
      }
    },
    onHeadingUpdated: heading => {
      this.deviceHeading = heading
      this.requestUpdate()
    },
    getMapZoom: () => this.map?.getZoom(),
    onRecordingChanged: (isRecording: boolean) => {
      this.isRecording = isRecording
    },
    showNotification: (type, title, message, activityId) =>
      this.showNotification(type, title, message, activityId),
    onFeaturesCreated: features => features.forEach(f => this.renderFeatureOnMap(f)),
    onProfileUpdated: data => {
      this.routeProfile = data.routeProfile
      this.trackProfile = data.trackProfile
      this.currentRouteDistance = data.currentDistance
      this.routeWaypoints = data.waypoints
      this.distanceTime = data.distanceTime
      this.aspectCounts = data.aspectCounts
    },
  }

  /** Whether this map is currently counted as a location consumer. */
  private locationConsumerActive = false

  // The managers now live in the app-level locationService singleton (shared
  // across map instances); these accessors keep existing call sites working.
  private get locationTrackingManager(): typeof locationService.location {
    return locationService.location
  }
  private get trackRecordingManager(): typeof locationService.recording {
    return locationService.recording
  }

  private linePolygonDrawingManager = new LinePolygonDrawingManager({
    onFeatureCreated: feature => this.handleDrawingFeatureCreated(feature),
    onFeatureSelected: featureId => {
      tripFeatureManager.selectFeature(featureId)
      this.updateContextMode()
    },
    updateContextMode: () => this.updateContextMode(),
    getShapeComponent: featureId => this.shapeComponents.get(featureId),
    onDraftChanged: feature => {
      this.creatingFeature = feature
    },
  })

  private dragDrawingManager = new DragDrawingManager({
    onFeatureCreated: feature => this.handleDrawingFeatureCreated(feature),
    onFeatureSelected: featureId => {
      tripFeatureManager.selectFeature(featureId)
      this.updateContextMode()
    },
    updateContextMode: () => this.updateContextMode(),
    getShapeComponent: featureId => this.shapeComponents.get(featureId),
    getShapeComponents: () => this.shapeComponents,
    getSelectedFeatures: () => this.selectedFeatures,
    disableMapPanning: () => this.disableMapPanning(),
    enableMapPanning: () => this.enableMapPanning(),
    disableMapInteraction: () => this.disableMapInteraction(),
    exitAllEditModes: () => this.exitAllEditModes(),
    hideAllEditHandles: () => this.hideAllEditHandles(),
    updateSelectedFeatures: () => this.updateSelectedFeatures(),
    cleanupTempDrawing: () => this.linePolygonDrawingManager.cleanupTempDrawing(),
    onVertexSelectionRect: bounds => this.handleVertexSelectionRect(bounds),
  })

  // Single pointer/touch authority. Recognises tap (identify), long-press /
  // right-click (action menu) and box-select; routes everything to the semantic
  // handlers below. Pan/pinch/double-tap-zoom stay with MapLibre.
  private gestureController = new GestureController({
    onTap: (g, mods) => void this.handleTap(g, mods),
    onActionMenu: g => void this.handleActionMenu(g),
    onBoxSelectStart: g => this.dragDrawingManager.beginBoxSelect(g.lngLat),
    onBoxSelectUpdate: g => this.dragDrawingManager.updateBoxSelect(g.lngLat),
    onBoxSelectEnd: (g, mods) =>
      // Additive when a modifier is held (desktop shift/ctrl) or a selection is
      // already active — so a second box adds rather than replaces (the only way
      // to extend a selection by drag on touch, which has no modifiers). Clear by
      // tapping empty ground.
      void this.dragDrawingManager.endBoxSelect(
        g.lngLat,
        mods.ctrl || mods.meta || mods.shift || this.hasActiveSelection()
      ),
    shouldBoxSelect: () =>
      !(tripFeatureManager.getMapMode() === 'draw' && this.hasActiveDrawTool()),
    // Drawing and measuring want quick taps to land immediately (place a vertex /
    // measure point); explore (and idle draw mode) debounce for double-tap-zoom.
    shouldDebounceTap: () => {
      const mode = tripFeatureManager.getMapMode()
      const immediate = this.measureActive || (mode === 'draw' && this.hasActiveDrawTool())
      return !immediate
    },
    // While a selection is active, a quick tap toggles a feature immediately
    // (no need to hold to arm first) — hold-drag still box-selects.
    selectionActive: () => this.hasActiveSelection(),
    isEnabled: () => !this.routePlanComputing,
  })

  private styleEditorManager = new StyleEditorManager({
    onStateChanged: () => this.requestUpdate(),
    getShapeComponent: featureId => this.shapeComponents.get(featureId),
    getSelectedFeatures: () => this.selectedFeatures,
    updateSelectedFeatures: () => this.updateSelectedFeatures(),
    getHostBoundingRect: () => this.getBoundingClientRect(),
  })

  private searchResultOutline:
    | { sourceId: string; fillLayerId: string; strokeLayerId: string }
    | undefined
  private currentTileLayer: { sourceId: string; layerId: string } | undefined

  private joinPreviewState: {
    reverseA: boolean
    reverseB: boolean
    sourceId: string
    layerId: string
    endpointMarkers: maplibregl.Marker[]
  } | null = null

  private editHandleManager = new EditHandleManager({
    getShapeComponent: featureId => this.shapeComponents.get(featureId),
    refreshFeatureOnMap: featureId => this.refreshFeatureOnMap(featureId),
    updateContextMode: () => this.updateContextMode(),
  })

  private boundHandleSettingsUpdate = ((event: Event): void => {
    this.handleSettingsUpdate(event as CustomEvent)
  }) as EventListener

  // Cross-device settings pull (sync-manager) updated the localStorage settings mirror;
  // re-read units/coordinate format so the map reflects the synced values.
  private boundHandleUserSettingsSynced = (): void => {
    this.loadSettings()
  }

  private boundHandleLocationSearchNavigation = ((event: Event): void => {
    this.handleLocationSearchNavigation(event as CustomEvent)
  }) as EventListener

  private boundHandleLocationPermissionChange = ((event: Event): void => {
    this.handleLocationPermissionChange(event as CustomEvent)
  }) as EventListener

  private boundHandlePopState = (): void => {
    if (this.contextOverlayEl) {
      this.contextOverlayEl = null
    }
  }

  importPendingFile(file: File): void {
    void this.importDroppedFile(file)
  }

  private boundVisibilityHandler = (): void => {
    const isVisible = document.visibilityState === 'visible'
    log.debug(`🧭 Visibility changed to: ${document.visibilityState}`)
    this.compassService.logDiagnostic('VISIBILITY', `changed to ${document.visibilityState}`)
    if (isVisible) {
      void this.compassService.requestCompassUpdates()
    } else {
      void this.compassService.releaseCompassUpdates()
    }
    // Screen-state → GPS params/drain/lifecycle is owned by locationService's
    // own app-level visibility listener (works even when no map is mounted),
    // so we no longer forward it from here.
  }

  private visibilityHandlerRegistered = false

  private timelineListener = (state: Readonly<TimelineState>): void => {
    this.timelineState = { ...state }
    this.updateTimeAwareOverlays(state.currentTime)
  }

  override connectedCallback(): void {
    super.connectedCallback()

    if (this.planId) {
      this.applyPlanScope()
      if (!this.preview) {
        tripFeatureManager.setMapMode(this.viewOnly ? 'map' : 'draw')
        this.updateContextMode()
      }
      void this.loadPlanBounds()
    } else if (this.routeId) {
      this.applyRouteScope()
      tripFeatureManager.setMapMode(this.viewOnly ? 'map' : 'draw')
      this.updateContextMode()
    }

    this.loadSettings()

    // Register timeline listener
    timelineService.addListener(this.timelineListener)

    if (this.timelineRange) {
      timelineService.setTimeRange(this.timelineRange.start, this.timelineRange.end)
      timelineService.setCurrentTime(this.timelineRange.start)
    }

    // Listen for settings changes
    this.addEventListener('settings-updated', this.boundHandleSettingsUpdate)
    // Listen for location search navigation from app header
    this.addEventListener('location-search-navigation', this.boundHandleLocationSearchNavigation)

    // Re-read settings when a cross-device pull updates the local mirror
    window.addEventListener('user-settings-synced', this.boundHandleUserSettingsSynced)

    // Listen for back navigation to close context overlay
    window.addEventListener('popstate', this.boundHandlePopState)

    // Drag-and-drop file import — must be on window to intercept drops
    // before the browser navigates to the file.
    window.addEventListener('dragenter', this.handleDragEnter)
    window.addEventListener('dragover', this.handleDragOver)
    window.addEventListener('dragleave', this.handleDragLeave)
    window.addEventListener('drop', this.handleDrop)

    // Listen for location permission changes
    document.addEventListener(
      'location-permission-changed',
      this.boundHandleLocationPermissionChange
    )

    // Splash screen already resolved permission — read it synchronously.
    // Skip the whole permission dance when location is disabled for this map
    // (preview or explicit disableLocation opt-out) so we never even touch
    // the native GPS stack.
    if (this.locationEnabled) {
      const alreadyGranted = this.compassService.isPermissionGranted
      if (alreadyGranted) {
        this.locationPermissionGranted = true
        tripFeatureManager.setLocationPermission(true)
        this.setAttribute('has-location-permission', '')
        this.updateContextMode()
        void this.startLocationTracking()
      } else {
        void this.checkLocationPermission()
      }
    }
  }

  override firstUpdated(): void {
    void this.initializeMap()
  }

  override updated(changed: Map<string, unknown>): void {
    // Reactively re-fit the map when initialBounds changes in preview mode
    // (e.g. user picks a new location in the create-plan form). Outside of
    // preview we leave camera control to the user.
    if (this.preview && changed.has('initialBounds') && this.map && this.initialBounds) {
      this.map.fitBounds(
        [
          [this.initialBounds.sw[1], this.initialBounds.sw[0]],
          [this.initialBounds.ne[1], this.initialBounds.ne[0]],
        ],
        { padding: 60, animate: false }
      )
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()

    // Clear scope when navigating away from plan/route context
    if (this.planId || this.routeId) {
      tripFeatureManager.setScope(null)
    }

    // Remove settings and location search listeners
    this.removeEventListener('settings-updated', this.boundHandleSettingsUpdate)
    this.removeEventListener('location-search-navigation', this.boundHandleLocationSearchNavigation)

    // Remove drag-and-drop listeners
    window.removeEventListener('dragenter', this.handleDragEnter)
    window.removeEventListener('dragover', this.handleDragOver)
    window.removeEventListener('dragleave', this.handleDragLeave)
    window.removeEventListener('drop', this.handleDrop)

    // Remove popstate listener
    window.removeEventListener('user-settings-synced', this.boundHandleUserSettingsSynced)
    window.removeEventListener('popstate', this.boundHandlePopState)

    // Remove location permission listener
    document.removeEventListener(
      'location-permission-changed',
      this.boundHandleLocationPermissionChange
    )

    // Remove visibility handler to prevent stale handlers from stopping GPS
    if (this.visibilityHandlerRegistered) {
      document.removeEventListener('visibilitychange', this.boundVisibilityHandler)
      this.visibilityHandlerRegistered = false
    }

    // Clean up keyboard handlers
    this.cleanupKeyboardHandlers()

    if (this.map) {
      this.map.getContainer().removeEventListener('mousemove', this.handleNativeMouseMove)
    }
    this.gestureController.dispose()

    // Detach this map from the app-level location/recording service WITHOUT
    // stopping GPS or recording. The service is demand-gated: it only stops if
    // no map is mounted AND no recording is active — so navigating away mid-
    // recording no longer truncates the track.
    locationService.detachView(this.locationView)
    locationService.detachMap()
    if (this.locationConsumerActive) {
      locationService.removeConsumer()
      this.locationConsumerActive = false
    }
    this.linePolygonDrawingManager.dispose()
    this.dragDrawingManager.dispose()

    // Clean up shape components
    this.shapeComponents.forEach(shapeComponent => {
      shapeComponent.remove()
    })
    this.shapeComponents.clear()

    highlightManager.disconnect()
    highlightManager.unregisterAdapter(this.cotrexAdapter)
    highlightManager.unregisterAdapter(this.userFeatureAdapter)
    highlightManager.unregisterAdapter(this.backcountryAdapter)
    unregisterSelectableLayerProvider(cotrexLayerService)
    unregisterSelectableLayerProvider(cotrexClosureService)
    unregisterSelectableLayerProvider(backcountryOverlayService)

    slopeAngleService.unregister()
    sunExposureService.unregister()
    hillshadeService.unregister()
    landCoverService.unregister()
    snowDepthService.unregister()
    ALL_NWM_SERVICES.forEach(s => s.unregister())
    unregisterOmLayers()
    contourService.unregister()
    landOwnershipService.unregister()
    iNaturalistService.unregister()
    criticalHabitatService.unregister()
    cotrexLayerService.unregister()
    cotrexClosureService.unregister()
    fireLayerService.unregister()
    avalancheLayerService.unregister()
    npsAlertsService.unregister()
    backcountryOverlayService.unregister()
    openBetaLayerService.unregister()
    satelliteConstellationService.unregister()
    losCoverageService.unregister()
    tileCompositeService.unregister()
    this.editHandleManager.dispose()
    this.styleEditorManager.dispose()
    this.measurementManager.dispose()
    // NOTE: do NOT dispose the recording manager here — it is app-level and
    // must keep running while a recording is active even with no map mounted.
    timelineService.removeListener(this.timelineListener)

    // Clear pending overlay swap timers
    for (const timer of this.pendingOverlaySwap.values()) {
      clearTimeout(timer)
    }
    this.pendingOverlaySwap.clear()

    if (this.map) {
      this.map.remove()
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
  }

  private loadSettings(): void {
    try {
      const settings = this.settingsStorage.getSettingsWithDefaults()
      this.unitSystem = settings.unitSystem
      this.coordinateFormat = settings.coordinateFormat
      if (settings.coordinatePrecision) {
        this.coordinatePrecision = settings.coordinatePrecision
      }
    } catch (error) {
      log.error('Error loading settings:', error)
      this.unitSystem = 'imperial' // fallback
    }
  }

  private handleSettingsUpdate(event: CustomEvent): void {
    try {
      const settings = event.detail
      if (settings.unitSystem !== this.unitSystem) {
        this.unitSystem = settings.unitSystem
      }
      if (settings.coordinateFormat !== this.coordinateFormat) {
        this.coordinateFormat = settings.coordinateFormat ?? 'DD'
      }
      if (settings.coordinatePrecision) {
        this.coordinatePrecision = settings.coordinatePrecision
      }
      this.requestUpdate()
    } catch (error) {
      log.error('Error handling settings update:', error)
    }
  }

  private async initializeMap(): Promise<void> {
    try {
      if (!this.mapRef.value) {
        return
      }
      ensureCairnProtocolRegistered()
      this.compassService.logDiagnostic('APP', 'initializeMap — WebView loaded')

      // 1. Initialize the map with stored or default location
      const bestLocation = LocationPersistenceService.getBestLocation()

      this.map = new maplibregl.Map({
        container: this.mapRef.value,
        style: {
          version: 8 as const,
          sources: {},
          layers: [],
          glyphs: CACHED_GLYPHS,
        },
        center: [bestLocation.lng, bestLocation.lat],
        zoom: bestLocation.zoom ?? 13,
        attributionControl: false,
        boxZoom: false,
        dragRotate: false,
        pitchWithRotate: true,
        touchPitch: false,
        maxPitch: 60,
        maxTileCacheSize: 100,
        maxTileCacheZoomLevels: 3,
        fadeDuration: 0,
        cancelPendingTileRequestsWhileZooming: true,
        refreshExpiredTiles: false,
        renderWorldCopies: false,
        interactive: !this.preview,
        pixelRatio: Math.min(devicePixelRatio, 2),
        canvasContextAttributes: {
          antialias: false,
          powerPreference: 'high-performance',
        },
      })

      // Explicitly disable all interaction handlers in preview mode
      if (this.preview) {
        this.map.scrollZoom.disable()
        this.map.dragPan.disable()
        this.map.dragRotate.disable()
        this.map.touchZoomRotate.disable()
        this.map.doubleClickZoom.disable()
        this.map.keyboard.disable()
        this.map.touchPitch.disable()
      }

      // If plan context provides initial bounds, fit the map to them
      // Bounds format is { sw: [lat, lng], ne: [lat, lng] }, MapLibre expects [lng, lat]
      // In preview mode with a plan/route, fitToFeatureBounds takes over after
      // features load — but we still want to apply initialBounds for previews
      // without any features (e.g. the create-plan form's location preview).
      if (this.initialBounds && (!this.preview || (!this.planId && !this.routeId))) {
        this.map.fitBounds(
          [
            [this.initialBounds.sw[1], this.initialBounds.sw[0]],
            [this.initialBounds.ne[1], this.initialBounds.ne[0]],
          ],
          { padding: 60, animate: false }
        )
      }

      // Load saved base layer preference, or default to Liberty vector style
      const savedBaseLayerId = localStorage.getItem('cairn_selected_base_layer')
      const savedLayer = baseLayerConfigs.find(l => l.id === savedBaseLayerId)

      const applyRestoredOverlays = async (): Promise<void> => {
        await this.ensureLayerSwitcherLoaded()
        await this.updateComplete
        const ls = this.shadowRoot?.querySelector('layer-switcher') as LayerSwitcher | null
        if (typeof ls?.applyServiceStates === 'function') ls.applyServiceStates()
      }

      const loadBaseLayer = (): void => {
        if (savedLayer?.type === 'vector') {
          vectorStyleCacheService
            .loadStyle(savedLayer.url)
            .then(rewrittenStyle => {
              if (!this.map) return
              void this.map.once('style.load', () => {
                this.reAddOverlaysAndFeatures()
                void applyRestoredOverlays()
              })
              this.map.setStyle(rewrittenStyle)
              this.isVectorBase = true
            })
            .catch(() => {
              this.loadFallbackRasterBase()
              void this.loadExistingFeatures()
              void applyRestoredOverlays()
            })
        } else if (savedLayer) {
          this.addRasterBaseLayer(savedLayer)
          void this.loadExistingFeatures()
          void applyRestoredOverlays()
        } else {
          vectorStyleCacheService
            .loadStyle('https://tiles.openfreemap.org/styles/liberty')
            .then(rewrittenStyle => {
              if (!this.map) return
              void this.map.once('style.load', () => {
                this.reAddOverlaysAndFeatures()
                void applyRestoredOverlays()
              })
              this.map.setStyle(rewrittenStyle)
              this.isVectorBase = true
            })
            .catch(() => {
              this.loadFallbackRasterBase()
              void this.loadExistingFeatures()
              void applyRestoredOverlays()
            })
        }
      }

      if (this.map.isStyleLoaded()) {
        loadBaseLayer()
      } else {
        void this.map.once('style.load', loadBaseLayer)
      }

      log.debug('🗺️ Map initialized with:', {
        lat: bestLocation.lat.toFixed(6),
        lng: bestLocation.lng.toFixed(6),
        zoom: bestLocation.zoom ?? 13,
        source: bestLocation.source,
      })

      this.currentTileLayer = {
        sourceId: this.currentBaseSourceId,
        layerId: this.currentBaseLayerId,
      }

      slopeAngleService.register(this.map)
      sunExposureService.register(this.map)
      hillshadeService.register(this.map, this.cairnTiles.bind(this))
      landCoverService.register(this.map)
      snowDepthService.register(this.map)
      ALL_NWM_SERVICES.forEach(s => s.register(this.map!))
      registerOmLayers(this.map)
      lightningUnifiedService.setMap(this.map)
      contourService.register(this.map)
      landOwnershipService.register(this.map)
      cellularCoverageService.register(this.map)
      emergencyLayerService.register(this.map)
      iNaturalistService.register(this.map)
      criticalHabitatService.register(this.map)
      cotrexLayerService.register(this.map)
      cotrexClosureService.register(this.map)
      fireLayerService.register(this.map)
      avalancheLayerService.register(this.map)
      npsAlertsService.register(this.map)
      backcountryOverlayService.register(this.map)
      openBetaLayerService.register(this.map)
      openBetaSearchService.init()
      registerSelectableLayerProvider(cotrexLayerService)
      registerSelectableLayerProvider(cotrexClosureService)
      registerSelectableLayerProvider(backcountryOverlayService)
      highlightManager.registerAdapter(this.userFeatureAdapter)
      highlightManager.registerAdapter(this.cotrexAdapter)
      highlightManager.registerAdapter(this.backcountryAdapter)
      highlightManager.connect()
      satelliteConstellationService.register(this.map)
      losCoverageService.register(this.map)
      tileCompositeService.register(this.map)
      graticuleService.register(this.map)
      utmGridService.register(this.map)
      mgrsGridService.register(this.map)
      declinationService.register(this.map)
      rangeRingsService.register(this.map)
      plssService.register(this.map, landOwnershipService.getProtocol() ?? undefined)
      this.measurementManager.setMap(this.map)
      locationService.setMap(this.map)
      this.linePolygonDrawingManager.setMap(this.map)
      this.dragDrawingManager.setMap(this.map)
      this.editHandleManager.setMap(this.map)

      // 3. Handle dynamic resizing
      this.setupResizeHandling()

      // Preview / disableLocation maps are static snapshots — skip anything
      // that would move the camera (live location jumpTo, saved-extent
      // persistence) or talk to native plugins (compass, track recording
      // recovery).
      if (this.locationEnabled) {
        // 3.5. Setup compass visibility-based management
        await this.setupCompassVisibilityHandling()

        // 4. Start location tracking (permission prompt handled by native plugin)
        await this.startLocationTracking()

        // 4.5. Resume interrupted track recording (WebView recycling recovery).
        // Skip if recording is already live in the app-level service (e.g. we
        // navigated back to a map mid-recording) — re-resuming would overwrite
        // the in-memory track with a slightly-stale localStorage snapshot.
        if (!locationService.isRecording) {
          await locationService.tryResumeRecording()
        }

        // 4.6. Drain any buffered locations from the native file buffer.
        // After WebView recycling, the visibilitychange handler won't fire
        // (app is already visible), so we must drain explicitly here.
        if (this.trackRecordingManager.isRecording) {
          void this.compassService.flushBufferedLocations()
        }

        // 5. Start compass tracking with platform-optimal provider
        void this.startCompassTracking()

        // 6. Setup map extent persistence
        this.setupMapExtentPersistence()
      }

      // 7. Initialize drawing functionality
      this.initializeDrawingFeatures()

      // 8. Show context bar

      this.isLoading = false

      setTimeout(() => {
        if (this.map) {
          this.map.resize()
        }
      }, 100)

      this.checkUrlContextOverlay()
    } catch (error) {
      log.error('Failed to initialize map:', error)
      this.isLoading = false
    }
  }

  private initializeDrawingFeatures(): void {
    if (!this.map) {
      return
    }

    // Create layer group for features
    this.featureLayerGroupReady = true

    // Disable follow mode on user-initiated pan/drag
    this.map.on('dragstart', () => {
      this.isFollowingUser = false
    })

    // Warm the render loop the instant a finger/pointer lands. With 3D terrain
    // on, MapLibre converts the touch point to a ground location by rendering
    // the terrain coords framebuffer and reading it back; after the map goes
    // idle the webview throttles rAF and frees the terrain render-to-texture,
    // so that first readback at gesture-start stalls (~hundreds of ms of "lag
    // before it starts moving"). triggerRepaint here wakes the loop / rebuilds
    // the RTT one frame ahead of the move events, so the readback runs warm.
    // Only fires on touch — no idle battery cost. (Verified on-device: removes
    // the 3D first-touch lag.)
    this.map.getCanvasContainer().addEventListener(
      'pointerdown',
      () => {
        this.map?.triggerRepaint()
      },
      { passive: true }
    )

    // GestureController is the single pointer/touch authority: it recognises
    // tap / long-press / box-select and routes them to the semantic handlers.
    // Pan, pinch and double-tap/double-click zoom stay with MapLibre.
    this.gestureController.setMap(this.map)
    this.gestureController.setupEventListeners()

    // Double-click still finishes an in-progress line/polygon; native mousemove
    // drives the line/polygon rubber-band preview; map mousemove keeps misc
    // hover behaviour.
    this.map.on('dblclick', this.handleMapDoubleClick.bind(this))
    this.map.on('mousemove', this.handleMapMouseMove.bind(this))
    this.map.getContainer().addEventListener('mousemove', this.handleNativeMouseMove)

    // Circle/rectangle drawing drags (box-select now lives in GestureController).
    this.dragDrawingManager.setupEventListeners()

    // Point features are DOM markers, so they route their own taps here rather
    // than through the canvas gesture controller.
    this.addEventListener('user-feature-tapped', ((e: CustomEvent) => {
      void this.handleUserFeatureTapped(e.detail.featureId as string, {
        ctrl: !!e.detail.ctrlKey,
        meta: !!e.detail.metaKey,
        shift: !!e.detail.shiftKey,
      })
    }) as EventListener)

    // Right-click on a point marker opens the same popup as a canvas right-click,
    // anchored at the point (markers are above the canvas, so the gesture
    // controller never sees them).
    this.addEventListener('user-feature-contextmenu', ((e: CustomEvent) => {
      if (!this.map) return
      if (tripFeatureManager.getMapMode() === 'draw' && this.hasActiveDrawTool()) return
      const lngLat = new maplibregl.LngLat(e.detail.lng as number, e.detail.lat as number)
      void this.showContextPopup(lngLat, this.map.project(lngLat))
    }) as EventListener)

    // Listen for feature editing mode changes
    this.addEventListener(
      'editing-mode-changed',
      this.handleEditingModeChanged.bind(this) as EventListener
    )
    this.addEventListener(
      'feature-selected',
      this.handleFeatureSelected.bind(this) as unknown as EventListener
    )
    this.addEventListener(
      'feature-deselected',
      this.handleFeatureDeselected.bind(this) as EventListener
    )
    // Listen for shape-deleted events to clean up component references
    this.addEventListener('shape-deleted', this.handleShapeDeleted.bind(this) as EventListener)

    // Add keyboard shortcuts
    this.setupKeyboardHandlers()

    log.debug('🗑️ Event listeners set up, feature-deleted handled via context bar')

    // Features are loaded after the base style is ready (see loadBaseLayer).
    // For vector styles, setStyle() destroys all canvas layers, so loading
    // features before the base style arrives would just have them wiped.

    log.debug('Drawing features initialized')
  }

  /** Set the feature manager scope to the current plan */
  private applyPlanScope(): void {
    if (!this.planId) return
    tripFeatureManager.setScope({
      entityType: 'plan',
      entityId: this.planId,
      defaultTags: {
        line: ['route'],
        point: ['waypoint'],
        polygon: ['boundary'],
      },
    })
  }

  /** Load plan location bounds as fallback camera position */
  private async loadPlanBounds(): Promise<void> {
    if (!this.planId || this.initialBounds || this.preview) return
    try {
      const plan = await db.plans.get(this.planId)
      if (plan?.location?.bounds) {
        this.initialBounds = plan.location.bounds
        if (this.map) {
          this.map.fitBounds(
            [
              [this.initialBounds.sw[1], this.initialBounds.sw[0]],
              [this.initialBounds.ne[1], this.initialBounds.ne[0]],
            ],
            { padding: 60, animate: false }
          )
        }
      }
    } catch (error) {
      log.error('Failed to load plan bounds:', error)
    }
  }

  /** Set the feature manager scope to the current route */
  private applyRouteScope(): void {
    if (!this.routeId) return
    tripFeatureManager.setScope({
      entityType: 'route',
      entityId: this.routeId,
      defaultTags: {
        line: ['route'],
        point: ['waypoint'],
        polygon: ['boundary'],
      },
    })
  }

  private async loadExistingFeatures(): Promise<void> {
    try {
      // Clear any existing features and handles from both systems
      this.clearAllFeatures()

      // In preview mode, load directly from storage (no scope mutation)
      const previewEntityType = this.planId ? 'plan' : this.routeId ? 'route' : null
      const previewEntityId = this.planId ?? this.routeId
      if (this.preview && previewEntityType && previewEntityId) {
        const features = await featureStorage.getFeaturesForEntity(
          previewEntityType,
          previewEntityId
        )
        features.forEach(feature => {
          if (feature.type === 'point' && feature.style) {
            feature = {
              ...feature,
              style: {
                ...feature.style,
                markerSize: Math.round((feature.style.markerSize ?? 12) / 2),
              },
            }
          }
          this.renderFeatureOnMap(feature)
        })
        if (features.length > 0) {
          this.fitToFeatureBounds(features)
        } else if (this.initialBounds) {
          this.map?.fitBounds(
            [
              [this.initialBounds.sw[1], this.initialBounds.sw[0]],
              [this.initialBounds.ne[1], this.initialBounds.ne[0]],
            ],
            { padding: 60, animate: false }
          )
        }
        return
      }
      // Skip legacy track operations in scoped mode
      if (!this.planId && !this.routeId) {
        const userId = authService.getCurrentUserId()
        if (userId) {
          syncChildrenOnNavigate('user', userId)
            .then(async () => {
              this.clearAllFeatures()
              const synced = await tripFeatureManager.getAllFeatures()
              synced.forEach(feature => this.renderFeatureOnMap(feature))
            })
            .catch(() => {})
        }
        await layerGroupManager.cleanAllTracks()
        await layerGroupManager.materializeTracksForAllTrips()
      }

      const features = await tripFeatureManager.getAllFeatures()
      features.forEach(feature => this.renderFeatureOnMap(feature))

      // In plan/route context, fit the map to feature bounds
      if ((this.planId || this.routeId) && features.length > 0) {
        this.fitToFeatureBounds(features)
      }

      if (!this.mapReadyFired && this.map) {
        this.mapReadyFired = true
        this.dispatchEvent(
          new CustomEvent('map-ready', {
            detail: { map: this.map },
            bubbles: true,
            composed: true,
          })
        )
      }
    } catch (error) {
      log.error('Failed to load existing features:', error)
    }
  }

  /** Compute a bounding box from features and fit the map to it */
  private fitToFeatureBounds(features: Feature[]): void {
    if (!this.map || features.length === 0) return

    let minLng = Infinity
    let maxLng = -Infinity
    let minLat = Infinity
    let maxLat = -Infinity

    const extendCoord = (lng: number, lat: number): void => {
      minLng = Math.min(minLng, lng)
      maxLng = Math.max(maxLng, lng)
      minLat = Math.min(minLat, lat)
      maxLat = Math.max(maxLat, lat)
    }

    for (const f of features) {
      // Always compute from geometry for accuracy
      const geom = f.geometry
      if (geom.type === 'Point') {
        extendCoord(geom.coordinates[0], geom.coordinates[1])
      } else if (geom.type === 'LineString') {
        for (const c of geom.coordinates) extendCoord(c[0], c[1])
      } else if (geom.type === 'Polygon') {
        for (const c of geom.coordinates[0]) extendCoord(c[0], c[1])
      }
    }

    if (isFinite(minLng)) {
      this.map.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        { padding: 30, animate: false }
      )
      if (this.preview) {
        const z = this.map.getZoom()
        this.map.setZoom(z - 0.5)
      }
    }
  }

  private clearAllFeatures(): void {
    // Clear legacy system
    this.featureMarkers.forEach(marker => {
      marker.remove()
    })
    this.featureMarkers.clear()
    this.hideAllEditHandles()

    // Clear shape components
    this.shapeComponents.forEach(component => {
      component.remove()
    })
    this.shapeComponents.clear()
  }

  private async handleTap(g: GesturePoint, mods: GestureModifiers): Promise<void> {
    if (!this.map) return

    // Route planning consumes taps to drop points.
    if (this.routePlanningActive && !this.routePlanComputing) {
      this.addRoutePlanPoint([g.lngLat.lng, g.lngLat.lat])
      return
    }

    // A shape edit just finished and asked us to swallow the resulting click.
    if (BaseShape.ignoreNextMapClick > 0) {
      BaseShape.ignoreNextMapClick--
      return
    }

    if (this.styleEditorManager.dropdownJustClosed) {
      return
    }

    // Split mode: the next tap picks the vertex to split at, regardless of mode or an
    // open popup.
    if (this.splitModeFeatureId) {
      this.pickSplitVertex(g)
      return
    }

    // Popup-aware: an off-popup tap dismisses an open identify popup rather than
    // opening a new one. (Clicks inside the popup never reach here — the gesture
    // controller only acts on canvas-targeted gestures.)
    if (this.contextPopup || this.contextOverlayEl) {
      this.dismissContextPopup()
      return
    }

    // Draw mode only captures the tap while a tool is actually engaged (a tool
    // is picked, or we're extending/editing). Idle draw mode falls through to
    // the global identify/select behaviour so you can tap a shape to edit it.
    if (tripFeatureManager.getMapMode() === 'draw' && this.hasActiveDrawTool()) {
      await this.handleDrawTap(g)
      return
    }

    // Measure active: taps set measure points (e.g. the destination for
    // "Measure from here to…"). Refresh the context mode afterwards so the
    // crosshair cursor tracks the new state — completing the line, or a further
    // tap that clears it, both change whether we're still awaiting a point.
    if (this.measureActive) {
      this.measurementManager.handleClick(g.lngLat)
      this.updateContextMode()
      return
    }

    // Every non-draw mode behaves identically: identify, or toggle selection
    // while a selection is active (or when ctrl/cmd forces a toggle).
    const userFeatureId = this.queryUserFeatureAtPoint(g.point)
    const overlayRef = userFeatureId ? null : this.queryOverlayFeatureAtPoint(g.point)
    const hasSelection = this.hasActiveSelection()
    const toggleIntent = mods.ctrl || mods.meta

    if (userFeatureId) {
      if (hasSelection || toggleIntent) {
        await this.toggleUserFeatureSelection(userFeatureId)
      } else {
        await this.showContextPopup(g.lngLat, g.point)
      }
      return
    }

    if (overlayRef) {
      if (hasSelection || toggleIntent) {
        selectionService.toggle(overlayRef)
        await this.afterSelectionChanged()
      } else {
        await this.showContextPopup(g.lngLat, g.point)
      }
      return
    }

    // Not a user/selectable-overlay feature. A tap with an active selection just
    // clears it. Otherwise: a long-press / right-click means "what's here" and
    // always opens the popup (location info, even bare ground); a plain
    // left-click only identifies a discrete feature (NPS alerts, fire, avalanche,
    // iNat, …) and does nothing on truly empty/ambient ground.
    if (hasSelection) {
      this.clearAllSelection()
      return
    }
    // Ctrl/cmd+click is a select-only gesture — if it missed every selectable
    // feature, it's a no-op, not an identify.
    if (toggleIntent) {
      return
    }
    await this.showContextPopup(g.lngLat, g.point, !mods.longPress)
  }

  /**
   * Long-press / right-click anywhere: open the unified identify popup. It is
   * the single "act here" surface — it carries the draw-tool band and measure
   * verbs (see showContextPopup's menu-* wiring) alongside the location/feature
   * info. A plain left-click only opens it when it lands on a feature.
   */
  private handleActionMenu(g: GesturePoint): void {
    if (!this.map) return
    // Suppressed only while actively drawing; idle draw mode still gets the menu.
    if (tripFeatureManager.getMapMode() === 'draw' && this.hasActiveDrawTool()) return
    void this.showContextPopup(g.lngLat, g.point)
  }

  /** Dismiss the identify popup, whichever presentation is open (desktop popup
   * or mobile drawer/overlay). */
  private dismissContextPopup(): void {
    if (this.contextPopup) {
      this.contextPopup.remove()
      this.contextPopup = null
    }
    if (this.contextOverlayEl) {
      this.closeContextOverlay()
    }
  }

  /** Activate a draw tool from the action menu; a point drops at the spot. */
  private activateDrawToolFromMenu(tool: string, lngLat: maplibregl.LngLat): void {
    tripFeatureManager.clearSelection()
    this.selectedFeatures = []
    this.hideAllEditHandles()
    this.exitAllEditModes()
    tripFeatureManager.selectDrawingTool(
      tool as 'point' | 'line' | 'polygon' | 'circle' | 'rectangle'
    )
    BaseShape.ignoreNextMapClick = 0
    if (tool === 'point') {
      void this.createPointAtLocation({ lat: lngLat.lat, lng: lngLat.lng })
    }
    this.updateContextMode()
  }

  /** Begin a measurement anchored at the point; the next tap sets the end. */
  private startMeasureFromHere(lngLat: maplibregl.LngLat): void {
    this.measurementManager.cleanup()
    this.measurementManager.handleClick(lngLat)
    this.updateContextMode()
  }

  /** Measure straight-line from the current location to the point. */
  private startMeasureToHere(lngLat: maplibregl.LngLat): void {
    if (!this.currentLocation) return
    this.measurementManager.cleanup()
    this.measurementManager.handleClick(
      new maplibregl.LngLat(this.currentLocation.lng, this.currentLocation.lat)
    )
    this.measurementManager.handleClick(lngLat)
    this.updateContextMode()
  }

  /**
   * Whether an actual drawing interaction is engaged (a tool is picked, we're
   * extending a line, or a feature is focused for editing). When false, draw
   * mode behaves like Explore — taps identify/select instead of being captured.
   */
  private hasActiveDrawTool(): boolean {
    return (
      !!tripFeatureManager.getSelectedTool() ||
      tripFeatureManager.isExtending() ||
      tripFeatureManager.getDrawSubContext() === 'focused'
    )
  }

  /** Draw-mode tap routing: extend / unfocus / place with the active tool. */
  private async handleDrawTap(g: GesturePoint): Promise<void> {
    // Extend mode: add point to line (only when a direction has been chosen)
    if (tripFeatureManager.isActivelyExtending()) {
      const extendingFeatureId = tripFeatureManager.getExtendingFeatureId()
      if (extendingFeatureId) {
        const latLng: FeatureLatLng = { lat: g.lngLat.lat, lng: g.lngLat.lng }
        await tripFeatureManager.extendLineToPoint(extendingFeatureId, latLng)
        const shapeComponent = this.shapeComponents.get(extendingFeatureId)
        if (shapeComponent) {
          const updatedFeature = await tripFeatureManager.getFeature(extendingFeatureId)
          if (updatedFeature) {
            shapeComponent.feature = updatedFeature
            shapeComponent.updateSource()
          }
        }
      }
      return
    }
    // Extend mode but no direction yet — ignore (user must click an endpoint)
    if (tripFeatureManager.isExtending()) {
      return
    }

    // Focused: tap empty space unfocuses, back to draw tools (or the mode the
    // edit was entered from).
    if (tripFeatureManager.getDrawSubContext() === 'focused') {
      void this.commitPendingEdit()
      tripFeatureManager.clearSelection()
      this.selectedFeatures = []
      this.hideAllEditHandles()
      this.exitAllEditModes()
      this.restoreEditReturnMode()
      this.updateContextMode()
      return
    }

    const tool = tripFeatureManager.getSelectedTool()
    if (!tool) {
      return
    }

    const latLng: FeatureLatLng = { lat: g.lngLat.lat, lng: g.lngLat.lng }
    switch (tool) {
      case 'point':
        void this.createPointAtLocation(latLng)
        break
      case 'line': {
        // Extending but no end chosen yet → this tap picks the end (nearest of the line's
        // two endpoints); it does not place a point. (The gesture controller captures the
        // tap before the endpoint markers, so the pick has to happen here.)
        if (this.linePolygonDrawingManager.isAwaitingExtendEnd) {
          this.pickExtendEndFromTap(g)
          return
        }
        // Auto-detect the default snap: start on a trail → snap to trails + terrain;
        // start off-trail → freehand. Both are flippable mid-draw via the create bar.
        if (this.linePolygonDrawingManager.drawingPointCount === 0) {
          const ref = this.queryOverlayFeatureAtPoint(g.point)
          const onTrail = ref?.source === 'backcountry-trail'
          // Default only — a pre-point toggle in the create bar takes precedence.
          this.linePolygonDrawingManager.setSnapDefault(onTrail, onTrail)
        }
        // Snap the placed anchor onto the track when within tolerance, so a previewed
        // snap always becomes a real one (no falling through to DEM).
        this.linePolygonDrawingManager.addPointToLine(
          this.linePolygonDrawingManager.snapToTrack(g.lngLat)
        )
        break
      }
      case 'polygon':
        this.linePolygonDrawingManager.addPointToPolygon(g.lngLat)
        break
      case 'circle':
      case 'rectangle':
        // Placed by drag (DragDrawingManager), not by tap.
        break
    }
  }

  /** Hit-test the user-drawn shapes at a screen point → feature id, or null. */
  private queryUserFeatureAtPoint(point: maplibregl.Point): string | null {
    if (!this.map) return null
    const hits = this.map.queryRenderedFeatures(point)
    for (const hit of hits) {
      if (hit.source.startsWith('feature-')) {
        return hit.source.replace('feature-', '')
      }
    }
    return null
  }

  /** Whether any user or overlay feature is currently selected. */
  private hasActiveSelection(): boolean {
    return tripFeatureManager.getSelectedFeatures().length > 0 || selectionService.count > 0
  }

  /** Toggle a user shape in/out of the selection and refresh selection UI. */
  private async toggleUserFeatureSelection(featureId: string): Promise<void> {
    tripFeatureManager.toggleFeatureSelection(featureId)
    const selected = tripFeatureManager.getSelectedFeatures().includes(featureId)
    this.shapeComponents.get(featureId)?.setSelected(selected)
    await this.afterSelectionChanged()
  }

  /**
   * Sync selection-derived UI after a toggle. The selection bar is state-driven
   * (shown whenever a selection exists, regardless of FAB mode); when it empties
   * we fall back to whatever the FAB mode shows.
   */
  private async afterSelectionChanged(): Promise<void> {
    if (this.hasActiveSelection()) {
      await this.updateSelectedFeatures()
      this.updateContextMode()
      this.requestUpdate()
    } else {
      this.clearAllSelection()
    }
  }

  /** Clear both selection stores. The selection bar is state-driven, so the
   * context bar falls back to the current FAB mode on its own. */
  private clearAllSelection(): void {
    selectionService.clearAll()
    this.shapeComponents.forEach(c => c.setSelected(false))
    this.handleSelectionCleared()
  }

  /**
   * Route a tap on a point-feature marker through the same model as canvas taps:
   * dismiss an open popup, toggle when selecting, otherwise identify.
   */
  private async handleUserFeatureTapped(
    featureId: string,
    mods: { ctrl: boolean; meta: boolean; shift: boolean }
  ): Promise<void> {
    if (this.contextPopup) {
      this.contextPopup.remove()
      this.contextPopup = null
      return
    }
    // A drawing tool owns taps in draw mode.
    if (tripFeatureManager.getMapMode() === 'draw') return

    if (this.hasActiveSelection() || mods.ctrl || mods.meta) {
      await this.toggleUserFeatureSelection(featureId)
      return
    }

    const shape = this.shapeComponents.get(featureId)
    if (!shape || !this.map || shape.feature.geometry.type !== 'Point') return
    const coords = shape.feature.geometry.coordinates
    const lngLat = new maplibregl.LngLat(coords[0], coords[1])
    await this.showContextPopup(lngLat, this.map.project(lngLat))
  }

  /**
   * Enter edit on a user feature — the deliberate edit entry from the identify
   * popup's Edit button. Focuses the feature, shows its handles and the editing
   * tools bar. (Editing is never triggered by a stray map tap.)
   */
  private async handleEditFeatureRequest(featureId: string): Promise<void> {
    if (this.contextPopup) {
      this.contextPopup.remove()
      this.contextPopup = null
    }
    this.closeContextOverlay()

    const shape = this.shapeComponents.get(featureId)
    if (!shape) return

    // Remember where we were so finishing the edit returns here (focusFeature
    // below switches us into draw mode).
    this.editReturnMode = tripFeatureManager.getMapMode()

    this.shapeComponents.forEach((component, id) => {
      if (id !== featureId) component.exitEditMode()
    })
    tripFeatureManager.clearSelection()
    tripFeatureManager.focusFeature(featureId)
    tripFeatureManager.selectFeature(featureId)
    const snapshot = await db.features.get(featureId)
    if (snapshot) undoStack.setPendingEdit(featureId, snapshot)
    shape.enterEditMode()
    await this.updateSelectedFeatures()
    this.updateContextMode()
  }

  private handleMapDoubleClick(e: maplibregl.MapMouseEvent): void {
    const tool = tripFeatureManager.getSelectedTool()
    // While drawing/extending a line or polygon, a double-tap must NOT finish (or zoom):
    // on touch, a quick pair of point taps would otherwise commit the line — and during
    // an extend it would even spawn a stray new feature. Finishing is via the Finish
    // button; preventDefault also suppresses MapLibre's double-tap-zoom here.
    if (tool === 'line' || tool === 'polygon') {
      e.preventDefault()
    }
  }

  private async createPointAtLocation(location: FeatureLatLng): Promise<void> {
    try {
      log.debug('🎯 Creating point at location:', location)
      const feature = await tripFeatureManager.createPointFeature(location)
      this.renderFeatureOnMap(feature)

      tripFeatureManager.selectFeature(feature.id)
      this.updateContextMode()

      const shapeComponent = this.shapeComponents.get(feature.id)
      if (shapeComponent) {
        undoStack.setPendingEdit(feature.id, structuredClone(feature))
        shapeComponent.enterEditMode()
      }
      // Placing a point deselects the point tool — re-pick to add another.
      tripFeatureManager.clearDrawingTool()

      undoStack.push({
        type: 'create',
        label: `Created ${feature.name}`,
        features: [{ id: feature.id, before: null, after: structuredClone(feature) }],
        edges: [],
      })
      showToast('info', `Created ${feature.name}`, {
        tag: 'undo',
        action: { label: 'Undo', callback: () => void this.performUndo() },
      })
    } catch (error) {
      log.error('❌ Failed to create point feature:', error)
    }
  }

  private handleMapMouseMove(e: maplibregl.MapMouseEvent): void {
    if (this.dragDrawingManager.isActiveDrag) {
      return
    }

    const mode = tripFeatureManager.getMapMode()
    if (this.measureActive) {
      this.updateMeasurePreview(e.lngLat)
      return
    }
    if (!this.map || mode === 'draw') {
      return
    }

    // Show a pointer over any identifiable overlay feature, regardless of layer,
    // so hover matches what a click would identify. User shapes and DOM-marker
    // layers (NPS alerts, emergency, iNaturalist, point features) manage their
    // own cursor; everything that renders as a queryable map layer is gathered
    // here. We only clear a cursor we set (and not while also over a user
    // feature) to avoid fighting them.
    const layers = [
      ...new Set([
        ...getSelectableOverlayLayerIds(),
        ...backcountryOverlayService.interactiveLayerIds(),
        ...cotrexClosureService.interactiveLayerIds(),
        ...fireLayerService.getSelectableLayerIds(),
        ...avalancheLayerService.getSelectableLayerIds(),
        ...criticalHabitatService.interactiveLayerIds(),
        ...openBetaLayerService.interactiveLayerIds(),
      ]),
    ].filter(id => this.map!.getLayer(id))
    // Query a small tolerance box (not a single pixel) so thin overlay lines
    // hover with the same slop as user shapes / wider hit layers.
    const R = 6
    const hoverBox: [maplibregl.PointLike, maplibregl.PointLike] = [
      [e.point.x - R, e.point.y - R],
      [e.point.x + R, e.point.y + R],
    ]
    const overlayHit =
      layers.length > 0 && this.map.queryRenderedFeatures(hoverBox, { layers }).length > 0

    if (overlayHit) {
      this.map.getCanvas().style.cursor = 'pointer'
      this.overlayHovered = true
    } else if (this.overlayHovered) {
      this.overlayHovered = false
      if (!this.queryUserFeatureAtPoint(e.point)) {
        this.map.getCanvas().style.cursor = ''
      }
    }
  }

  private handleNativeMouseMove = (e: MouseEvent): void => {
    if (this.dragDrawingManager.isActiveDrag || !this.map) {
      return
    }
    if (
      !this.linePolygonDrawingManager.isCurrentlyDrawing ||
      this.linePolygonDrawingManager.drawingPointCount === 0
    ) {
      return
    }

    const tool = tripFeatureManager.getSelectedTool()
    if (tool !== 'line' && tool !== 'polygon') {
      return
    }

    const rect = this.map.getCanvas().getBoundingClientRect()
    const point = new maplibregl.Point(e.clientX - rect.left, e.clientY - rect.top)
    const lngLat = this.map.unproject(point)
    this.linePolygonDrawingManager.updateMousePreview(lngLat)
  }

  private renderFeatureOnMap(feature: Feature | MapFeature): void {
    if (!this.featureLayerGroupReady || !this.map) {
      log.error('❌ No feature layer group or map available')
      return
    }

    log.debug('🎯 Rendering feature with shape component:', {
      type: feature.type,
      id: feature.id,
      geometry: feature.geometry,
      coordinates: (feature.geometry as GeoJSON.Point | GeoJSON.LineString | GeoJSON.Polygon)
        .coordinates,
    })

    // Remove existing shape component if it exists
    const existingShape = this.shapeComponents.get(feature.id)
    if (existingShape) {
      existingShape.remove()
      this.shapeComponents.delete(feature.id)
    }

    // Also remove any old marker from the legacy system
    const oldMarker = this.featureMarkers.get(feature.id)
    if (oldMarker) {
      oldMarker.remove()
      this.featureMarkers.delete(feature.id)
    }

    // Create appropriate shape component based on feature type
    let shapeComponent: BaseShape

    switch (feature.type) {
      case 'point':
        shapeComponent = document.createElement('shape-point') as BaseShape
        break
      case 'line':
        shapeComponent = document.createElement('shape-line') as BaseShape
        break
      case 'polygon':
        shapeComponent = document.createElement('shape-polygon') as BaseShape
        break
      case 'rectangle':
        shapeComponent = document.createElement('shape-rectangle') as BaseShape
        break
      case 'circle':
        shapeComponent = document.createElement('shape-circle') as BaseShape
        break
      case 'route':
      case 'track':
        // Route and track features are LineStrings — render as shape-line
        shapeComponent = document.createElement('shape-line') as BaseShape
        break
      default:
        log.error('❌ Unknown feature type:', feature.type)
        return
    }

    // Set properties on the shape component
    shapeComponent.feature = feature
    shapeComponent.map = this.map
    shapeComponent.map = this.map

    shapeComponent.addEventListener('shape-selected', (e): void => {
      void (async (): Promise<void> => {
        const customEvent = e as CustomEvent
        const selectedShapeId = customEvent.detail.shapeId
        const ctrlKey = customEvent.detail.ctrlKey
        let currentMode = tripFeatureManager.getMapMode()

        if (ctrlKey && currentMode !== 'select') {
          this.exitAllEditModes()
          this.hideAllEditHandles()
          tripFeatureManager.setMapMode('select')
          currentMode = 'select'
        }

        if (currentMode === 'map') {
          return
        }

        if (currentMode === 'measure') {
          this.handleMeasureShapeClick(selectedShapeId)
          return
        }

        if (currentMode === 'select') {
          BaseShape.ignoreNextMapClick++
          const ref = { source: 'user' as const, featureId: selectedShapeId }
          if (ctrlKey || customEvent.detail.shiftKey) {
            selectionService.toggle(ref)
          } else {
            if (selectionService.isSelected(ref)) {
              selectionService.deselect(ref)
            } else {
              selectionService.replaceSelection([ref])
            }
          }
          await this.updateSelectedFeatures()
          this.updateContextMode()
          return
        }

        this.shapeComponents.forEach((component, featureId) => {
          if (featureId !== selectedShapeId) {
            component.exitEditMode()
          }
        })

        tripFeatureManager.clearSelection()
        tripFeatureManager.focusFeature(selectedShapeId)
        tripFeatureManager.selectFeature(selectedShapeId)
        const focusedShape = this.shapeComponents.get(selectedShapeId)
        if (focusedShape) {
          const snapshot = await db.features.get(selectedShapeId)
          if (snapshot) undoStack.setPendingEdit(selectedShapeId, snapshot)
          focusedShape.enterEditMode()
        }
        await this.updateSelectedFeatures()
        this.updateContextMode()
      })()
    })

    shapeComponent.addEventListener('vertex-selection-changed', (): void => {
      this._bump()
    })

    shapeComponent.addEventListener('extend-begin', (e: Event): void => {
      void this.handleExtendBegin(e as CustomEvent)
    })

    shapeComponent.addEventListener('split-at-vertex', (e: Event): void => {
      this.handleSplitAtVertex(e as CustomEvent)
    })

    // Add to map component so events bubble up correctly
    this.appendChild(shapeComponent)

    // Store the component reference
    this.shapeComponents.set(feature.id, shapeComponent)

    if (feature.isVisible === false) {
      requestAnimationFrame(() => shapeComponent.setVisible(false))
    }
  }

  private cleanupTempDrawing(): void {
    this.linePolygonDrawingManager.cleanupTempDrawing()
    this.dragDrawingManager.cleanupCircleDrag()
    this.dragDrawingManager.cleanupRectangleDrag()
  }

  private async showContextPopup(
    lngLat: maplibregl.LngLat,
    point: maplibregl.Point,
    requireFeature = false
  ): Promise<void> {
    if (!this.map) return

    if (this.contextPopup) {
      this.contextPopup.remove()
      this.contextPopup = null
    }
    if (this.contextOverlayEl) {
      this.contextOverlayEl = null
    }

    const landFeatures = landOwnershipService.queryFeatures(point)
    const plssInfo = plssService.queryAtPoint(point)
    const backcountryFeatures = backcountryOverlayService.queryFeatures(point)
    const emergencyHits = await emergencyLayerService.queryFeaturesAtPoint(point)
    const inatHits = await iNaturalistService.queryFeaturesAtPoint(point)
    const criticalHabitatHits = criticalHabitatService.queryFeatures(point)
    const cotrexHits = cotrexLayerService.queryFeatures(point)
    const closureHits = cotrexClosureService.queryFeatures(point)
    const fireHits = fireLayerService.queryFeatures(point).map(f => ({
      fireType: fireLayerService.typeForLayerId(f.layer.id) ?? 'unknown',
      feature: f,
    }))
    const avalancheZoneHits = avalancheLayerService.queryFeatures(point)
    const cellularFeatures = cellularCoverageService.queryFeatures(point)
    const npsAlertHits = await npsAlertsService.queryFeaturesAtPoint(point)
    const climbingHits = openBetaLayerService.queryFeaturesAtPoint(point)

    const allHits = this.map.queryRenderedFeatures(point)
    const userFeatures: Array<{
      id: string
      name: string
      type: string
      geometry: GeoJSON.Geometry
      association?: { kind: 'activity' | 'plan'; id: string; name: string }
      elevation?: number
    }> = []
    const seenFeatureIds = new Set<string>()
    for (const hit of allHits) {
      if (!hit.source.startsWith('feature-')) continue
      const featureId = hit.source.replace('feature-', '')
      if (seenFeatureIds.has(featureId)) continue
      seenFeatureIds.add(featureId)
      const shape = this.shapeComponents.get(featureId)
      if (shape) {
        userFeatures.push({
          id: featureId,
          name: shape.feature.name || featureId,
          type: shape.feature.type,
          geometry: shape.feature.geometry,
        })
      }
    }

    // Point features render as DOM markers (invisible to queryRenderedFeatures),
    // so detect them by screen proximity to the click.
    const POINT_HIT_RADIUS_PX = 22
    this.shapeComponents.forEach((shape, featureId) => {
      if (seenFeatureIds.has(featureId)) return
      if (shape.feature.geometry.type !== 'Point') return
      const coords = shape.feature.geometry.coordinates
      const screenPt = this.map!.project([coords[0], coords[1]])
      if (Math.hypot(screenPt.x - point.x, screenPt.y - point.y) > POINT_HIT_RADIUS_PX) return
      seenFeatureIds.add(featureId)
      userFeatures.push({
        id: featureId,
        name: shape.feature.name || featureId,
        type: shape.feature.geometry.type,
        geometry: shape.feature.geometry,
      })
    })

    // Resolve each user feature's association (activity / plan / standalone) so
    // the popup can show a link to the owning entity.
    for (const uf of userFeatures) {
      const parent = await featureStorage.getParentEntity(uf.id)
      if (parent?.type === 'activity') {
        const activity = await db.activities.get(parent.id)
        uf.association = { kind: 'activity', id: parent.id, name: activity?.name || 'Activity' }
      } else if (parent?.type === 'plan') {
        const plan = await db.plans.get(parent.id)
        uf.association = { kind: 'plan', id: parent.id, name: plan?.name || 'Plan' }
      }

      // Points carry no measurement of their own — surface their terrain
      // elevation instead (like OSM peaks).
      if (uf.geometry.type === 'Point') {
        const [lng, lat] = uf.geometry.coordinates
        const ele = await elevationPointService.getElevation(lat, lng)
        if (typeof ele === 'number' && Number.isFinite(ele)) uf.elevation = ele
      }
    }

    // A plain left-tap only identifies when it actually lands on a discrete
    // feature. Land ownership and cellular coverage are ambient backdrops (they
    // blanket huge areas), so they don't count — otherwise an "empty" tap would
    // pop the location panel everywhere. Right-click/long-press passes
    // requireFeature=false and always shows (its job is "what's here").
    if (requireFeature) {
      const hasFeature =
        userFeatures.length > 0 ||
        backcountryFeatures.length > 0 ||
        emergencyHits.length > 0 ||
        inatHits.length > 0 ||
        criticalHabitatHits.length > 0 ||
        cotrexHits.length > 0 ||
        closureHits.length > 0 ||
        fireHits.length > 0 ||
        avalancheZoneHits.length > 0 ||
        npsAlertHits.length > 0 ||
        climbingHits.length > 0
      if (!hasFeature) return
    }

    const popupEl = document.createElement('map-context-popup')
    popupEl.lat = lngLat.lat
    popupEl.lng = lngLat.lng
    popupEl.landFeatures = landFeatures
    popupEl.plss = plssInfo
    popupEl.backcountryFeatures = backcountryFeatures
    popupEl.userFeatures = userFeatures
    popupEl.emergencyFeatures = emergencyHits
    popupEl.inatObservations = inatHits
    popupEl.criticalHabitatFeatures = criticalHabitatHits
    popupEl.cotrexFeatures = cotrexHits
    popupEl.closureFeatures = closureHits
    popupEl.fireFeatures = fireHits
    popupEl.avalancheZoneFeatures = avalancheZoneHits
    popupEl.cellularFeatures = cellularFeatures
    popupEl.npsAlerts = npsAlertHits
    popupEl.clickedCrags = climbingHits
    if (openBetaLayerService.isFilterActive) {
      popupEl.climbFilter = {
        typeMask: openBetaLayerService.typeMask,
        ydsMin: openBetaLayerService.ydsMin,
        ydsMax: openBetaLayerService.ydsMax,
        vMin: openBetaLayerService.vMin,
        vMax: openBetaLayerService.vMax,
      }
    }

    this.dispatchEvent(
      new CustomEvent('context-popup-showing', {
        detail: { popup: popupEl, lngLat },
        bubbles: true,
        composed: true,
      })
    )

    popupEl.addEventListener('route-plan-requested', ((e: CustomEvent) => {
      const { lng, lat } = e.detail
      this.closeContextOverlay()
      this.startRoutePlanning([lng, lat])
    }) as EventListener)

    popupEl.addEventListener('edit-feature', ((e: CustomEvent) => {
      void this.handleEditFeatureRequest(e.detail.featureId as string)
    }) as EventListener)

    popupEl.addEventListener('open-entity', ((e: CustomEvent) => {
      const kind = e.detail.kind as 'activity' | 'plan'
      const id = e.detail.id as string
      if (this.contextPopup) {
        this.contextPopup.remove()
        this.contextPopup = null
      }
      this.closeContextOverlay()
      router.navigate(kind === 'activity' ? `/activities/${id}` : `/plans/${id}`)
    }) as EventListener)

    // Merged "act here" surface: the draw-tool band + measure verbs live in the
    // popup. Picking one dismisses the popup and runs the location-anchored verb.
    popupEl.hasLocation = !!this.currentLocation
    popupEl.addEventListener('menu-draw-tool', ((e: CustomEvent) => {
      this.dismissContextPopup()
      this.activateDrawToolFromMenu(e.detail.tool as string, lngLat)
    }) as EventListener)
    popupEl.addEventListener('menu-measure-to', () => {
      this.dismissContextPopup()
      this.startMeasureToHere(lngLat)
    })
    popupEl.addEventListener('menu-measure-from', () => {
      this.dismissContextPopup()
      this.startMeasureFromHere(lngLat)
    })

    const isMobile = window.innerWidth <= CairnMap.MOBILE_BREAKPOINT

    if (isMobile) {
      popupEl.addEventListener('close-popup', () => this.closeContextOverlay())
      popupEl.fullscreen = true
      popupEl.style.minWidth = 'unset'
      popupEl.style.maxWidth = 'unset'
      popupEl.style.width = '100%'
      this.contextOverlayEl = popupEl
      this.openMobileOverlayAtPeek()
      const zoom = this.map.getZoom()
      const url = new URL(window.location.href)
      url.searchParams.set('dlat', lngLat.lat.toFixed(6))
      url.searchParams.set('dlng', lngLat.lng.toFixed(6))
      url.searchParams.set('dz', zoom.toFixed(1))
      window.history.pushState({ contextOverlay: true }, '', url.toString())
    } else {
      popupEl.addEventListener('close-popup', () => {
        if (this.contextPopup) {
          this.contextPopup.remove()
          this.contextPopup = null
        }
      })
      this.contextPopup = new maplibregl.Popup({
        maxWidth: '420px',
        closeButton: false,
        className: 'cairn-context-popup',
      })
        .setLngLat(lngLat)
        .setDOMContent(popupEl)
        .addTo(this.map)
    }
  }

  private closeContextOverlay(): void {
    if (this.contextOverlayEl) {
      this.contextOverlayEl = null
      const url = new URL(window.location.href)
      url.searchParams.delete('dlat')
      url.searchParams.delete('dlng')
      url.searchParams.delete('dz')
      window.history.replaceState(null, '', url.toString())
    }
  }

  private checkUrlContextOverlay(): void {
    if (!this.map) return
    const params = new URLSearchParams(window.location.search)
    const lat = parseFloat(params.get('dlat') ?? '')
    const lng = parseFloat(params.get('dlng') ?? '')
    const zoom = parseFloat(params.get('dz') ?? '')
    if (!isFinite(lat) || !isFinite(lng)) return

    if (isFinite(zoom)) {
      this.map.jumpTo({ center: [lng, lat], zoom })
    }

    const lngLat = new maplibregl.LngLat(lng, lat)
    const point = this.map.project(lngLat)
    void this.showContextPopup(lngLat, point)
  }

  private updateMeasurePreview(mousePos: maplibregl.LngLat): void {
    this.measurementManager.updatePreview(mousePos)
  }

  private handleMeasureShapeClick(shapeId: string): void {
    void this.measurementManager.handleShapeClick(shapeId)
  }

  private handleMeasureClear(): void {
    this.cleanupMeasure()
  }

  // --- Chart cursor → map indicator ---
  private chartCursorMarker: maplibregl.Marker | undefined

  private handleChartCursor(e: CustomEvent<{ elapsed: number | null }>): void {
    const elapsed = e.detail.elapsed
    if (elapsed == null) {
      this.chartCursorMarker?.remove()
      this.chartCursorMarker = undefined
      return
    }

    const track = this.trackRecordingManager.getTrackPoints()
    if (track.length < 2 || !this.trackRecordingManager.getStartTime()) return

    const startTime = this.trackRecordingManager.getStartTime()!
    const targetTs = startTime + elapsed * 1000

    // Find bracketing points and interpolate
    let prev = track[0]
    for (let i = 1; i < track.length; i++) {
      const curr = track[i]
      if (curr.timestamp >= targetTs) {
        const tRange = curr.timestamp - prev.timestamp
        const t = tRange > 0 ? (targetTs - prev.timestamp) / tRange : 0
        const lat = prev.lat + t * (curr.lat - prev.lat)
        const lng = prev.lng + t * (curr.lng - prev.lng)
        this.showChartCursorAt(lng, lat)
        return
      }
      prev = curr
    }
    // Past end — show at last point
    const last = track[track.length - 1]
    this.showChartCursorAt(last.lng, last.lat)
  }

  private showChartCursorAt(lng: number, lat: number): void {
    if (!this.map) return
    if (this.chartCursorMarker) {
      this.chartCursorMarker.setLngLat([lng, lat])
    } else {
      const el = document.createElement('div')
      el.style.width = '14px'
      el.style.height = '14px'
      el.style.borderRadius = '50%'
      el.style.background = '#facc15'
      el.style.border = '2px solid white'
      el.style.boxShadow = '0 0 4px rgba(0,0,0,0.5)'
      this.chartCursorMarker = new maplibregl.Marker({ element: el })
        .setLngLat([lng, lat])
        .addTo(this.map)
    }
  }

  private cleanupMeasure(): void {
    this.measurementManager.cleanup()
    this.updateContextMode()
  }

  private updateContextMode(): void {
    const previousTool = this.selectedDrawingTool
    this.contextMode = tripFeatureManager.getContextMode()
    this.mapMode = tripFeatureManager.getMapMode()
    this.drawSubContext = tripFeatureManager.getDrawSubContext()
    this.selectedDrawingTool = tripFeatureManager.getSelectedTool()

    if (previousTool && previousTool !== this.selectedDrawingTool) {
      this.completeOrCancelCurrentDrawing(previousTool)
    }

    const isExtending = tripFeatureManager.isExtending()

    if (this.selectedDrawingTool) {
      this.setAttribute('drawing-mode', '')
      this.removeAttribute('draw-focused')
      this.removeAttribute('extending-mode')
      this.removeAttribute('select-mode')
      this.removeAttribute('measure-mode')
      if (this.selectedDrawingTool === 'circle' || this.selectedDrawingTool === 'rectangle') {
        this.disableMapInteraction()
      } else {
        this.enableMapPanning()
      }
    } else if (this.mapMode === 'draw' && this.drawSubContext === 'focused') {
      this.setAttribute('draw-focused', '')
      this.removeAttribute('drawing-mode')
      this.removeAttribute('extending-mode')
      this.removeAttribute('select-mode')
      this.removeAttribute('measure-mode')
      // Pan stays enabled — the GestureController gates vertex box-select behind
      // shift+drag (desktop) / hold-then-drag (touch) and suppresses pan only during
      // those, so plain drag should pan (needed to reach off-screen vertices).
      this.enableMapPanning()
    } else if (isExtending) {
      this.setAttribute('extending-mode', '')
      this.removeAttribute('drawing-mode')
      this.removeAttribute('draw-focused')
      this.removeAttribute('select-mode')
      this.removeAttribute('measure-mode')
      this.enableMapPanning()
    } else if (this.mapMode === 'select') {
      this.setAttribute('select-mode', '')
      this.removeAttribute('drawing-mode')
      this.removeAttribute('draw-focused')
      this.removeAttribute('extending-mode')
      this.removeAttribute('measure-mode')
      this.enableMapPanning()
    } else if (this.mapMode === 'measure') {
      this.setAttribute('measure-mode', '')
      this.removeAttribute('drawing-mode')
      this.removeAttribute('draw-focused')
      this.removeAttribute('extending-mode')
      this.removeAttribute('select-mode')
      this.enableMapPanning()
    } else {
      this.removeAttribute('drawing-mode')
      this.removeAttribute('draw-focused')
      this.removeAttribute('extending-mode')
      this.removeAttribute('select-mode')
      this.removeAttribute('measure-mode')
      this.enableMapPanning()
      if (this.mapMode !== 'draw' || this.drawSubContext !== 'focused') {
        this.cleanupTempDrawing()
      }
    }

    // Measure is a transient state (not a mode), so reflect its crosshair cursor
    // independently of the mode attributes set above.
    if (this.measureActive) {
      this.setAttribute('measure-mode', '')
    }

    this.requestUpdate()
  }

  private handleToolSelected(event: CustomEvent): void {
    const tool = event.detail.tool

    if (tool) {
      tripFeatureManager.clearSelection()
      this.selectedFeatures = []
      this.hideAllEditHandles()
      this.exitAllEditModes()
      tripFeatureManager.selectDrawingTool(tool)
    } else {
      tripFeatureManager.clearDrawingTool()
    }

    BaseShape.ignoreNextMapClick = 0
    this.updateContextMode()
  }

  private handleDrawingModeEntered(): void {
    this.updateContextMode()
    this.ensureCleanDrawingState()
  }

  private handleDrawingModeExited(): void {
    this.cleanupTempDrawing()
    this.updateContextMode()
  }

  private ensureCleanDrawingState(): void {
    // Always start with a clean state when entering drawing mode
    if (this.linePolygonDrawingManager.isCurrentlyDrawing) {
      log.debug('🧹 Cleaning previous drawing state before starting new tool')
      this.cleanupTempDrawing()
    }
  }

  private disableMapPanning(): void {
    if (!this.map) {
      return
    }
    this.map.dragPan.disable()
  }

  private enableMapPanning(): void {
    if (!this.map) {
      return
    }
    this.map.dragPan.enable()
    this.map.scrollZoom.enable()
    this.map.touchZoomRotate.enable()
  }

  private disableMapInteraction(): void {
    if (!this.map) {
      return
    }
    this.map.dragPan.disable()
    this.map.scrollZoom.disable()
    this.map.touchZoomRotate.disable()
  }

  private completeOrCancelCurrentDrawing(previousTool: string): void {
    this.linePolygonDrawingManager.completeOrCancelCurrentDrawing(previousTool)
    if (previousTool !== 'line' && previousTool !== 'polygon' && previousTool !== 'point') {
      // No active drawing, just cleanup
      this.cleanupTempDrawing()
    }
  }

  private setupResizeHandling(): void {
    if (!this.map || !this.mapRef.value) {
      return
    }

    // Handle window resize
    window.addEventListener('resize', () => {
      if (this.map) {
        this.map.resize()
      }
    })

    // Handle container resize with ResizeObserver for better detection
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.map) {
          // Small delay to ensure DOM has updated
          setTimeout(() => {
            if (this.map) {
              this.map.resize()
            }
          }, 100)
        }
      })
      this.resizeObserver.observe(this.mapRef.value)
    }
  }

  private async setupCompassVisibilityHandling(): Promise<void> {
    log.debug('🧭 Setting up compass visibility handling...')

    try {
      // Register visibility handler only once per instance lifetime to
      // prevent multiple handlers (which cause duplicate service calls)
      if (!this.visibilityHandlerRegistered) {
        document.addEventListener('visibilitychange', this.boundVisibilityHandler)
        this.visibilityHandlerRegistered = true
      }

      // Handle window focus changes (app switched away from)
      window.addEventListener('focus', (): void => {
        log.debug('🧭 App focused - requesting compass updates')
        void this.compassService.requestCompassUpdates()
      })

      window.addEventListener('blur', (): void => {
        log.debug('🧭 App blurred - releasing compass updates')
        void this.compassService.releaseCompassUpdates()
      })

      // Start compass updates if currently visible
      if (document.visibilityState === 'visible') {
        await this.compassService.requestCompassUpdates()
      }

      log.debug('🧭 Compass visibility-based management enabled')
    } catch (error) {
      log.error('🧭 Error setting up compass visibility handling:', error)
    }
  }

  private extentSaveTimer?: ReturnType<typeof setTimeout>

  private setupMapExtentPersistence(): void {
    if (!this.map) {
      return
    }

    // Add event listeners to save map extent when user navigates
    this.map.on('moveend', () => {
      this.debouncedSaveMapExtent()
    })

    this.map.on('zoomend', () => {
      this.debouncedSaveMapExtent()
    })
  }

  private debouncedSaveMapExtent(): void {
    if (this.extentSaveTimer) {
      clearTimeout(this.extentSaveTimer)
    }
    this.extentSaveTimer = setTimeout(() => this.saveMapExtent(), 500)
  }

  private saveMapExtent(): void {
    if (!this.map) {
      return
    }

    const center = this.map.getCenter()
    const zoom = this.map.getZoom()

    LocationPersistenceService.storeLocation({
      lat: center.lat,
      lng: center.lng,
      zoom: zoom,
      timestamp: Date.now(),
      source: 'manual',
    })

    log.debug('🗺️ Map extent saved:', {
      lat: center.lat.toFixed(6),
      lng: center.lng.toFixed(6),
      zoom: zoom,
    })
  }

  private activityCallbackRegistered = false

  private async startLocationTracking(): Promise<void> {
    // Preview / disableLocation maps are static snapshots — never touch the
    // GPS stack, never move the camera to the user's current location.
    if (!this.locationEnabled) return
    // Register this map as a location consumer (demand-gates the foreground
    // service in locationService) and bind our view for marker/UI rendering.
    // Idempotent across the multiple call sites (connect, permission grant).
    if (!this.locationConsumerActive) {
      this.locationConsumerActive = true
      locationService.attachView(this.locationView)
      locationService.addConsumer()
    }
    // Get an immediate fix so the map doesn't wait for the first periodic update
    void locationService.requestImmediateLocation()
    // Subscribe to activity changes for status bar display (once only)
    if (!this.activityCallbackRegistered) {
      this.activityCallbackRegistered = true
      this.compassService.onActivityChanged(state => {
        this.currentActivity = state.activity
      })
    }
  }

  private async startCompassTracking(): Promise<void> {
    if (!this.locationEnabled) return
    await this.locationTrackingManager.startCompassTracking()
  }

  private async toggleRecording(): Promise<void> {
    // Route through locationService so it can (a) force a final native-buffer
    // drain on stop and (b) re-evaluate the demand gate: when recording stops
    // with the screen off / no visible map, GPS is released automatically.
    await locationService.toggleRecording()
  }

  /**
   * Show a notification to the user
   */
  private showNotification(
    type: 'success' | 'error',
    title: string,
    message?: string,
    entityId?: string
  ): void {
    // Create notification element
    const notification = document.createElement('div')
    notification.className = `notification ${type}`

    // Create content structure
    const content = document.createElement('div')
    content.className = 'notification-content'

    const textDiv = document.createElement('div')
    textDiv.className = 'notification-text'

    const titleDiv = document.createElement('div')
    titleDiv.className = 'notification-title'
    titleDiv.textContent = title
    textDiv.appendChild(titleDiv)

    if (message) {
      const messageDiv = document.createElement('div')
      messageDiv.className = 'notification-message'
      messageDiv.textContent = message
      textDiv.appendChild(messageDiv)
    }

    if (entityId) {
      const actionsDiv = document.createElement('div')
      actionsDiv.className = 'notification-actions'

      const linkButton = document.createElement('button')
      linkButton.className = 'notification-link'
      linkButton.textContent = 'View Activity'
      linkButton.addEventListener('click', () => this.navigateToActivity(entityId))

      actionsDiv.appendChild(linkButton)
      textDiv.appendChild(actionsDiv)
    }

    const closeButton = document.createElement('button')
    closeButton.className = 'notification-close'
    closeButton.textContent = '×'
    closeButton.addEventListener('click', () => {
      log.debug('📢 Close button clicked')
      this.removeNotification(notification)
    })

    content.appendChild(textDiv)
    content.appendChild(closeButton)
    notification.appendChild(content)

    // Add to shadow DOM
    this.shadowRoot?.appendChild(notification)

    // Animate in
    setTimeout(() => {
      notification.classList.add('show')
    }, 100)

    // Auto-remove after 8 seconds (success) or 12 seconds (error)
    const autoRemoveDelay = type === 'success' ? 8000 : 12000
    setTimeout(() => {
      this.removeNotification(notification)
    }, autoRemoveDelay)

    log.debug(`📢 Notification shown: ${type} - ${title}${message ? ` (${message})` : ''}`)
  }

  /**
   * Remove a notification with animation
   */
  private removeNotification(notification: HTMLElement): void {
    log.debug('📢 Removing notification')
    if (notification.parentElement) {
      notification.classList.remove('show')
      setTimeout(() => {
        if (notification.parentElement) {
          notification.remove()
          log.debug('📢 Notification removed')
        }
      }, 300)
    } else {
      log.debug('📢 Notification has no parent element')
    }
  }

  /**
   * Navigate to activity view (called from notification)
   */
  private navigateToActivity(activityId: string): void {
    log.debug(`🗂️ Navigating to activity: ${activityId}`)

    // Use the router to navigate directly to the activity view
    router.navigate(`/activities/${activityId}`)
  }

  private toggleFollowUser(): void {
    if (this.isFollowingUser) {
      this.isFollowingUser = false
    } else {
      this.isFollowingUser = true
      if (this.followPosition) {
        this.map?.easeTo({ center: this.followPosition, duration: 300 })
      } else {
        void this.locationTrackingManager.requestImmediateLocation()
      }
    }
  }

  /** Update follow position; pans map if follow mode is active */
  followTo(lng: number, lat: number, bearing?: number): void {
    this.followPosition = [lng, lat]
    if (!this.isFollowingUser || !this.map) return

    if (this.is3dMode && bearing != null) {
      if (this.smoothedBearing == null) {
        this.smoothedBearing = bearing
      } else {
        // Shortest-arc interpolation to avoid spinning through 360→0
        let delta = bearing - this.smoothedBearing
        if (delta > 180) delta -= 360
        if (delta < -180) delta += 360
        this.smoothedBearing = (((this.smoothedBearing + delta * 0.15) % 360) + 360) % 360
      }
      this.map.jumpTo({ center: [lng, lat], bearing: this.smoothedBearing })
    } else {
      this.map.setCenter([lng, lat])
    }
  }

  private async checkLocationPermission(): Promise<void> {
    try {
      const hasPermission = await this.compassService.hasLocationPermission()
      this.locationPermissionGranted = hasPermission

      // Update trip feature manager with initial location permission state
      tripFeatureManager.setLocationPermission(hasPermission)

      // Set attribute for CSS styling immediately
      if (hasPermission) {
        this.setAttribute('has-location-permission', '')
        void this.startLocationTracking()
      } else {
        this.removeAttribute('has-location-permission')
      }

      // Update context mode after checking permission
      this.updateContextMode()
    } catch (error) {
      log.error('Error checking location permission:', error)
      this.locationPermissionGranted = false
      this.removeAttribute('has-location-permission')
      tripFeatureManager.setLocationPermission(false)
      this.updateContextMode()
    }
  }

  private handleLocationPermissionChange(event: CustomEvent): void {
    const { available } = event.detail
    this.locationPermissionGranted = available
    log.debug('📍 Map: Location permission changed:', available)

    // Update trip feature manager with location permission state
    tripFeatureManager.setLocationPermission(available)

    // Set attribute for CSS styling immediately
    if (available) {
      this.setAttribute('has-location-permission', '')
      // Start location tracking if permission is now available
      void this.startLocationTracking()
    } else {
      this.removeAttribute('has-location-permission')
      // Permission revoked: drop our consumer demand. The service stops unless
      // a recording is still active (in which case it can't capture anyway, but
      // we don't forcibly kill an in-progress recording's lifecycle here).
      if (this.locationConsumerActive) {
        locationService.removeConsumer()
        this.locationConsumerActive = false
      }
      this.hasLocationData = false
      this.requestUpdate()
    }

    // Update context mode after permission change
    this.updateContextMode()
  }

  private _layerSwitcherLoaded = false
  private async ensureLayerSwitcherLoaded(): Promise<void> {
    if (this._layerSwitcherLoaded) return
    this._layerSwitcherLoaded = true
    await import('./layer-switcher')
  }

  private _editorsLoaded = false
  private async ensureEditorsLoaded(): Promise<void> {
    if (this._editorsLoaded) return
    this._editorsLoaded = true
    await Promise.all([
      import('./color-picker-dropdown'),
      import('./stroke-style-dropdown'),
      import('./stroke-editor-dropdown'),
      import('./marker-editor-dropdown'),
      import('./label-editor-dropdown'),
      import('./tag-editor-dropdown.js'),
      import('./layer-picker-dropdown'),
    ])
  }

  private toggleLayerSwitcher(): void {
    if (!this.showLayerSwitcher) {
      void this.ensureLayerSwitcherLoaded()
    }
    this.showLayerSwitcher = !this.showLayerSwitcher
  }

  private handleModeChanged(event: CustomEvent): void {
    const mode = event.detail.mode as import('../../services/trip-feature-manager.js').MapMode
    // Picking a FAB mode clears any in-progress measure (it's a transient state).
    if (this.measureActive) {
      this.cleanupMeasure()
    }
    this.exitAllEditModes()
    this.hideAllEditHandles()
    BaseShape.ignoreNextMapClick = 0
    tripFeatureManager.setMapMode(mode)
    this.updateContextMode()
  }

  // --- Timeline event handlers ---

  private handleTimelinePlay(): void {
    timelineService.play()
  }
  private handleTimelinePause(): void {
    timelineService.pause()
  }
  private handleTimelineReset(): void {
    timelineService.reset()
  }
  private handleTimelineLoopToggled(): void {
    timelineService.toggleLoop()
  }
  private handleTimelineSpeedChanged(): void {
    timelineService.cyclePlaybackDuration()
  }

  private handleTimelineScrub(event: CustomEvent): void {
    const { time } = event.detail
    if (time instanceof Date) {
      timelineService.setCurrentTime(time)
    }
  }

  private handleTimelineRangeChanged(event: CustomEvent): void {
    const { start, end } = event.detail
    if (start instanceof Date && end instanceof Date) {
      timelineService.setTimeRange(start, end)
    }
  }

  private handleLocationSearchNavigation(event: CustomEvent): void {
    const { coordinates, bounds, location, osmType, osmId, icon, source } = event.detail

    if (!this.map) {
      return
    }

    if (source === 'cotrex') {
      if (!cotrexLayerService.enabled) {
        cotrexLayerService.enable()
      }
      this.clearSearchResult()
      const cotrexMeta = event.detail.cotrexMeta as { placeId: number } | undefined
      const placeId = cotrexMeta?.placeId ?? 0
      selectionService.replaceSelection([
        { source: 'cotrex-trail', name: location, placeId, featureId: '' },
      ])
      if (bounds) {
        const swLat = bounds.sw[0]
        const swLng = bounds.sw[1]
        const neLat = bounds.ne[0]
        const neLng = bounds.ne[1]
        const mapLibreBounds = new maplibregl.LngLatBounds([swLng, swLat], [neLng, neLat])
        this.map.fitBounds(mapLibreBounds, {
          padding: { top: 20, bottom: 20, left: 20, right: 20 },
        })
      } else {
        this.map.jumpTo({ center: [coordinates[1], coordinates[0]], zoom: 14 })
      }
      return
    }

    if (source === 'openbeta') {
      if (!openBetaLayerService.isEnabled) {
        openBetaLayerService.enable()
      }
      this.clearSearchResult()
      this.map.flyTo({ center: [coordinates[1], coordinates[0]], zoom: 14 })
      return
    }

    this.clearSearchResult()
    selectionService.clearAll()

    // Position camera
    if (bounds) {
      const swLat = bounds.sw[0]
      const swLng = bounds.sw[1]
      const neLat = bounds.ne[0]
      const neLng = bounds.ne[1]
      const mapLibreBounds = new maplibregl.LngLatBounds([swLng, swLat], [neLng, neLat])
      this.map.fitBounds(mapLibreBounds, { padding: { top: 20, bottom: 20, left: 20, right: 20 } })
    } else {
      this.map.jumpTo({ center: [coordinates[1], coordinates[0]], zoom: 14 })
    }

    if (osmType === 'N' || !osmType) {
      this.addSearchMarker(coordinates, location, icon)
    } else if ((osmType === 'W' || osmType === 'R') && osmId) {
      void this.fetchAndDrawOverpassGeometry(osmType, osmId, coordinates, bounds, location)
    } else if (bounds) {
      this.drawSearchExtent(bounds, location)
    } else {
      this.addSearchMarker(coordinates, location, icon)
    }
  }

  private clearSearchResult(): void {
    if (this.searchResultOutline) {
      try {
        if (this.map?.getLayer(this.searchResultOutlineFillLayerId)) {
          this.map.removeLayer(this.searchResultOutlineFillLayerId)
        }
        if (this.map?.getLayer(this.searchResultOutlineStrokeLayerId)) {
          this.map.removeLayer(this.searchResultOutlineStrokeLayerId)
        }
        if (this.map?.getLayer(this.searchResultLineLayerId)) {
          this.map.removeLayer(this.searchResultLineLayerId)
        }
        if (this.map?.getSource(this.searchResultOutlineSourceId)) {
          this.map.removeSource(this.searchResultOutlineSourceId)
        }
      } catch {
        // layers may already be removed
      }
      this.searchResultOutline = undefined
    }
    if (this.searchResultLabel) {
      this.searchResultLabel.remove()
      this.searchResultLabel = undefined
    }
    if (this.searchResultMarker) {
      this.searchResultMarker.remove()
      this.searchResultMarker = undefined
    }
    selectionService.clearAll()
  }

  private addSearchMarker(coordinates: [number, number], location?: string, icon?: string): void {
    if (!this.map) return

    const marker = buildMarkerHtml({
      markerIcon: icon ?? '',
      markerSize: 24,
    })

    const element = document.createElement('div')
    element.innerHTML = marker.html
    element.style.width = `${marker.size[0]}px`
    element.style.height = `${marker.size[1]}px`
    element.style.cursor = 'pointer'

    const offsetX = marker.size[0] / 2 - marker.anchor[0]
    const offsetY = marker.size[1] / 2 - marker.anchor[1]

    const lngLat: [number, number] = [coordinates[1], coordinates[0]]

    this.searchResultMarker = new maplibregl.Marker({
      element,
      anchor: 'center',
      offset: [offsetX, -offsetY],
    })
      .setLngLat(lngLat)
      .addTo(this.map)

    if (location) {
      const label = { text: location, visible: true }
      const markerSize = 24
      const strokeWidth = 2
      const containerW = 300
      const style = computeLabelStyle(label, markerSize, strokeWidth, containerW)
      const labelHtml = `<div style="color:${style.color};font-size:${style.fontSize}px;font-family:${style.fontFamily};font-weight:bold;white-space:nowrap;text-shadow:${style.textShadow};pointer-events:none;text-align:${style.textAlign};padding:${style.padding};">${location}</div>`
      const labelEl = document.createElement('div')
      labelEl.innerHTML = labelHtml
      labelEl.style.width = `${containerW}px`
      this.searchResultLabel = new maplibregl.Marker({
        element: labelEl,
        anchor: 'center',
        offset: [0, style.anchorOffsetY],
      })
        .setLngLat(lngLat)
        .addTo(this.map)
    }
  }

  private drawSearchExtent(
    bounds: { sw: [number, number]; ne: [number, number] },
    location?: string
  ): void {
    if (!this.map) return
    const swLat = bounds.sw[0]
    const swLng = bounds.sw[1]
    const neLat = bounds.ne[0]
    const neLng = bounds.ne[1]

    const rectCoords = [
      [swLng, swLat],
      [neLng, swLat],
      [neLng, neLat],
      [swLng, neLat],
      [swLng, swLat],
    ]

    this.map.addSource(this.searchResultOutlineSourceId, {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [rectCoords] },
        properties: {},
      },
    })

    addShapeLayerOrdered(this.map, {
      id: this.searchResultOutlineFillLayerId,
      type: 'fill',
      source: this.searchResultOutlineSourceId,
      paint: { 'fill-color': '#3b82f6', 'fill-opacity': 0.1 },
    })

    addShapeLayerOrdered(this.map, {
      id: this.searchResultOutlineStrokeLayerId,
      type: 'line',
      source: this.searchResultOutlineSourceId,
      paint: {
        'line-color': '#3b82f6',
        'line-width': 2,
        'line-opacity': 0.8,
        'line-dasharray': [5, 5],
      },
    })

    this.searchResultOutline = {
      sourceId: this.searchResultOutlineSourceId,
      fillLayerId: this.searchResultOutlineFillLayerId,
      strokeLayerId: this.searchResultOutlineStrokeLayerId,
    }

    if (location) {
      this.addSearchLabel([swLng, swLat, neLng, neLat], location)
    }
  }

  private addSearchLabel(extent: [number, number, number, number], location: string): void {
    if (!this.map) return
    const centerLng = (extent[0] + extent[2]) / 2
    const centerLat = (extent[1] + extent[3]) / 2

    const labelElement = document.createElement('div')
    labelElement.innerHTML = `<div style="background: transparent; color: white; padding: 6px 12px; font-size: 13px; font-weight: bold; text-shadow: 1px 1px 2px rgba(0,0,0,0.9), -1px -1px 2px rgba(0,0,0,0.9), 1px -1px 2px rgba(0,0,0,0.9), -1px 1px 2px rgba(0,0,0,0.9); transform: translate(-50%, -50%); position: absolute; top: 0; left: 0;">${location}</div>`

    this.searchResultLabel = new maplibregl.Marker({ element: labelElement, anchor: 'center' })
      .setLngLat([centerLng, centerLat])
      .addTo(this.map)
  }

  private async fetchAndDrawOverpassGeometry(
    osmType: 'W' | 'R',
    osmId: number,
    coordinates: [number, number],
    bounds: { sw: [number, number]; ne: [number, number] } | undefined,
    location?: string
  ): Promise<void> {
    const elementType = osmType === 'W' ? 'way' : 'relation'
    const query = `[out:json];${elementType}(${osmId});out geom;`

    try {
      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), 10000)

      const response = await fetch('https://overpass-api.de/api/interpreter', {
        method: 'POST',
        body: `data=${encodeURIComponent(query)}`,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        signal: controller.signal,
      })
      clearTimeout(timeout)

      if (!response.ok) throw new Error(`Overpass HTTP ${response.status}`)

      const data = await response.json()
      const element = data.elements?.[0]
      if (!element) throw new Error('No element returned')

      const geojson = overpassToGeoJSON(element)
      if (!geojson) throw new Error('Could not convert geometry')

      this.drawSearchGeometry(geojson, location)
    } catch (err) {
      log.warn('Overpass fetch failed, falling back to bounds', err)
      if (bounds) {
        this.drawSearchExtent(bounds, location)
      } else {
        this.addSearchMarker(coordinates, location)
      }
    }
  }

  private drawSearchGeometry(geojson: GeoJSON.Feature, location?: string): void {
    if (!this.map) return

    this.map.addSource(this.searchResultOutlineSourceId, {
      type: 'geojson',
      data: geojson,
    })

    const geomType = geojson.geometry.type

    if (geomType === 'Polygon' || geomType === 'MultiPolygon') {
      addShapeLayerOrdered(this.map, {
        id: this.searchResultOutlineFillLayerId,
        type: 'fill',
        source: this.searchResultOutlineSourceId,
        paint: { 'fill-color': '#3b82f6', 'fill-opacity': 0.1 },
      })
      addShapeLayerOrdered(this.map, {
        id: this.searchResultOutlineStrokeLayerId,
        type: 'line',
        source: this.searchResultOutlineSourceId,
        paint: {
          'line-color': '#3b82f6',
          'line-width': 2,
          'line-opacity': 0.8,
        },
      })
      this.searchResultOutline = {
        sourceId: this.searchResultOutlineSourceId,
        fillLayerId: this.searchResultOutlineFillLayerId,
        strokeLayerId: this.searchResultOutlineStrokeLayerId,
      }
    } else {
      addShapeLayerOrdered(this.map, {
        id: this.searchResultLineLayerId,
        type: 'line',
        source: this.searchResultOutlineSourceId,
        paint: {
          'line-color': '#3b82f6',
          'line-width': 3,
          'line-opacity': 0.8,
        },
      })
      this.searchResultOutline = {
        sourceId: this.searchResultOutlineSourceId,
        fillLayerId: this.searchResultLineLayerId,
        strokeLayerId: this.searchResultLineLayerId,
      }
    }

    if (location) {
      const bbox = this.getGeojsonBbox(geojson)
      this.addSearchLabel(bbox, location)
    }
  }

  private getGeojsonBbox(geojson: GeoJSON.Feature): [number, number, number, number] {
    let minLng = Infinity,
      minLat = Infinity,
      maxLng = -Infinity,
      maxLat = -Infinity

    const processCoords = (coords: unknown): void => {
      if (!Array.isArray(coords)) return
      if (typeof coords[0] === 'number') {
        const [lng, lat] = coords as [number, number]
        if (lng < minLng) minLng = lng
        if (lat < minLat) minLat = lat
        if (lng > maxLng) maxLng = lng
        if (lat > maxLat) maxLat = lat
      } else {
        for (const c of coords) processCoords(c)
      }
    }

    const geom = geojson.geometry as { coordinates: unknown }
    processCoords(geom.coordinates)
    return [minLng, minLat, maxLng, maxLat]
  }

  private zoomIn(): void {
    if (this.map) {
      this.map.zoomIn()
    }
  }

  private zoomOut(): void {
    if (this.map) {
      this.map.zoomOut()
    }
  }

  private toggle3dMode(): void {
    if (!this.map) {
      return
    }
    this.is3dMode = !this.is3dMode
    if (this.is3dMode) {
      // Add terrain DEM source if not present
      if (!this.map.getSource('terrain-dem')) {
        this.map.addSource('terrain-dem', {
          type: 'raster-dem',
          tiles: this.cairnTiles('terrarium-dem', DEM_URL),
          tileSize: DEM_TILE_SIZE,
          maxzoom: TERRAIN_MESH_MAX_ZOOM,
          encoding: 'terrarium',
        })
      }
      hillshadeService.enable()
      this.map.setTerrain({ source: 'terrain-dem', exaggeration: 1.0 })
      this.map.setProjection({ type: 'globe' })
      this.map.dragRotate.enable()
      this.map.touchPitch.enable()
      this.map.easeTo({ pitch: 50, duration: 400 })
    } else {
      this.map.setTerrain(null)
      this.map.setProjection({ type: 'mercator' })
      this.map.easeTo({ pitch: 0, bearing: 0, duration: 400 })
      setTimeout(() => {
        this.map?.dragRotate.disable()
        this.map?.touchPitch.disable()
      }, 400)
    }
  }

  private closeLayerSwitcher(): void {
    this.showLayerSwitcher = false
  }

  /**
   * Wrap a raster tile URL template so MapLibre fetches it through the
   * persistent tile cache. The resulting URL uses the cairn:// scheme and is
   * handled by cairnProtocolHandler.
   */
  private cairnTiles(sourceId: string, urlTemplate: string): string[] {
    return [cairnifyTileUrl(sourceId, urlTemplate)]
  }

  private changeBaseLayer(event: CustomEvent): void {
    const layer: BaseLayer = event.detail.layer

    if (!this.map || !this.currentTileLayer) {
      return
    }

    if (layer.type === 'vector') {
      void vectorStyleCacheService
        .loadStyle(layer.url)
        .then(rewrittenStyle => {
          if (!this.map) return
          void this.map.once('style.load', () => {
            if (this.map) {
              ensureAnchors(this.map)
            }
            this.reAddOverlaysAndFeatures()
          })
          this.map.setStyle(rewrittenStyle)
          this.isVectorBase = true
        })
        .catch(err => log.error('Failed to load vector style:', err))
      return
    } else {
      if (this.isVectorBase) {
        // Switching from vector to raster — set an empty style first, then add base + overlays
        this.isVectorBase = false
        void this.map.once('style.load', () => {
          this.addRasterBaseLayer(layer)
          this.reAddOverlaysAndFeatures()
        })
        this.map.setStyle({
          version: 8,
          sources: {},
          layers: [],
          glyphs: CACHED_GLYPHS,
        })
      } else {
        // Raster to raster — simple swap
        if (this.map.getLayer(this.currentBaseLayerId)) {
          this.map.removeLayer(this.currentBaseLayerId)
        }
        if (this.map.getSource(this.currentBaseSourceId)) {
          this.map.removeSource(this.currentBaseSourceId)
        }
        this.addRasterBaseLayer(layer)
      }
    }

    this.currentTileLayer = { sourceId: this.currentBaseSourceId, layerId: this.currentBaseLayerId }
    log.debug(`Switched to layer: ${layer.name}`)
  }

  private loadFallbackRasterBase(): void {
    if (!this.map) {
      return
    }
    this.map.addSource(this.currentBaseSourceId, {
      type: 'raster',
      tiles: this.cairnTiles('osm-fallback', 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'),
      tileSize: 256,
      maxzoom: 19,
    })
    // Insert below the overlays anchor so the base sits beneath every band.
    this.map.addLayer(
      {
        id: this.currentBaseLayerId,
        type: 'raster',
        source: this.currentBaseSourceId,
      },
      this.map.getLayer(ANCHOR_OVERLAYS) ? ANCHOR_OVERLAYS : undefined
    )
    ensureAnchors(this.map)
  }

  private addRasterBaseLayer(layer: BaseLayer): void {
    if (!this.map) {
      return
    }
    this.map.addSource(this.currentBaseSourceId, {
      type: 'raster',
      tiles: this.cairnTiles(layer.id, layer.url),
      tileSize: layer.options.tileSize ?? 256,
      ...(layer.options.maxZoom != null && { maxzoom: layer.options.maxZoom }),
    })
    // Insert below the overlays anchor so the base sits beneath every band.
    this.map.addLayer(
      {
        id: this.currentBaseLayerId,
        type: 'raster',
        source: this.currentBaseSourceId,
      },
      this.map.getLayer(ANCHOR_OVERLAYS) ? ANCHOR_OVERLAYS : undefined
    )
    ensureAnchors(this.map)
  }

  private reAddOverlaysAndFeatures(): void {
    // Style change wipes all layers — clear group tracking so re-adds
    // register fresh entries without duplicates.
    clearOverlayTracking()

    // Install anchors first so every subsequent overlay/shape insert lands
    // in the correct band regardless of call order.
    if (this.map) {
      ensureAnchors(this.map)
    }

    // Re-render trip features (tracks, shapes, etc.)
    void this.loadExistingFeatures()

    // Re-add terrain overlays that were active before style change
    slopeAngleService.reAdd()
    sunExposureService.reAdd()
    hillshadeService.reAdd()
    landCoverService.reAdd()
    snowDepthService.reAdd()
    ALL_NWM_SERVICES.forEach(s => s.reAdd())
    reAddOmLayers()
    contourService.reAdd()
    landOwnershipService.reAdd()
    cellularCoverageService.reAdd()
    emergencyLayerService.reAdd()
    iNaturalistService.reAdd()
    criticalHabitatService.reAdd()
    cotrexLayerService.reAdd()
    cotrexClosureService.reAdd()
    fireLayerService.reAdd()
    avalancheLayerService.reAdd()
    npsAlertsService.reAdd()
    backcountryOverlayService.reAdd()
    openBetaLayerService.reAdd()
    satelliteConstellationService.reAdd()
    losCoverageService.reAdd()
    graticuleService.reAdd()
    utmGridService.reAdd()
    mgrsGridService.reAdd()
    declinationService.reAdd()
    rangeRingsService.reAdd()
    plssService.reAdd()

    // Re-add 3D terrain if active (hillshade is handled by hillshadeService.reAdd above)
    if (this.is3dMode && this.map) {
      if (!this.map.getSource('terrain-dem')) {
        this.map.addSource('terrain-dem', {
          type: 'raster-dem',
          tiles: this.cairnTiles('terrarium-dem', DEM_URL),
          tileSize: DEM_TILE_SIZE,
          maxzoom: TERRAIN_MESH_MAX_ZOOM,
          encoding: 'terrarium',
        })
      }
      this.map.setTerrain({ source: 'terrain-dem', exaggeration: 1.0 })
      this.map.setProjection({ type: 'globe' })
    }

    if (this.map) {
      try {
        const raw = localStorage.getItem('cairn_tier_order_overrides')
        const overrides = raw
          ? new Map(Object.entries(JSON.parse(raw) as Record<string, number>))
          : undefined
        reorderAllOverlays(this.map, overrides)
      } catch {
        reorderAllOverlays(this.map)
      }
    }

    // Clear overlay tracking — standard overlays need to be re-toggled by user
    this.overlaySourceIds.clear()
    this.overlayLayerIds.clear()
  }

  private addOverlayLayer = (event: CustomEvent): void => {
    const layer: OverlayLayer = event.detail.layer
    if (!this.map || this.overlayLayerIds.has(layer.id)) {
      return
    }

    const sourceId = `overlay-${layer.id}`
    const layerId = `overlay-layer-${layer.id}`

    if (layer.subdaily && layer.subdailyWmtsUrl) {
      // Subdaily WMTS mode — use date-only initially (always valid), then async-resolve to exact time
      const dateOnly = timelineService.getState().currentTime.toISOString().split('T')[0]
      const tileUrl = layer.subdailyWmtsUrl.replace('{Time}', dateOnly)
      this.map.addSource(sourceId, {
        type: 'raster',
        tiles: this.cairnTiles(`${layer.id}:${dateOnly}`, tileUrl),
        tileSize: 256,
        ...((layer.options.maxNativeZoom ?? layer.options.maxZoom) != null && {
          maxzoom: (layer.options.maxNativeZoom ?? layer.options.maxZoom)!,
        }),
      })
    } else if (layer.type === 'wms' && layer.wmsOptions) {
      // For sparse layers (cmrCollection), omit TIME to get GIBS "best available" per tile.
      // For other time-aware layers, use the current timeline time clamped by latency.
      const effectiveTime = layer.cmrCollection
        ? undefined
        : layer.timeAware
          ? CairnMap.formatWmsTime(timelineService.getState().currentTime, layer.latencyDays)
          : layer.wmsOptions.time

      // Create WMS layer - need to construct the WMS URL
      const wmsTemplate = buildWmsTileUrl(layer.url, layer.wmsOptions, effectiveTime)

      this.map.addSource(sourceId, {
        type: 'raster',
        tiles: this.cairnTiles(`${layer.id}:${effectiveTime ?? 'best'}`, wmsTemplate),
        tileSize: layer.options.tileSize ?? 256,
        ...((layer.options.maxNativeZoom ?? layer.options.maxZoom) != null && {
          maxzoom: (layer.options.maxNativeZoom ?? layer.options.maxZoom)!,
        }),
      })
    } else {
      this.map.addSource(sourceId, {
        type: 'raster',
        tiles: this.cairnTiles(layer.id, layer.url),
        tileSize: layer.options.tileSize ?? 256,
        ...((layer.options.maxNativeZoom ?? layer.options.maxZoom) != null && {
          maxzoom: (layer.options.maxNativeZoom ?? layer.options.maxZoom)!,
        }),
      })
    }

    addOverlayLayerOrdered(
      this.map,
      {
        id: layerId,
        type: 'raster',
        source: sourceId,
        paint: {
          'raster-opacity': layer.options.opacity ?? 1,
        },
      },
      layer.id
    )

    this.overlaySourceIds.set(layer.id, sourceId)
    this.overlayLayerIds.set(layer.id, layerId)

    if (layer.timeAware) {
      this.timeAwareOverlays.set(layer.id, layer)

      // Async-resolve the best time and re-swap for layers that need it
      const currentTime = timelineService.getState().currentTime
      if (layer.subdailyWmtsUrl) {
        // GOES-type: clamp to available time range
        const layerMatch = layer.subdailyWmtsUrl.match(/\/best\/([^/]+)\//)
        const layerIdentifier = layerMatch?.[1] ?? ''
        void GibsCapabilitiesService.clampToAvailableTime(
          layerIdentifier,
          currentTime,
          !!layer.subdaily
        ).then(resolvedTime => {
          if (!this.map) {
            return
          }
          this.swapOverlaySource(layer.id, layer, resolvedTime)
        })
      } else if (layer.cmrCollection) {
        // For recent dates, keep no-TIME ("best available" — GIBS serves most recent per tile).
        // Only resolve via CMR when the timeline is scrolled to an older date.
        const now = new Date()
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
        if (currentTime <= weekAgo) {
          const requestedDate = CairnMap.formatWmsTime(currentTime, layer.latencyDays)
          void GibsCapabilitiesService.getNearestAvailableDate(
            layer.cmrCollection,
            requestedDate
          ).then(resolvedDate => {
            if (!this.map) {
              return
            }
            this.swapOverlaySource(layer.id, layer, resolvedDate)
          })
        }
        // else: already added with no TIME — GIBS serves best available per tile
      }
    }

    log.debug(
      `Added overlay: ${layer.name} (${layer.type ?? 'wmts'})${layer.timeAware ? ' [time-aware]' : ''}`
    )
  }

  private removeOverlayLayer = (event: CustomEvent): void => {
    const layer: OverlayLayer = event.detail.layer
    if (!this.map || !this.overlayLayerIds.has(layer.id)) {
      return
    }

    const layerId = this.overlayLayerIds.get(layer.id)!
    const sourceId = this.overlaySourceIds.get(layer.id)!

    this.map.removeLayer(layerId)
    this.map.removeSource(sourceId)
    this.overlayLayerIds.delete(layer.id)
    this.overlaySourceIds.delete(layer.id)
    this.timeAwareOverlays.delete(layer.id)
    this.lastOverlayTimePerLayer.delete(layer.id)
    this.lastOverlaySwapTime.delete(layer.id)
    const pendingTimer = this.pendingOverlaySwap.get(layer.id)
    if (pendingTimer) {
      clearTimeout(pendingTimer)
      this.pendingOverlaySwap.delete(layer.id)
    }

    log.debug(`Removed overlay: ${layer.name}`)
  }

  // Legend registration is decoupled from the base-overlay add/remove lifecycle so an
  // overlay's Key FAB entry tracks its *visibility* (and survives a time-composite
  // toggle, which swaps the render layer but keeps the layer visible). Covers both
  // colour-ramp (gradientLegend) and discrete (classificationLegend) overlays.
  private registerOverlayLegend = (event: CustomEvent): void => {
    const layer: OverlayLayer = event.detail.layer
    if (layer.gradientLegend || layer.classificationLegend) {
      this.legendOverlays = new Map(this.legendOverlays).set(layer.id, layer)
    }
  }

  private unregisterOverlayLegend = (event: CustomEvent): void => {
    const layer: OverlayLayer = event.detail.layer
    if (this.legendOverlays.has(layer.id)) {
      const next = new Map(this.legendOverlays)
      next.delete(layer.id)
      this.legendOverlays = next
    }
  }

  private handleOverlayOpacityChanged = (event: CustomEvent): void => {
    const { layerId, opacity } = event.detail
    if (typeof opacity !== 'number') return
    const mapLayerId = this.overlayLayerIds.get(layerId)
    if (this.map && mapLayerId && this.map.getLayer(mapLayerId)) {
      this.map.setPaintProperty(mapLayerId, 'raster-opacity', opacity)
    }
  }

  private handleReorderOverlays = (event: CustomEvent): void => {
    if (!this.map) return
    const userTierOverrides = event.detail.tierOrderOverrides as
      | ReadonlyMap<string, number>
      | undefined
    reorderAllOverlays(this.map, userTierOverrides)
  }

  private static formatWmsTime(date: Date, latencyDays = 0): string {
    return formatWmsTime(date, latencyDays)
  }

  private static formatWmtsSubdailyTime(date: Date): string {
    return formatWmtsSubdailyTime(date)
  }

  /**
   * Update time-aware overlay tiles when timeline time changes.
   * Each layer tracks its own last time string to avoid unnecessary refreshes.
   * Layers with cmrCollection resolve to the nearest available date via CMR API.
   */
  private updateTimeAwareOverlays(currentTime: Date): void {
    if (!this.map || this.timeAwareOverlays.size === 0) {
      return
    }

    for (const [id, layer] of this.timeAwareOverlays) {
      // Compute a coarse key for dedup — subdaily uses minute-level, daily uses date
      const coarseKey =
        layer.subdaily && layer.subdailyWmtsUrl
          ? CairnMap.formatWmtsSubdailyTime(currentTime)
          : CairnMap.formatWmsTime(currentTime, layer.latencyDays)

      // Skip if the coarse key hasn't changed for this layer
      const lastRequested = this.lastOverlayTimePerLayer.get(id)
      if (coarseKey === lastRequested) {
        continue
      }
      this.lastOverlayTimePerLayer.set(id, coarseKey)

      // Throttle: don't swap more frequently than every 5 seconds per layer
      const now = performance.now()
      const lastSwap = this.lastOverlaySwapTime.get(id) ?? 0
      const elapsed = now - lastSwap

      // Clear any pending deferred swap for this layer
      const pending = this.pendingOverlaySwap.get(id)
      if (pending) {
        clearTimeout(pending)
      }

      const doSwap = (): void => {
        this.lastOverlaySwapTime.set(id, performance.now())
        this.pendingOverlaySwap.delete(id)
        // Re-read the latest stored key in case it changed while deferred
        const latestKey = this.lastOverlayTimePerLayer.get(id) ?? coarseKey
        this.resolveAndSwap(id, layer, currentTime, latestKey)
      }

      if (elapsed >= CairnMap.OVERLAY_SWAP_MIN_INTERVAL_MS) {
        doSwap()
      } else {
        const delay = CairnMap.OVERLAY_SWAP_MIN_INTERVAL_MS - elapsed
        this.pendingOverlaySwap.set(id, setTimeout(doSwap, delay))
      }
    }
  }

  /**
   * Resolve the actual time string for a layer and perform the source swap.
   * Subdaily layers query WMTS capabilities for the nearest valid time slot.
   * Sparse layers query CMR for the nearest available date.
   * Daily layers use the time string directly.
   */
  private resolveAndSwap(
    id: string,
    layer: OverlayLayer,
    currentTime: Date,
    fallbackTime: string
  ): void {
    if (!this.map) {
      return
    }

    if (layer.subdailyWmtsUrl) {
      // GOES-type layer: clamp to available time range via WMTS capabilities
      const layerMatch = layer.subdailyWmtsUrl.match(/\/best\/([^/]+)\//)
      const layerIdentifier = layerMatch?.[1] ?? ''
      void GibsCapabilitiesService.clampToAvailableTime(
        layerIdentifier,
        currentTime,
        !!layer.subdaily
      ).then(resolvedTime => {
        if (!this.map) {
          return
        }
        this.swapOverlaySource(id, layer, resolvedTime)
      })
    } else if (layer.cmrCollection) {
      // For sparse layers: if within a week of now, omit TIME to get GIBS "best available"
      // per tile (each tile gets its most recent acquisition). For older dates, use CMR.
      const now = new Date()
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
      if (currentTime > weekAgo) {
        // Recent: omit TIME — GIBS serves most recent per-tile
        this.swapOverlaySource(id, layer, '')
      } else {
        void GibsCapabilitiesService.getNearestAvailableDate(
          layer.cmrCollection,
          fallbackTime
        ).then(resolvedDate => {
          if (!this.map) {
            return
          }
          this.swapOverlaySource(id, layer, resolvedDate)
        })
      }
    } else {
      this.swapOverlaySource(id, layer, fallbackTime)
    }
  }

  /**
   * Remove and re-add an overlay source/layer with a new TIME parameter.
   */
  private swapOverlaySource(id: string, layer: OverlayLayer, timeStr: string): void {
    if (!this.map) {
      return
    }
    const sourceId = this.overlaySourceIds.get(id)
    const layerId = this.overlayLayerIds.get(id)
    if (!sourceId || !layerId || !this.map.getLayer(layerId)) {
      return
    }

    const currentOpacity =
      (this.map.getPaintProperty(layerId, 'raster-opacity') as number | undefined) ??
      layer.options.opacity ??
      0.7

    this.map.removeLayer(layerId)
    this.map.removeSource(sourceId)

    if (layer.subdaily && layer.subdailyWmtsUrl) {
      // Subdaily WMTS mode — timeStr is ISO datetime, baked into URL template
      const tileUrl = layer.subdailyWmtsUrl.replace('{Time}', timeStr)
      this.map.addSource(sourceId, {
        type: 'raster',
        tiles: this.cairnTiles(`${layer.id}:${timeStr}`, tileUrl),
        tileSize: 256,
        ...((layer.options.maxNativeZoom ?? layer.options.maxZoom) != null && {
          maxzoom: (layer.options.maxNativeZoom ?? layer.options.maxZoom)!,
        }),
      })
    } else if (layer.type === 'wms' && layer.wmsOptions) {
      const wmsTemplate = buildWmsTileUrl(layer.url, layer.wmsOptions, timeStr)

      this.map.addSource(sourceId, {
        type: 'raster',
        tiles: this.cairnTiles(`${layer.id}:${timeStr}`, wmsTemplate),
        tileSize: layer.options.tileSize ?? 256,
        ...((layer.options.maxNativeZoom ?? layer.options.maxZoom) != null && {
          maxzoom: (layer.options.maxNativeZoom ?? layer.options.maxZoom)!,
        }),
      })
    } else {
      const newUrl = layer.wmtsLayer
        ? GibsCapabilitiesService.buildWMTSUrl(layer.wmtsLayer, { time: timeStr })
        : layer.url
      this.map.addSource(sourceId, {
        type: 'raster',
        tiles: this.cairnTiles(`${layer.id}:${timeStr}`, newUrl),
        tileSize: layer.options.tileSize ?? 256,
        ...((layer.options.maxNativeZoom ?? layer.options.maxZoom) != null && {
          maxzoom: (layer.options.maxNativeZoom ?? layer.options.maxZoom)!,
        }),
      })
    }

    addOverlayLayerOrdered(
      this.map,
      {
        id: layerId,
        type: 'raster',
        source: sourceId,
        paint: {
          'raster-opacity': currentOpacity,
        },
      },
      layer.id
    )
  }

  // ===== EDIT HANDLES MANAGEMENT =====

  private handleEditingModeChanged(event: CustomEvent): void {
    const isEditing = event.detail.isEditing
    if (isEditing) {
      void this.showEditHandlesForSelectedFeatures()
      this.disableMapPanning()
      log.debug('🔧 Map panning disabled for feature editing')
    } else {
      this.hideAllEditHandles()
      this.enableMapPanning()
      log.debug('🔧 Map panning enabled - exited feature editing')
    }
  }

  private async handleFeatureSelected(event: CustomEvent): Promise<void> {
    const featureId = event.detail.featureId
    log.debug('🔧 Feature selected:', featureId)

    // Update selected features state for context bar - need full objects, not just IDs
    await this.updateSelectedFeatures()

    // Shape component has already entered edit mode via its click handler
    // We just need to disable map panning
    const shapeComponent = this.shapeComponents.get(featureId)
    if (shapeComponent) {
      // Shape component already handled entering edit mode
      this.disableMapPanning()
    } else {
      // Legacy fallback for old system (should not happen with new architecture)
      void this.showEditHandles(featureId)
      this.disableMapPanning()
    }
  }

  private handleFeatureDeselected(_event: CustomEvent): void {
    log.debug('🔧 Feature deselected')

    // Update selected features state for context bar
    this.selectedFeatures = []

    this.hideAllEditHandles()
    this.exitAllEditModes()
    this.enableMapPanning()
    log.debug('🔧 Map panning enabled - feature deselected')
  }

  private async updateSelectedFeatures(): Promise<void> {
    const selectedFeatureIds = tripFeatureManager.getSelectedFeatures()
    const selectedFeatures = []

    for (const featureId of selectedFeatureIds) {
      const feature = await tripFeatureManager.getFeature(featureId)
      if (feature) {
        selectedFeatures.push(feature)
      }
    }

    this.selectedFeatures = selectedFeatures
  }

  private async handleFeatureDeleted(event: CustomEvent): Promise<void> {
    const featureId = event.detail.featureId
    const parentActivityId = await featureStorage.getParentActivity(featureId)

    const dialog = this.shadowRoot?.querySelector('confirm-dialog') as ConfirmDialog | null
    if (dialog) {
      dialog.headline = 'Delete Feature'
      dialog.message = parentActivityId
        ? 'This will also delete the associated activity. Delete?'
        : 'Delete selected feature?'
      const confirmed = await dialog.show()
      if (!confirmed) {
        return
      }
    }
    this.styleEditorManager.closeAllAndStopListening()

    const featureClone = await db.features.get(featureId)
    const { edgeService: es, nodeRef: nr } = await import('../../services/edge-service.js')
    const edges = featureClone ? await es.getByObject(nr('feature', featureId), 'has-feature') : []

    const shapeComponent = this.shapeComponents.get(featureId)
    if (shapeComponent) {
      await shapeComponent.deleteFeature()
    }

    if (parentActivityId) {
      await activityStorage.deleteActivity(parentActivityId)
    }

    if (featureClone) {
      undoStack.clearPendingEdit()
      undoStack.push({
        type: 'delete',
        label: `Deleted ${featureClone.name}`,
        features: [{ id: featureId, before: featureClone, after: null }],
        edges: edges.map(e => ({
          subject: e.subject,
          predicate: e.predicate,
          object: e.object,
          action: 'deleted' as const,
        })),
      })
      showToast('info', `Deleted ${featureClone.name}`, {
        tag: 'undo',
        action: { label: 'Undo', callback: () => void this.performUndo() },
      })
    }

    tripFeatureManager.clearSelection()
    this.selectedFeatures = []
    this.hideAllEditHandles()
    this.exitAllEditModes()
    this.updateContextMode()
    this.requestUpdate()
  }

  private removeShapeFromMap(featureId: string): void {
    const shape = this.shapeComponents.get(featureId)
    if (shape) {
      shape.removeFromMap()
      shape.remove()
    }
    this.shapeComponents.delete(featureId)
    tripFeatureManager.removeFromSelection(featureId)
    this.selectedFeatures = this.selectedFeatures.filter(f => f.id !== featureId)
  }

  private handleLayerDeleted(): void {
    const remaining = new Set<string>()
    void db.features.toArray().then(features => {
      for (const f of features) {
        remaining.add(f.id)
      }
      for (const [id] of this.shapeComponents) {
        if (!remaining.has(id)) {
          this.removeShapeFromMap(id)
        }
      }
      this.updateContextMode()
      this.requestUpdate()
    })
  }

  private handleLayerFeatureDeleted(event: CustomEvent): void {
    const featureId = event.detail.featureId
    this.removeShapeFromMap(featureId)
    this.updateContextMode()
    this.requestUpdate()
  }

  private handleZoomToFeature(event: CustomEvent): void {
    if (!this.map) return
    const feature = event.detail.feature
    if (!feature?.geometry) return

    if (feature.geometry.type === 'Point') {
      const [lng, lat] = feature.geometry.coordinates
      this.map.flyTo({ center: [lng, lat], zoom: 15, duration: 1000 })
    } else if (feature.bounds) {
      const { sw, ne } = feature.bounds
      this.map.fitBounds(
        [
          [sw[1], sw[0]],
          [ne[1], ne[0]],
        ],
        { padding: 60, duration: 1000 }
      )
    } else {
      const bbox = this.computeGeometryBounds(feature.geometry)
      if (bbox) {
        this.map.fitBounds(bbox, { padding: 60, duration: 1000 })
      }
    }
  }

  private computeGeometryBounds(
    geometry: GeoJSON.Geometry
  ): [[number, number], [number, number]] | null {
    let minLng = Infinity,
      maxLng = -Infinity,
      minLat = Infinity,
      maxLat = -Infinity
    const process = (coords: unknown): void => {
      if (Array.isArray(coords) && typeof coords[0] === 'number') {
        const [lng, lat] = coords as number[]
        minLng = Math.min(minLng, lng)
        maxLng = Math.max(maxLng, lng)
        minLat = Math.min(minLat, lat)
        maxLat = Math.max(maxLat, lat)
      } else if (Array.isArray(coords)) {
        for (const c of coords) process(c)
      }
    }
    if ('coordinates' in geometry) process(geometry.coordinates)
    if (minLng === Infinity) return null
    return [
      [minLng, minLat],
      [maxLng, maxLat],
    ]
  }

  private async handleFileImported(event: CustomEvent<ImportResult>): Promise<void> {
    const result = event.detail

    const importResult = await processGpxImport(result, {
      featureStorage: featureStorage as GpxImportDeps['featureStorage'],
      planStorage: planStorage as GpxImportDeps['planStorage'],
      activityStorage: activityStorage as GpxImportDeps['activityStorage'],
      layerGroupManager,
      elevationService: elevationPointService,
      userId: authService.getState().userId,
    })

    // Render all imported features (tracks, routes, waypoints) on map
    for (const fid of importResult.featureIds) {
      const feature = await db.features.get(fid)
      if (feature) {
        this.renderFeatureOnMap(feature)
      }
    }

    // Refresh the layer panel so the new import group + its features appear without a manual refresh
    const layerSwitcher = this.shadowRoot?.querySelector('layer-switcher') as LayerSwitcher | null
    await layerSwitcher?.loadLayerTree()

    // Zoom to imported data
    if (importResult.bounds && this.map) {
      const { sw, ne } = importResult.bounds
      this.map.fitBounds(
        [
          [sw[1], sw[0]],
          [ne[1], ne[0]],
        ],
        { padding: 60, duration: 1000 }
      )
    }

    this.requestUpdate()
  }

  // ─── Drag-and-drop file import ──────────────────────────────────────

  private handleDragEnter = (e: DragEvent): void => {
    e.preventDefault()
    this.dragCounter++
    if (e.dataTransfer?.types.includes('Files')) {
      this.showDropOverlay = true
    }
  }

  private handleDragOver = (e: DragEvent): void => {
    e.preventDefault()
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy'
    }
  }

  private handleDragLeave = (_e: DragEvent): void => {
    this.dragCounter--
    if (this.dragCounter <= 0) {
      this.dragCounter = 0
      this.showDropOverlay = false
    }
  }

  private handleDrop = (e: DragEvent): void => {
    e.preventDefault()
    this.dragCounter = 0
    this.showDropOverlay = false

    const files = e.dataTransfer?.files
    if (!files?.length) return

    const file = Array.from(files).find(f => isImportableFile(f))
    if (!file) return

    void this.importDroppedFile(file)
  }

  private async importDroppedFile(file: File): Promise<void> {
    console.log('[import] importDroppedFile called:', file.name, file.size)
    await this.updateComplete
    const dialog = this.shadowRoot?.querySelector('#drop-import-dialog') as ImportDialog | undefined
    console.log('[import] dialog found:', !!dialog)
    if (!dialog) return

    const result = await dialog.show(file, this.planId ? { planId: this.planId } : undefined)
    if (!result) return

    const importResult = await processGpxImport(result, {
      featureStorage: featureStorage as GpxImportDeps['featureStorage'],
      planStorage: planStorage as GpxImportDeps['planStorage'],
      activityStorage: activityStorage as GpxImportDeps['activityStorage'],
      layerGroupManager,
      elevationService: elevationPointService,
      userId: authService.getState().userId,
    })

    for (const fid of importResult.featureIds) {
      const feature = await db.features.get(fid)
      if (feature) this.renderFeatureOnMap(feature)
    }

    // Refresh the layer panel so the new import group + its features appear without a manual refresh
    const layerSwitcher = this.shadowRoot?.querySelector('layer-switcher') as LayerSwitcher | null
    await layerSwitcher?.loadLayerTree()

    if (importResult.bounds && this.map) {
      const { sw, ne } = importResult.bounds
      this.map.fitBounds(
        [
          [sw[1], sw[0]],
          [ne[1], ne[0]],
        ],
        { padding: 60, duration: 1000 }
      )
    }

    this.requestUpdate()
  }

  private handleShapeDeleted(event: CustomEvent): void {
    const featureId = event.detail.featureId
    log.debug('🗑️ Map cleaning up after shape deletion:', featureId)
    log.debug(
      '🗑️ selectedFeatures before deletion:',
      this.selectedFeatures.length,
      this.selectedFeatures.map(f => f.id)
    )

    // Clean up our reference to the deleted component
    this.shapeComponents.delete(featureId)

    // Remove from trip feature manager selection
    tripFeatureManager.removeFromSelection(featureId)
    log.debug(
      '🗑️ trip feature manager selection after removal:',
      tripFeatureManager.getSelectedFeatures()
    )

    // Update our local selected features to reflect the deletion
    const beforeCount = this.selectedFeatures.length
    this.selectedFeatures = this.selectedFeatures.filter(f => f.id !== featureId)
    log.debug(
      '🗑️ selectedFeatures after deletion:',
      this.selectedFeatures.length,
      this.selectedFeatures.map(f => f.id)
    )
    log.debug('🗑️ Array changed:', beforeCount, '->', this.selectedFeatures.length)

    // Update context mode (will automatically switch to drawing if no features selected)
    const beforeMode = this.contextMode
    this.updateContextMode()
    log.debug('🗑️ Context mode after update:', beforeMode, '->', this.contextMode)

    log.debug(
      '🗑️ Map cleanup after deletion complete, remaining selected:',
      this.selectedFeatures.length
    )

    // Force a re-render to make sure the context bar updates
    this.requestUpdate()
    log.debug('🗑️ Requested update to force re-render')
  }

  private setupKeyboardHandlers(): void {
    log.debug('⌨️ Setting up keyboard shortcuts')
    // Bind the handler to preserve 'this' context
    this.boundKeyHandler = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.boundKeyHandler)
  }

  private cleanupKeyboardHandlers(): void {
    if (this.boundKeyHandler) {
      document.removeEventListener('keydown', this.boundKeyHandler)
      log.debug('⌨️ Keyboard shortcuts cleaned up')
    }
  }

  private handleKeydown(event: KeyboardEvent): void {
    // Ignore if user is typing in an input field
    const target = event.target as HTMLElement
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      return
    }
    const path = event.composedPath()
    if (
      path.some(
        el =>
          el instanceof HTMLElement &&
          (el.tagName === 'INPUT' ||
            el.tagName === 'TEXTAREA' ||
            el.tagName === 'MD-OUTLINED-TEXT-FIELD' ||
            el.tagName === 'MD-FILLED-TEXT-FIELD' ||
            el.tagName === 'LABEL-EDITOR-DROPDOWN' ||
            el.tagName === 'LAYER-PICKER-DROPDOWN')
      )
    ) {
      return
    }

    if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
      event.preventDefault()
      // While drawing a line/polygon, undo the most recent anchor point instead of
      // popping the feature-level undo stack.
      if (
        this.linePolygonDrawingManager.isCurrentlyDrawing &&
        this.linePolygonDrawingManager.drawingPointCount > 0
      ) {
        this.handleCreateUndo()
      } else if (undoStack.getPendingEdit()) {
        this.handleEditCancelled()
      } else {
        void this.performUndo()
      }
      return
    }
    if (
      (event.ctrlKey || event.metaKey) &&
      ((event.key === 'z' && event.shiftKey) || event.key === 'y')
    ) {
      event.preventDefault()
      void this.performRedo()
      return
    }

    switch (event.key) {
      case 'Delete':
      case 'Backspace':
        event.preventDefault()
        this.handleDeleteKeyPressed()
        break

      case 'Escape':
        event.preventDefault()
        this.handleEscapeKeyPressed()
        break
    }
  }

  private handleDeleteKeyPressed(): void {
    // If a vertex is selected on an editing shape, delete the vertex instead of the feature
    for (const shape of this.shapeComponents.values()) {
      if ('deleteSelectedVertices' in shape && (shape as ShapeLine).hasSelectedVertex()) {
        void (shape as ShapeLine).deleteSelectedVertices()
        return
      }
    }

    if (this.selectedFeatures.length > 0) {
      void this.handleDeleteSelected()
    }
  }

  private handleEscapeKeyPressed(): void {
    const mode = tripFeatureManager.getMapMode()
    const subContext = tripFeatureManager.getDrawSubContext()

    if (this.measureActive) {
      this.cleanupMeasure()
      return
    }

    if (this.hasActiveSelection()) {
      if (this.joinPreviewState) {
        this.handleJoinCancelled()
        return
      }
      this.clearAllSelection()
      return
    }

    if (mode === 'draw') {
      if (subContext === 'extending') {
        const extendingId = tripFeatureManager.getExtendingFeatureId()
        if (extendingId) {
          const shape = this.shapeComponents.get(extendingId)
          if (shape && 'exitExtendMode' in shape) {
            ;(shape as unknown as ShapeLine).exitExtendMode()
          }
        }
        this.updateContextMode()
      } else if (subContext === 'focused') {
        this.exitAllEditModes()
        this.hideAllEditHandles()
        tripFeatureManager.clearSelection()
        this.selectedFeatures = []
        this.restoreEditReturnMode()
        this.updateContextMode()
      } else if (this.selectedDrawingTool) {
        tripFeatureManager.clearDrawingTool()
        this.updateContextMode()
      } else {
        tripFeatureManager.setMapMode('map')
        this.updateContextMode()
      }
      return
    }
  }

  /** Extend a selected line: open the extend toolbar immediately (returns/snap/undo-redo)
   *  with the line's endpoint handles shown, BEFORE choosing an end. Returns act on the
   *  line right away; placing points waits until an endpoint is picked. */
  private async handleFeatureExtend(event: CustomEvent): Promise<void> {
    const featureId = event.detail.feature?.id
    if (!featureId) return
    const feature = await tripFeatureManager.getFeature(featureId)
    if (feature?.geometry.type !== 'LineString') return

    // Seed the (awaiting-end) session so returns can act on the line.
    this.linePolygonDrawingManager.beginExtend(feature)
    // Show the line's two endpoint handles so an end can be picked for point-adding.
    const shape = this.shapeComponents.get(feature.id)
    if (shape && 'showExtendEndpoints' in shape) {
      ;(shape as unknown as ShapeLine).showExtendEndpoints()
    }
    // Flip to the create/extend toolbar with this line as the "creating" feature.
    this.creatingFeature = feature
    tripFeatureManager.selectDrawingTool('line')
    this.updateContextMode()
    this.requestUpdate()
  }

  /** Endpoint picked → activate the session from that end so placing points works. */
  private async handleExtendBegin(e: CustomEvent): Promise<void> {
    const { featureId, direction } = e.detail as {
      featureId: string
      direction: 'append' | 'prepend'
    }
    if (!featureId || !direction) return
    const feature = await tripFeatureManager.getFeature(featureId)
    if (feature?.geometry.type !== 'LineString') return

    this.linePolygonDrawingManager.chooseExtendEnd(direction)
    this.hideExtendingShape()

    // Auto-detect the snap default on the working end (like a fresh line's first point).
    const coords = feature.geometry.coordinates as [number, number][]
    const endCoord = direction === 'prepend' ? coords[0] : coords[coords.length - 1]
    if (endCoord && this.map) {
      const ref = this.queryOverlayFeatureAtPoint(
        this.map.project({ lng: endCoord[0], lat: endCoord[1] })
      )
      const onTrail = ref?.source === 'backcountry-trail'
      this.linePolygonDrawingManager.setSnapDefault(onTrail, onTrail)
    }
    this.updateContextMode()
    this.requestUpdate()
  }

  /** Hide the extending feature's shape + handles once the session activates (an end pick
   *  or a return), so the temp drawing line becomes the single visual. */
  private hideExtendingShape(): void {
    const id = this.linePolygonDrawingManager.extendingFeature
    if (!id) return
    const shape = this.shapeComponents.get(id)
    if (shape) {
      shape.exitEditMode()
      shape.setVisible(false)
    }
  }

  /** Awaiting-end tap: pick the nearer of the line's two ends and activate the session
   *  from it (this does not place a point — the next tap does). */
  private pickExtendEndFromTap(g: GesturePoint): void {
    const ends = this.linePolygonDrawingManager.extendEnds
    if (!ends || !this.map) return
    const pStart = this.map.project(ends[0])
    const pEnd = this.map.project(ends[1])
    const dStart = Math.hypot(g.point.x - pStart.x, g.point.y - pStart.y)
    const dEnd = Math.hypot(g.point.x - pEnd.x, g.point.y - pEnd.y)
    const direction: 'append' | 'prepend' = dStart <= dEnd ? 'prepend' : 'append'
    const endLngLat = direction === 'prepend' ? ends[0] : ends[1]
    this.linePolygonDrawingManager.chooseExtendEnd(direction)
    this.hideExtendingShape()
    const ref = this.queryOverlayFeatureAtPoint(this.map.project(endLngLat))
    const onTrail = ref?.source === 'backcountry-trail'
    this.linePolygonDrawingManager.setSnapDefault(onTrail, onTrail)
    this.updateContextMode()
    this.requestUpdate()
  }

  private handleFeatureReturn(event: CustomEvent): void {
    const feature = event.detail.feature
    if (!feature) {
      return
    }
    const shapeComponent = this.shapeComponents.get(feature.id)
    if (shapeComponent && 'appendReturn' in shapeComponent) {
      void (shapeComponent as unknown as ShapeLine).appendReturn()
    }
  }

  private handleVertexSelectionRect(bounds: {
    west: number
    east: number
    south: number
    north: number
  }): void {
    if (this.selectedFeatures.length !== 1) return
    const shape = this.shapeComponents.get(this.selectedFeatures[0].id)
    if (shape instanceof ShapeLine) {
      shape.selectVerticesInBounds(bounds)
    }
  }

  private handleJoinPreview(): void {
    if (this.selectedFeatures.length !== 2 || !this.map) return
    const [featureA, featureB] = this.selectedFeatures
    const lineTypes = ['line', 'track', 'route']
    if (!lineTypes.includes(featureA.type) || !lineTypes.includes(featureB.type)) return

    const reverseA = false
    const reverseB = false
    const sourceId = 'join-preview-source'
    const layerId = 'join-preview-layer'

    const coordsA = (featureA.geometry as GeoJSON.LineString).coordinates
    const coordsB = (featureB.geometry as GeoJSON.LineString).coordinates
    const { endA, startB } = getJoinEndpoints(coordsA, coordsB, reverseA, reverseB)

    const previewGeoJSON: GeoJSON.Feature = {
      type: 'Feature',
      properties: {},
      geometry: { type: 'LineString', coordinates: [endA, startB] },
    }

    this.map.addSource(sourceId, { type: 'geojson', data: previewGeoJSON })
    this.map.addLayer({
      id: layerId,
      type: 'line',
      source: sourceId,
      paint: {
        'line-color': '#4fc3f7',
        'line-width': 3,
        'line-dasharray': [5, 5],
      },
    })

    const endpointMarkers = this.createJoinEndpointMarkers(coordsA, coordsB, reverseA, reverseB)

    this.joinPreviewState = { reverseA, reverseB, sourceId, layerId, endpointMarkers }
    this.requestUpdate()
  }

  private createJoinEndpointMarkers(
    coordsA: number[][],
    coordsB: number[][],
    reverseA: boolean,
    reverseB: boolean
  ): maplibregl.Marker[] {
    if (!this.map) return []

    const { endA, startB } = getJoinEndpoints(coordsA, coordsB, reverseA, reverseB)
    const endpoints = [
      { coord: coordsA[0], line: 'A' as const, end: 'start' as const },
      { coord: coordsA[coordsA.length - 1], line: 'A' as const, end: 'end' as const },
      { coord: coordsB[0], line: 'B' as const, end: 'start' as const },
      { coord: coordsB[coordsB.length - 1], line: 'B' as const, end: 'end' as const },
    ]

    const isActive = (coord: number[], line: string): boolean => {
      if (line === 'A') return coord === endA || (coord[0] === endA[0] && coord[1] === endA[1])
      return coord === startB || (coord[0] === startB[0] && coord[1] === startB[1])
    }

    return endpoints.map(({ coord, line, end }) => {
      const active = isActive(coord, line)
      const isTouchDevice = 'ontouchstart' in window
      const size = isTouchDevice ? '28px' : '14px'
      const el = document.createElement('div')
      el.style.width = size
      el.style.height = size
      el.style.borderRadius = '50%'
      el.style.border = '2px solid white'
      el.style.cursor = 'pointer'
      el.style.backgroundColor = active ? '#4fc3f7' : 'rgba(255,255,255,0.4)'
      el.style.boxShadow = '0 1px 4px rgba(0,0,0,0.4)'

      const handleTap = (e: Event): void => {
        e.stopPropagation()
        e.preventDefault()
        this.handleJoinEndpointClick(line, end)
      }
      el.addEventListener('click', handleTap)
      el.addEventListener(
        'touchstart',
        (e: Event) => {
          e.stopPropagation()
        },
        { passive: false }
      )
      el.addEventListener('touchend', handleTap, { passive: false })

      const marker = new maplibregl.Marker({ element: el })
        .setLngLat([coord[0], coord[1]])
        .addTo(this.map!)

      return marker
    })
  }

  private handleJoinEndpointClick(line: 'A' | 'B', end: 'start' | 'end'): void {
    if (!this.joinPreviewState || this.selectedFeatures.length !== 2) return

    if (line === 'A') {
      this.joinPreviewState.reverseA = end === 'start'
    } else {
      this.joinPreviewState.reverseB = end === 'end'
    }

    this.updateJoinPreview()
  }

  private updateJoinPreview(): void {
    if (!this.joinPreviewState || !this.map || this.selectedFeatures.length !== 2) return

    const { reverseA, reverseB, sourceId } = this.joinPreviewState
    const coordsA = (this.selectedFeatures[0].geometry as GeoJSON.LineString).coordinates
    const coordsB = (this.selectedFeatures[1].geometry as GeoJSON.LineString).coordinates
    const { endA, startB } = getJoinEndpoints(coordsA, coordsB, reverseA, reverseB)

    const source = this.map.getSource(sourceId)
    if (source && 'setData' in source) {
      ;(source as { setData: (data: unknown) => void }).setData({
        type: 'Feature',
        properties: {},
        geometry: { type: 'LineString', coordinates: [endA, startB] },
      })
    }

    for (const m of this.joinPreviewState.endpointMarkers) {
      m.remove()
    }
    this.joinPreviewState.endpointMarkers = this.createJoinEndpointMarkers(
      coordsA,
      coordsB,
      reverseA,
      reverseB
    )
  }

  private cleanupJoinPreview(): void {
    if (!this.joinPreviewState || !this.map) return
    for (const m of this.joinPreviewState.endpointMarkers) {
      m.remove()
    }
    if (this.map.getLayer(this.joinPreviewState.layerId)) {
      this.map.removeLayer(this.joinPreviewState.layerId)
    }
    if (this.map.getSource(this.joinPreviewState.sourceId)) {
      this.map.removeSource(this.joinPreviewState.sourceId)
    }
    this.joinPreviewState = null
  }

  private handleJoinCancelled(): void {
    this.cleanupJoinPreview()
    this.requestUpdate()
  }

  private async handleJoinConfirmed(): Promise<void> {
    if (!this.joinPreviewState || this.selectedFeatures.length !== 2) return

    const { reverseA, reverseB } = this.joinPreviewState
    const [featureA, featureB] = this.selectedFeatures
    const cloneA = structuredClone(featureA)
    const cloneB = structuredClone(featureB)

    const coordsA = (featureA.geometry as GeoJSON.LineString).coordinates
    const coordsB = (featureB.geometry as GeoJSON.LineString).coordinates
    const { merged, skippedDuplicate } = mergeLineCoordinates(coordsA, coordsB, reverseA, reverseB)

    const mergedProps = joinMetadataArrays(
      { ...(featureA.properties ?? {}), _coordCount: coordsA.length },
      { ...(featureB.properties ?? {}), _coordCount: coordsB.length },
      reverseA,
      reverseB,
      skippedDuplicate
    )
    delete mergedProps._coordCount

    const { edgeService: es, nodeRef: nr } = await import('../../services/edge-service.js')
    const deletedEdges = await es.getByObject(nr('feature', featureB.id), 'has-feature')
    const edgeSnaps: EdgeSnapshot[] = deletedEdges.map(e => ({
      subject: e.subject,
      predicate: e.predicate,
      object: e.object,
      action: 'deleted' as const,
    }))

    const newGeometry: GeoJSON.LineString = { type: 'LineString', coordinates: merged }
    await db.features.update(featureA.id, {
      geometry: newGeometry,
      properties: mergedProps,
      updatedAt: new Date().toISOString(),
    })

    const shapeA = this.shapeComponents.get(featureA.id)
    if (shapeA) {
      shapeA.feature.geometry = newGeometry
      shapeA.feature.properties = mergedProps
      shapeA.updateSource()
    }

    await featureStorage.deleteFeature(featureB.id)
    this.removeShapeFromMap(featureB.id)

    const mergedA = await db.features.get(featureA.id)
    undoStack.push({
      type: 'join',
      label: 'Lines joined',
      features: [
        { id: featureA.id, before: cloneA, after: mergedA ? structuredClone(mergedA) : null },
        { id: featureB.id, before: cloneB, after: null },
      ],
      edges: edgeSnaps,
    })

    this.cleanupJoinPreview()
    tripFeatureManager.clearSelection()
    tripFeatureManager.selectFeature(featureA.id)
    await this.updateSelectedFeatures()
    this.updateContextMode()
    this.requestUpdate()

    showToast('info', 'Lines joined', {
      tag: 'undo',
      action: { label: 'Undo', callback: () => void this.performUndo() },
    })
  }

  /** Split button → enter split mode; the next tap on the line picks the vertex. */
  private handleFeatureSplit(event: CustomEvent): void {
    const feature = event.detail.feature as Feature | undefined
    if (feature?.geometry.type !== 'LineString') return
    if (feature.geometry.coordinates.length < 3) return
    this.splitModeFeatureId = feature.id
    // Show the vertices but with dragging off, so a click on a vertex is a tap that
    // picks the split point (a draggable handle would eat the click as a drag).
    const shape = this.shapeComponents.get(feature.id)
    if (shape instanceof ShapeLine) shape.enterSplitMode()
    showToast('info', 'Tap a vertex on the line to split it there', { tag: 'split' })
    this.requestUpdate()
  }

  /** A vertex handle was clicked in split mode → split there. */
  private handleSplitAtVertex(e: CustomEvent): void {
    const { featureId, vertexIndex } = e.detail as { featureId: string; vertexIndex: number }
    this.splitModeFeatureId = null
    const shape = this.shapeComponents.get(featureId)
    if (shape instanceof ShapeLine) shape.exitSplitMode()
    const feature = this.selectedFeatures.find(f => f.id === featureId)
    if (feature) void this.executeSplitAtVertex(feature, vertexIndex)
  }

  /** Split-mode tap (on the canvas, near a vertex): split at the nearest interior vertex,
   *  or cancel if tapped away. (Clicking a handle directly is handled separately.) */
  private pickSplitVertex(g: GesturePoint): void {
    const id = this.splitModeFeatureId
    this.splitModeFeatureId = null
    const splitShape = id ? this.shapeComponents.get(id) : undefined
    if (splitShape instanceof ShapeLine) splitShape.exitSplitMode()
    this.requestUpdate()
    if (!id || !this.map) return
    const feature = this.selectedFeatures.find(f => f.id === id)
    if (feature?.geometry.type !== 'LineString') return
    const coords = feature.geometry.coordinates as [number, number][]
    let bestIdx = -1
    let bestDist = Infinity
    for (let i = 1; i < coords.length - 1; i++) {
      const p = this.map.project({ lng: coords[i][0], lat: coords[i][1] })
      const d = Math.hypot(g.point.x - p.x, g.point.y - p.y)
      if (d < bestDist) {
        bestDist = d
        bestIdx = i
      }
    }
    if (bestIdx < 0 || bestDist > 30) {
      this.requestUpdate() // tapped away from any interior vertex → cancel split mode
      return
    }
    void this.executeSplitAtVertex(feature, bestIdx)
  }

  /** Split `feature` at `vertexIndex` into two lines; the original keeps its name and
   *  first half, the second half becomes "{name} (2)"; land on the original in edit. */
  private async executeSplitAtVertex(feature: Feature, vertexIndex: number): Promise<void> {
    const shape = this.shapeComponents.get(feature.id)
    if (!(shape instanceof ShapeLine)) return

    const coords = (feature.geometry as GeoJSON.LineString).coordinates
    const segments = computeSplitSegments(coords.length, new Set([vertexIndex]))
    if (!segments) return

    const originalClone = structuredClone(feature)
    const segCoords = segments.map(([s, e]) => coords.slice(s, e + 1))
    const segProps = sliceMetadataForSegments(feature.properties ?? {}, segments)
    const baseName = feature.name || 'Line'

    // Original keeps its name + becomes the first segment.
    const firstGeometry: GeoJSON.LineString = { type: 'LineString', coordinates: segCoords[0] }
    await db.features.update(feature.id, {
      geometry: firstGeometry,
      properties: segProps[0],
      updatedAt: new Date().toISOString(),
    })
    shape.feature.geometry = firstGeometry
    shape.feature.properties = segProps[0]

    const { edgeService: es, nodeRef: nr } = await import('../../services/edge-service.js')
    const originalGroupIds = await es.getSubjectIds(nr('feature', feature.id), 'has-feature')

    const updatedFirst = await db.features.get(feature.id)
    const undoFeatures: UndoOperation['features'] = [
      {
        id: feature.id,
        before: originalClone,
        after: updatedFirst ? structuredClone(updatedFirst) : null,
      },
    ]
    const undoEdges: EdgeSnapshot[] = []

    for (let i = 1; i < segCoords.length; i++) {
      const result = await featureStorage.createFeature(
        {
          name: `${baseName} (${i + 1})`,
          description: feature.description,
          type: feature.type,
          geometry: { type: 'LineString', coordinates: segCoords[i] },
          properties: segProps[i],
          tags: [...(feature.tags ?? [])],
          createdBy: feature.createdBy,
        },
        { entityType: 'feature', entityId: feature.id }
      )
      if (result.success && result.data) {
        if (originalGroupIds.length > 0) {
          await es.create({
            subject: nr('group', originalGroupIds[0]),
            predicate: 'has-feature',
            object: nr('feature', result.data.id),
          })
          undoEdges.push({
            subject: `group:${originalGroupIds[0]}`,
            predicate: 'has-feature',
            object: `feature:${result.data.id}`,
            action: 'created',
          })
        }
        undoFeatures.push({ id: result.data.id, before: null, after: structuredClone(result.data) })
        this.renderFeatureOnMap(result.data)
      }
    }

    shape.updateSource()
    if (shape.isEditing) {
      shape.exitEditMode()
    }
    undoStack.clearPendingEdit()

    undoStack.push({
      type: 'split',
      label: `Split into ${segCoords.length} lines`,
      features: undoFeatures,
      edges: undoEdges,
    })

    tripFeatureManager.clearSelection()
    tripFeatureManager.selectFeature(feature.id)
    await this.updateSelectedFeatures()
    const origShape = this.shapeComponents.get(feature.id)
    if (origShape instanceof ShapeLine) origShape.enterEditMode()
    this.updateContextMode()
    this.requestUpdate()

    showToast('info', `Split into ${segCoords.length} lines`, {
      tag: 'undo',
      action: { label: 'Undo', callback: () => void this.performUndo() },
    })
  }

  private queryOverlayFeatureAtPoint(
    point: maplibregl.Point
  ): import('../../services/selection/selectable-feature-ref.js').SelectableFeatureRef | null {
    if (!this.map) return null
    const layers = getSelectableOverlayLayerIds()
    if (layers.length === 0) return null
    // Query a small tolerance box (not a single pixel) so thin overlay lines
    // (OSM/COTREX trails) register reliably — matching identify/hover slop.
    const R = 6
    const box: [maplibregl.PointLike, maplibregl.PointLike] = [
      [point.x - R, point.y - R],
      [point.x + R, point.y + R],
    ]
    const hits = this.map.queryRenderedFeatures(box, { layers })
    for (const hit of hits) {
      const ref = mapGeoJSONFeatureToRef(hit)
      if (ref) {
        // A merged OSM trail feature bundles disconnected segments — keep only
        // the constituent line nearest the click, so you select just the piece
        // you clicked (named or not; click again for a separate piece).
        if (ref.source === 'backcountry-trail') {
          ref.geometry = backcountryOverlayService.nearestTrailSegment(
            ref.geometry,
            this.map.unproject(point)
          )
        }
        return ref
      }
    }
    return null
  }

  private handleSelectionCleared(): void {
    void this.commitPendingEdit()
    this.cleanupJoinPreview()
    this.styleEditorManager.clearSelectionDropdowns()
    tripFeatureManager.clearSelection()
    this.selectedFeatures = []
    this.hideAllEditHandles()
    this.exitAllEditModes()
    if (this.map) this.map.getCanvas().style.cursor = ''
    this.updateContextMode()
    this.requestUpdate()
  }

  // Style editor event delegators
  private handleOpenStyleEditor(e: CustomEvent): void {
    void this.ensureEditorsLoaded()
    this.styleEditorManager.handleOpenStyleEditor(e)
  }
  private handleColorChanged(e: CustomEvent): void {
    void this.styleEditorManager.handleColorChanged(e)
  }
  private handleFillPatternChanged(e: CustomEvent): void {
    void this.styleEditorManager.handleFillPatternChanged(e)
  }
  private handleColorPickerClose(): void {
    this.styleEditorManager.closeColorPicker()
  }
  private handleStrokeStyleChanged(e: CustomEvent): void {
    void this.styleEditorManager.handleStrokeStyleChanged(e)
  }
  private handleStrokeStyleClose(): void {
    this.styleEditorManager.closeStrokeStyle()
  }
  private handleStrokeEditorColorChanged(e: CustomEvent): void {
    void this.styleEditorManager.handleStrokeEditorColorChanged(e)
  }
  private handleStrokeEditorWidthChanged(e: CustomEvent): void {
    void this.styleEditorManager.handleStrokeEditorWidthChanged(e)
  }
  private handleStrokeEditorPatternChanged(e: CustomEvent): void {
    void this.styleEditorManager.handleStrokeEditorPatternChanged(e)
  }
  private handleMarkerChanged(e: CustomEvent): void {
    void this.styleEditorManager.handleMarkerChanged(e)
  }
  private handleLabelChanged(e: CustomEvent): void {
    void this.styleEditorManager.handleLabelChanged(e)
  }
  private handleTagsChanged(e: CustomEvent): void {
    void this.styleEditorManager.handleTagsChanged(e)
  }
  private handleLayerSelected(e: CustomEvent): void {
    void this.styleEditorManager.handleLayerSelected(e)
  }

  private async handleLayerVisibilityChanged(): Promise<void> {
    const allFeatures = await tripFeatureManager.getAllFeatures()
    for (const feature of allFeatures) {
      const shape = this.shapeComponents.get(feature.id)
      if (shape) {
        shape.setVisible(feature.isVisible)
      } else if (feature.isVisible) {
        // Feature was created (e.g. by materializeTracksForAllTrips) but not yet rendered
        this.renderFeatureOnMap(feature)
      }
    }
  }

  private async handleDeleteSelected(): Promise<void> {
    const selectedIds = [...tripFeatureManager.getSelectedFeatures()]

    const parentActivityIds = new Set<string>()
    for (const fid of selectedIds) {
      const actId = await featureStorage.getParentActivity(fid)
      if (actId) parentActivityIds.add(actId)
    }

    const dialog = this.shadowRoot?.querySelector('confirm-dialog') as ConfirmDialog | null
    if (dialog) {
      const count = selectedIds.length
      dialog.headline = 'Delete Feature'
      if (parentActivityIds.size > 0) {
        const actWord = parentActivityIds.size === 1 ? 'activity' : 'activities'
        dialog.message = `Delete ${count} feature${count > 1 ? 's' : ''}? This will also delete ${parentActivityIds.size} associated ${actWord}.`
      } else {
        dialog.message = `Delete selected features (${count})?`
      }
      const confirmed = await dialog.show()
      if (!confirmed) {
        return
      }
    }
    this.styleEditorManager.closeAllAndStopListening()

    const { edgeService: es, nodeRef: nr } = await import('../../services/edge-service.js')
    const snapshots: UndoOperation['features'] = []
    const edgeSnaps: EdgeSnapshot[] = []
    for (const fid of selectedIds) {
      const clone = await db.features.get(fid)
      if (clone) {
        snapshots.push({ id: fid, before: clone, after: null })
        const edges = await es.getByObject(nr('feature', fid), 'has-feature')
        for (const e of edges) {
          edgeSnaps.push({
            subject: e.subject,
            predicate: e.predicate,
            object: e.object,
            action: 'deleted',
          })
        }
      }
    }

    tripFeatureManager.clearSelection()
    for (const featureId of selectedIds) {
      const shapeComponent = this.shapeComponents.get(featureId)
      if (shapeComponent) {
        await shapeComponent.deleteFeature()
      }
    }
    for (const actId of parentActivityIds) {
      await activityStorage.deleteActivity(actId)
    }

    if (snapshots.length > 0) {
      undoStack.push({
        type: 'delete-multiple',
        label: `Deleted ${snapshots.length} features`,
        features: snapshots,
        edges: edgeSnaps,
      })
      showToast('info', `Deleted ${snapshots.length} feature${snapshots.length > 1 ? 's' : ''}`, {
        tag: 'undo',
        action: { label: 'Undo', callback: () => void this.performUndo() },
      })
    }

    this.shapeComponents.forEach(c => c.setSelected(false))
    this.selectedFeatures = []
    this.hideAllEditHandles()
    this.exitAllEditModes()
    this.updateContextMode()
    this.requestUpdate()
  }

  private async handleAddToPlan(e: CustomEvent<{ pickPlan?: boolean }>): Promise<void> {
    const pickPlan = e.detail.pickPlan ?? false
    let targetPlanId = this.planId

    if (pickPlan || !targetPlanId) {
      const picker = document.createElement('plan-picker-dialog')
      this.shadowRoot?.appendChild(picker)
      targetPlanId = (await picker.show()) ?? undefined
      picker.remove()
      if (!targetPlanId) return
    }

    await this.addOverlayFeaturesToPlan(targetPlanId)
  }

  private async handleCreatePlanFromSelection(): Promise<void> {
    const refs = [...selectionService.getSelected().values()]
    const overlayRefs = refs.filter(r => r.source !== 'user')
    if (overlayRefs.length === 0) return

    const namedRef = overlayRefs.find(r => 'name' in r) as { name: string } | undefined
    const firstName = namedRef?.name ?? 'Untitled Plan'
    const userId = authService.getState().userId
    if (!userId) return

    const result = await planStorage.createPlan({
      name: firstName,
      origin: { type: 'imported', source: 'cotrex' },
      createdBy: userId,
    })
    if (!result.success || !result.data) return

    await this.addOverlayFeaturesToPlan(result.data.id)
    router.navigate(`/plans/${result.data.id}/map`)
  }

  private async addOverlayFeaturesToPlan(planId: string): Promise<void> {
    const refs = [...selectionService.getSelected().values()]
    const overlayRefs = refs.filter(r => r.source !== 'user')
    if (overlayRefs.length === 0) return

    const userId = authService.getState().userId
    if (!userId) return

    for (const overlayRef of overlayRefs) {
      if (overlayRef.source === 'cotrex-trail') {
        const extracted = await cotrexGeometryExtractor.extractTrailGeometry(overlayRef.name)
        if (!extracted) continue
        await featureStorage.createFeature(
          {
            name: overlayRef.name,
            type: 'line',
            geometry: extracted.geometry,
            properties: { ...extracted.properties, source: 'cotrex' },
            tags: ['cotrex'],
            createdBy: userId,
          },
          { entityType: 'plan', entityId: planId }
        )
      } else if (overlayRef.source === 'cotrex-trailhead') {
        const extracted = await cotrexGeometryExtractor.extractTrailheadGeometry(overlayRef.name)
        if (!extracted) continue
        await featureStorage.createFeature(
          {
            name: overlayRef.name,
            type: 'point',
            geometry: extracted.geometry,
            properties: { ...extracted.properties, source: 'cotrex' },
            tags: ['cotrex'],
            createdBy: userId,
          },
          { entityType: 'plan', entityId: planId }
        )
      }
    }

    selectionService.clearAll()
    this.selectedFeatures = []

    if (this.planId === planId) {
      const features = await featureStorage.getFeaturesForEntity('plan', planId)
      this.clearAllFeatures()
      features.forEach(f => this.renderFeatureOnMap(f))
    }

    this.updateContextMode()
    this.requestUpdate()
  }

  // Edit handle delegators
  private async showEditHandlesForSelectedFeatures(): Promise<void> {
    await this.editHandleManager.showEditHandlesForSelectedFeatures()
  }
  private async showEditHandles(featureId: string): Promise<void> {
    await this.editHandleManager.showEditHandles(featureId)
  }
  private hideAllEditHandles(): void {
    this.editHandleManager.hideAllEditHandles()
  }
  private exitAllEditModes(): void {
    this.shapeComponents.forEach(shapeComponent => {
      shapeComponent.exitEditMode()
    })
  }

  private handleDrawingFeatureCreated(feature: Feature): void {
    this.renderFeatureOnMap(feature)
    undoStack.push({
      type: 'create',
      label: `Created ${feature.name}`,
      features: [{ id: feature.id, before: null, after: structuredClone(feature) }],
      edges: [],
    })
    showToast('info', `Created ${feature.name}`, {
      tag: 'undo',
      action: { label: 'Undo', callback: () => void this.performUndo() },
    })
    // Finishing a shape deselects the tool — you positively re-pick (even the
    // same one) to draw again, so a stray tap can't start another shape.
    tripFeatureManager.clearDrawingTool()
    this.updateContextMode()
  }

  private async commitPendingEdit(): Promise<void> {
    const pending = undoStack.getPendingEdit()
    if (!pending) return

    const current = await db.features.get(pending.featureId)
    undoStack.clearPendingEdit()
    if (!current) return

    const changed =
      JSON.stringify(pending.snapshot.geometry) !== JSON.stringify(current.geometry) ||
      JSON.stringify(pending.snapshot.properties) !== JSON.stringify(current.properties) ||
      pending.snapshot.name !== current.name ||
      JSON.stringify(pending.snapshot.style) !== JSON.stringify(current.style) ||
      JSON.stringify(pending.snapshot.label) !== JSON.stringify(current.label)

    if (changed) {
      undoStack.push({
        type: 'edit',
        label: `Edited ${current.name}`,
        features: [
          { id: pending.featureId, before: pending.snapshot, after: structuredClone(current) },
        ],
        edges: [],
      })
      showToast('info', `Edit saved`, {
        tag: 'undo',
        action: { label: 'Undo', callback: () => void this.performUndo() },
      })
    }
  }

  /** Restore the mode we were in before an edit (entered from the popup). */
  private restoreEditReturnMode(): void {
    if (this.editReturnMode !== null) {
      tripFeatureManager.setMapMode(this.editReturnMode)
      this.editReturnMode = null
    }
  }

  private handleEditSaved(): void {
    void (async () => {
      await this.commitPendingEdit()
      this.cleanupJoinPreview()
      this.styleEditorManager.clearSelectionDropdowns()
      this.hideAllEditHandles()
      this.exitAllEditModes()
      tripFeatureManager.clearSelection()
      this.selectedFeatures = []
      if (this.map) this.map.getCanvas().style.cursor = ''
      this.restoreEditReturnMode()
      this.updateContextMode()
      this.requestUpdate()
    })()
  }

  /** Create-shape toolbar: undo the most recent drawing operation. */
  private handleCreateUndo(): void {
    this.linePolygonDrawingManager.undo()
    this.requestUpdate()
  }

  /** Create-shape toolbar: redo the last undone drawing operation. */
  private handleCreateRedo(): void {
    this.linePolygonDrawingManager.redo()
    this.requestUpdate()
  }

  /** Create-shape toolbar: out-and-back (retrace the drawn path home). */
  private handleCreateOutAndBack(): void {
    this.linePolygonDrawingManager.outAndBack()
    this.hideExtendingShape() // a return from awaiting-end activates the session
    this.requestUpdate()
  }

  /** Create-shape toolbar: return home by the best trail route (fallback out-and-back). */
  private handleCreateReturn(): void {
    void this.linePolygonDrawingManager.returnHome()
    this.hideExtendingShape()
    this.requestUpdate()
  }

  /** Create-shape toolbar: finish the in-progress line/polygon. */
  private async handleCreateFinish(): Promise<void> {
    // Extending an existing line commits back to that feature (an edit, not a create).
    if (this.linePolygonDrawingManager.extendingFeature) {
      const id = await this.linePolygonDrawingManager.finishExtend()
      if (id) {
        const feature = await tripFeatureManager.getFeature(id)
        const shape = this.shapeComponents.get(id)
        if (feature && shape) {
          shape.feature = feature
          shape.updateSource()
          shape.setVisible(true)
        }
        tripFeatureManager.selectFeature(id)
        if (shape) shape.enterEditMode()
        this.creatingFeature = null
        tripFeatureManager.clearDrawingTool()
        this.updateContextMode()
        this.requestUpdate()
      }
      return
    }
    const tool = tripFeatureManager.getSelectedTool()
    if (tool === 'polygon') {
      void this.linePolygonDrawingManager.finishPolygonDrawing()
    } else {
      void this.linePolygonDrawingManager.finishLineDrawing()
    }
  }

  /** Create-shape toolbar: discard the in-progress shape and its draft feature. */
  private handleCreateCancel(): void {
    // Capture before teardown — extend leaves the original feature untouched, so we
    // just restore its (hidden) shape and return to editing it.
    const extendId = this.linePolygonDrawingManager.extendingFeature
    this.linePolygonDrawingManager.cancelDrawing()
    this.creatingFeature = null
    if (extendId) {
      const shape = this.shapeComponents.get(extendId)
      if (shape) shape.setVisible(true)
      tripFeatureManager.selectFeature(extendId)
      if (shape) shape.enterEditMode()
    }
    tripFeatureManager.clearDrawingTool()
    this.updateContextMode()
    this.requestUpdate()
  }

  /** Create-shape toolbar: flip the trail/terrain snap toggles mid-draw. */
  private handleSnapToggled(e: CustomEvent): void {
    const { trail, terrain } = e.detail as { trail: boolean; terrain: boolean }
    this.linePolygonDrawingManager.setSnap(trail, terrain)
    this.requestUpdate()
  }

  private handleEditCancelled(): void {
    void (async () => {
      const pending = undoStack.getPendingEdit()
      if (pending) {
        await db.features.update(pending.featureId, pending.snapshot)
        const shape = this.shapeComponents.get(pending.featureId)
        if (shape) {
          shape.feature = structuredClone(pending.snapshot)
          shape.updateSource()
        }
        undoStack.clearPendingEdit()
      }
      this.hideAllEditHandles()
      this.exitAllEditModes()
      tripFeatureManager.clearSelection()
      this.selectedFeatures = []
      this.restoreEditReturnMode()
      this.updateContextMode()
      this.requestUpdate()
    })()
  }

  private getUndoDeps(): UndoDeps {
    return {
      renderFeatureOnMap: (feature: Feature) => this.renderFeatureOnMap(feature),
      removeShapeFromMap: (featureId: string) => this.removeShapeFromMap(featureId),
      updateFeatureOnMap: (featureId: string, feature: Feature) => {
        const shape = this.shapeComponents.get(featureId)
        if (shape) {
          shape.feature = structuredClone(feature)
          shape.updateSource()
        }
      },
    }
  }

  private async performUndo(): Promise<void> {
    const op = undoStack.undo()
    if (!op) return
    await executeUndo(op, this.getUndoDeps())
    tripFeatureManager.clearSelection()
    this.selectedFeatures = []
    this.hideAllEditHandles()
    this.exitAllEditModes()
    this.updateContextMode()
    this.requestUpdate()
    showToast('info', `Undone: ${op.label}`, {
      tag: 'undo',
      action: { label: 'Redo', callback: () => void this.performRedo() },
    })
  }

  private async performRedo(): Promise<void> {
    const op = undoStack.redo()
    if (!op) return
    await executeRedo(op, this.getUndoDeps())
    tripFeatureManager.clearSelection()
    this.selectedFeatures = []
    this.hideAllEditHandles()
    this.exitAllEditModes()
    this.updateContextMode()
    this.requestUpdate()
    showToast('info', `Redone: ${op.label}`, {
      tag: 'undo',
      action: { label: 'Undo', callback: () => void this.performUndo() },
    })
  }

  private async refreshFeatureOnMap(featureId: string): Promise<void> {
    // For the new shape component architecture, we just need to update the feature
    // data and the component will handle the refresh automatically
    const shapeComponent = this.shapeComponents.get(featureId)
    if (shapeComponent) {
      const feature = await tripFeatureManager.getFeature(featureId)
      if (feature) {
        shapeComponent.feature = feature
        shapeComponent.updateSource()
      }
    }

    // Legacy code for old system - remove old marker if it exists
    const oldMarker = this.featureMarkers.get(featureId)
    if (oldMarker) {
      oldMarker.remove()
      this.featureMarkers.delete(featureId)
    }

    // If no shape component exists, create it (for compatibility)
    if (!shapeComponent) {
      const feature = await tripFeatureManager.getFeature(featureId)
      if (feature) {
        this.renderFeatureOnMap(feature)
      }
    }
  }

  override render(): unknown {
    // Set host attributes for CSS targeting
    if (this.hasLocationData) {
      this.setAttribute('has-location-data', '')
    } else {
      this.removeAttribute('has-location-data')
    }
    if (this.preview) {
      this.setAttribute('preview', '')
    }

    return html`
      <div class="map-wrapper">
        <div class="drop-overlay ${this.showDropOverlay ? 'visible' : ''}">
          <div class="drop-overlay-inner">
            <span class="material-icons">upload_file</span>
            <span>Drop to import GPX, KML, KMZ, or GeoJSON</span>
          </div>
        </div>
        <div class="map-container">
          <div id="mapid" ${ref(this.mapRef)}></div>
          ${this.isLoading ? html`<div class="map-loading">Loading map...</div>` : ''}
        </div>

        <!-- Right Side Control Group -->
        <div class="right-controls-group">
          <mode-fab
            .activeMode=${this.mapMode}
            .locationPermissionGranted=${this.locationPermissionGranted}
            @mode-changed=${this.handleModeChanged}
            @toggle-layers=${this.toggleLayerSwitcher}
          ></mode-fab>
        </div>

        <!-- Map "Key" FAB: upper-right, legend + live source/model per visible layer -->
        <layer-key-fab
          .map=${this.map}
          .revision=${this._v}
          .overlays=${[...this.legendOverlays.values()]}
        ></layer-key-fab>

        <!-- Unified Zoom Controls Group - Bottom Left -->
        <div class="zoom-controls-group">
          <button
            class="zoom-button zoom-only"
            @click=${this.zoomIn}
            aria-label="Zoom in"
            title="Zoom in"
          >
            <span class="material-icons">add</span>
          </button>
          <button
            class="zoom-button zoom-only"
            @click=${this.zoomOut}
            aria-label="Zoom out"
            title="Zoom out"
          >
            <span class="material-icons">remove</span>
          </button>
          ${this.followPosition || (this.locationPermissionGranted && this.hasLocationData)
            ? html`
                <button
                  class="zoom-button location-zoom-button ${this.isFollowingUser ? 'active' : ''}"
                  @click=${this.toggleFollowUser}
                  title="${this.isFollowingUser
                    ? 'Following location — tap to stop'
                    : 'Follow my location'}"
                >
                  <span class="material-icons"
                    >${this.isFollowingUser ? 'my_location' : 'location_searching'}</span
                  >
                </button>
              `
            : ''}
          <button
            class="zoom-button"
            @click=${this.toggle3dMode}
            title="${this.is3dMode ? 'Switch to 2D' : 'Switch to 3D'}"
          >
            <span class="material-icons">${this.is3dMode ? '2d' : '3d'}</span>
          </button>
        </div>

        <!-- Layer Switcher Drawer -->
        <layer-switcher
          ?open=${this.showLayerSwitcher}
          .planId=${this.planId}
          @close=${this.closeLayerSwitcher}
          @layer-changed=${this.changeBaseLayer}
          @overlay-added=${this.addOverlayLayer}
          @overlay-removed=${this.removeOverlayLayer}
          @overlay-legend-add=${this.registerOverlayLegend}
          @overlay-legend-remove=${this.unregisterOverlayLegend}
          @overlay-opacity-changed=${this.handleOverlayOpacityChanged}
          @reorder-overlays=${this.handleReorderOverlays}
          @layer-visibility-changed=${this.handleLayerVisibilityChanged}
          @layer-deleted=${this.handleLayerDeleted}
          @feature-deleted=${this.handleLayerFeatureDeleted}
          @file-imported=${this.handleFileImported}
          @zoom-to-feature=${this.handleZoomToFeature}
        ></layer-switcher>

        <cairn-context-bar
          .mapMode=${this.mapMode}
          .drawSubContext=${this.drawSubContext}
          .creatingFeature=${this.creatingFeature}
          .snapTrails=${this.linePolygonDrawingManager.snapTrailsOn}
          .snapTerrain=${this.linePolygonDrawingManager.snapTerrainOn}
          .mode=${this.contextMode}
          .currentLocation=${this.currentLocation}
          .currentElevation=${this.currentElevation}
          .currentHeading=${this.deviceHeading}
          .currentVelocity=${this.currentVelocity}
          .unitSystem=${this.unitSystem}
          .locationPermissionGranted=${this.locationPermissionGranted}
          .isRecording=${this.isRecording}
          .currentActivity=${this.currentActivity}
          .selectedFeatures=${this.selectedFeatures}
          .splitEnabled=${this.splitEnabled}
          .joinPreview=${this.joinPreviewState !== null}
          .overlayFeatureCount=${selectionService.overlayFeatureCount}
          .hasPlanContext=${!!this.planId}
          .selectedTool=${tripFeatureManager.getSelectedTool()}
          .activeStyleProperty=${this.styleEditorManager.layerPickerOpen
            ? 'layer'
            : this.styleEditorManager.tagEditorOpen
              ? 'tags'
              : this.styleEditorManager.labelEditorOpen
                ? 'label'
                : this.styleEditorManager.markerEditorOpen
                  ? 'marker'
                  : this.styleEditorManager.strokeEditorOpen
                    ? this.styleEditorManager.strokeEditorIsLine
                      ? 'lineStroke'
                      : 'stroke'
                    : this.styleEditorManager.colorPickerOpen
                      ? this.styleEditorManager.colorPickerProperty
                      : this.styleEditorManager.strokeStyleOpen
                        ? this.styleEditorManager.strokeStyleProperty
                        : undefined}
          .measureDistance=${this.measureDistance}
          .measureBearing=${this.measureBearing}
          .measureArea=${this.measureArea}
          .measurePerimeter=${this.measurePerimeter}
          .measureType=${this.measureType}
          .measureLocation=${this.measureLocation}
          .timelineState=${this.timelineState}
          .routeProfile=${this.routeProfile}
          .trackProfile=${this.trackProfile}
          .currentRouteDistance=${this.currentRouteDistance}
          .routeWaypoints=${this.routeWaypoints}
          .distanceTime=${this.distanceTime}
          .aspectCounts=${this.aspectCounts}
          @timeline-play=${this.handleTimelinePlay}
          @timeline-pause=${this.handleTimelinePause}
          @timeline-reset=${this.handleTimelineReset}
          @timeline-scrub=${this.handleTimelineScrub}
          @timeline-range-changed=${this.handleTimelineRangeChanged}
          @timeline-speed-changed=${this.handleTimelineSpeedChanged}
          @timeline-loop-toggled=${this.handleTimelineLoopToggled}
          @tool-selected=${this.handleToolSelected}
          @drawing-mode-entered=${this.handleDrawingModeEntered}
          @drawing-mode-exited=${this.handleDrawingModeExited}
          @toggle-recording=${this.toggleRecording}
          @feature-deleted=${this.handleFeatureDeleted}
          @feature-extend=${this.handleFeatureExtend}
          @feature-split=${this.handleFeatureSplit}
          @feature-return=${this.handleFeatureReturn}
          @selection-cleared=${this.handleSelectionCleared}
          @open-style-editor=${this.handleOpenStyleEditor}
          @delete-selected=${this.handleDeleteSelected}
          @join-preview=${this.handleJoinPreview}
          @join-confirmed=${this.handleJoinConfirmed}
          @join-cancelled=${this.handleJoinCancelled}
          @edit-saved=${this.handleEditSaved}
          @edit-cancelled=${this.handleEditCancelled}
          @create-undo=${this.handleCreateUndo}
          @create-redo=${this.handleCreateRedo}
          @create-out-and-back=${this.handleCreateOutAndBack}
          @create-return=${this.handleCreateReturn}
          @create-finish=${this.handleCreateFinish}
          @create-cancel=${this.handleCreateCancel}
          @snap-toggled=${this.handleSnapToggled}
          @add-to-plan=${this.handleAddToPlan}
          @create-plan-from-selection=${this.handleCreatePlanFromSelection}
          @measure-clear=${this.handleMeasureClear}
          @chart-cursor=${this.handleChartCursor}
        ></cairn-context-bar>
        ${this.styleEditorManager.colorPickerOpen
          ? html`
              <color-picker-dropdown
                style="position: absolute; top: ${this.styleEditorManager
                  .dropdownTop}px; left: ${this.styleEditorManager.dropdownLeft}px; z-index: 2000;"
                .color=${this.styleEditorManager.colorPickerColor}
                .opacity=${this.styleEditorManager.colorPickerOpacity}
                .showPattern=${this.styleEditorManager.colorPickerProperty === 'fillColor'}
                .pattern=${this.styleEditorManager.colorPickerPattern}
                @color-changed=${this.handleColorChanged}
                @pattern-changed=${this.handleFillPatternChanged}
                @dropdown-close=${this.handleColorPickerClose}
              ></color-picker-dropdown>
            `
          : ''}
        ${this.styleEditorManager.strokeStyleOpen
          ? html`
              <stroke-style-dropdown
                style="position: absolute; top: ${this.styleEditorManager
                  .dropdownTop}px; left: ${this.styleEditorManager.dropdownLeft}px; z-index: 2000;"
                .selected=${this.styleEditorManager.strokeStyleSelected}
                @style-changed=${this.handleStrokeStyleChanged}
                @dropdown-close=${this.handleStrokeStyleClose}
              ></stroke-style-dropdown>
            `
          : ''}
        ${this.styleEditorManager.strokeEditorOpen
          ? html`
              <stroke-editor-dropdown
                style="position: absolute; top: ${this.styleEditorManager
                  .dropdownTop}px; left: ${this.styleEditorManager.dropdownLeft}px; z-index: 2000;"
                .color=${this.styleEditorManager.strokeEditorColor}
                .opacity=${this.styleEditorManager.strokeEditorOpacity}
                .width=${this.styleEditorManager.strokeEditorWidth}
                .pattern=${this.styleEditorManager.strokeEditorPattern}
                @stroke-color-changed=${this.handleStrokeEditorColorChanged}
                @stroke-width-changed=${this.handleStrokeEditorWidthChanged}
                @stroke-pattern-changed=${this.handleStrokeEditorPatternChanged}
              ></stroke-editor-dropdown>
            `
          : ''}
        ${this.styleEditorManager.markerEditorOpen
          ? html`
              <marker-editor-dropdown
                style="position: absolute; top: ${this.styleEditorManager
                  .dropdownTop}px; left: ${this.styleEditorManager.dropdownLeft}px; z-index: 2000;"
                .markerType=${this.styleEditorManager.markerEditorType}
                .markerIcon=${this.styleEditorManager.markerEditorIcon}
                .markerSize=${this.styleEditorManager.markerEditorSize}
                .markerPosition=${this.styleEditorManager.markerEditorPosition}
                @marker-changed=${this.handleMarkerChanged}
              ></marker-editor-dropdown>
            `
          : ''}
        ${this.styleEditorManager.labelEditorOpen
          ? html`
              <label-editor-dropdown
                style="position: absolute; top: ${this.styleEditorManager
                  .dropdownTop}px; left: ${this.styleEditorManager.dropdownLeft}px; z-index: 2000;"
                .labelText=${this.styleEditorManager.labelEditorText}
                .labelColor=${this.styleEditorManager.labelEditorColor}
                .labelVisible=${this.styleEditorManager.labelEditorVisible}
                .verticalAlign=${this.styleEditorManager.labelEditorVerticalAlign}
                .horizontalAlign=${this.styleEditorManager.labelEditorHorizontalAlign}
                .fontSize=${this.styleEditorManager.labelEditorFontSize}
                .fontFamily=${this.styleEditorManager.labelEditorFontFamily}
                @label-changed=${this.handleLabelChanged}
              ></label-editor-dropdown>
            `
          : ''}
        ${this.styleEditorManager.tagEditorOpen
          ? html`
              <tag-editor-dropdown
                style="position: absolute; top: ${this.styleEditorManager
                  .dropdownTop}px; left: ${this.styleEditorManager.dropdownLeft}px; z-index: 2000;"
                .tags=${this.styleEditorManager.tagEditorTags}
                @tags-changed=${this.handleTagsChanged}
              ></tag-editor-dropdown>
            `
          : ''}
        ${this.styleEditorManager.layerPickerOpen
          ? html`
              <layer-picker-dropdown
                style="position: absolute; top: ${this.styleEditorManager
                  .dropdownTop}px; left: ${this.styleEditorManager.dropdownLeft}px; z-index: 2000;"
                .featureId=${this.styleEditorManager.layerPickerFeatureId}
                .selectedLayerId=${this.selectedFeatures[0]?.layerId}
                @layer-selected=${this.handleLayerSelected}
              ></layer-picker-dropdown>
            `
          : ''}
        ${this.routeId
          ? html`
              <button
                class="zoom-button import-features-btn"
                style="position: absolute; bottom: 16px; left: 50%; transform: translateX(-50%); width: auto; padding: 8px 16px; border-radius: 20px; display: flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 600; background: rgba(30,41,59,0.9); color: white; border: 1px solid rgba(255,255,255,0.15); backdrop-filter: blur(10px); cursor: pointer; z-index: 1000;"
                @click=${() => {
                  void import('../trips/plan-feature-import.js')
                  this.showImportPanel = true
                }}
              >
                <span class="material-icons" style="font-size: 18px;">download</span>
                Import Features
              </button>
              <plan-feature-import
                .routeId=${this.routeId}
                .open=${this.showImportPanel}
                @features-imported=${this.handleFeaturesImported}
                @close-import=${() => {
                  this.showImportPanel = false
                }}
              ></plan-feature-import>
            `
          : ''}
        ${this.routePlanAddingWaypoints
          ? html`
              <div class="route-plan-panel">
                <div class="route-plan-header">Route Points</div>
                <div class="route-plan-option-stats" style="padding: 4px 12px; opacity: 0.8;">
                  ${1 + this.routePlanWaypoints.length + 1} points (start +
                  ${this.routePlanWaypoints.length}
                  waypoint${this.routePlanWaypoints.length !== 1 ? 's' : ''} + end)
                </div>
                <div class="route-plan-actions">
                  <button class="route-plan-save" @click=${() => void this.executeRoutePlan()}>
                    <span class="material-icons" style="font-size:16px">route</span>
                    Plan Route
                  </button>
                  ${this.routePlanWaypoints.length > 0
                    ? html`<button class="route-plan-cancel" @click=${this.undoLastWaypoint}>
                        Undo
                      </button>`
                    : ''}
                  <button class="route-plan-cancel" @click=${this.cancelRoutePlan}>Cancel</button>
                </div>
              </div>
            `
          : ''}
        ${this.routePlanComputing
          ? html`
              <div class="route-plan-panel">
                <div class="route-plan-header">
                  <span class="material-icons route-plan-spinner">sync</span>
                  Planning routes...
                </div>
                <button class="route-plan-cancel" @click=${this.cancelRoutePlan}>Cancel</button>
              </div>
            `
          : ''}
        ${this.routePlanResults.length > 0
          ? html`
              <div class="route-plan-panel">
                <div class="route-plan-header">Route Alternatives</div>
                ${this.routePlanResults.map(
                  (r, i) => html`
                    <button
                      class="route-plan-option ${i === this.routePlanSelectedIndex
                        ? 'selected'
                        : ''}"
                      @click=${() => this.selectRouteAlternative(i)}
                    >
                      <span
                        class="route-plan-swatch"
                        style="background:${CairnMap.ROUTE_COLORS[r.strategy]}"
                      ></span>
                      <div class="route-plan-option-info">
                        <div class="route-plan-option-name">
                          ${CairnMap.ROUTE_LABELS[r.strategy]}
                        </div>
                        <div class="route-plan-option-stats">
                          ${(r.distanceMeters / 1000).toFixed(1)} km &middot;
                          ${r.estimatedTimeMinutes >= 60
                            ? `${Math.floor(r.estimatedTimeMinutes / 60)}h ${Math.round(r.estimatedTimeMinutes % 60)}m`
                            : `${Math.round(r.estimatedTimeMinutes)}m`}
                          &middot; +${Math.round(r.elevationGainMeters)}m
                          /-${Math.round(r.elevationLossMeters)}m
                        </div>
                      </div>
                    </button>
                  `
                )}
                <div class="route-plan-actions">
                  <button class="route-plan-save" @click=${this.saveSelectedRoute}>
                    <span class="material-icons" style="font-size:16px">check</span>
                    Save Route
                  </button>
                  <button class="route-plan-cancel" @click=${this.cancelRoutePlan}>Cancel</button>
                </div>
              </div>
            `
          : ''}
        <confirm-dialog></confirm-dialog>

        ${this.contextOverlayEl
          ? html`
              <div
                class="context-overlay ${this.overlayExpanded ? 'expanded' : ''} ${this
                  .overlayDragging
                  ? 'dragging'
                  : ''}"
                style="transform: translateY(${this.overlayTranslateY}px)"
              >
                <div
                  class="context-overlay-header"
                  @pointerdown=${this.onOverlayDragStart}
                  @pointermove=${this.onOverlayDragMove}
                  @pointerup=${this.onOverlayDragEnd}
                  @pointercancel=${this.onOverlayDragEnd}
                >
                  <div class="context-overlay-grabber"></div>
                  <div class="context-overlay-titlerow">
                    <span class="context-overlay-title">Details</span>
                    <button class="context-overlay-close" @click=${this.handleOverlayBack}>
                      <span class="material-icons">close</span>
                    </button>
                  </div>
                </div>
                <div class="context-overlay-body">${this.contextOverlayEl}</div>
              </div>
            `
          : nothing}
        <import-dialog id="drop-import-dialog"></import-dialog>
      </div>
    `
  }

  private handleOverlayBack(): void {
    window.history.back()
  }

  // ─── Mobile detail drawer drag ───────────────────────────────────────
  // translateY = 0 is fully expanded; overlayPeekT leaves a peek showing at the
  // bottom; the sheet height (off-screen) closes it. The whole header drags, and
  // a flick (velocity) snaps open/closed without needing to drag past a midpoint.

  private overlayHeight(): number {
    const el = this.renderRoot.querySelector('.context-overlay')
    return el?.clientHeight ?? this.clientHeight ?? window.innerHeight
  }

  private computeOverlayPeek(): number {
    const h = this.overlayHeight()
    const peekVisible = Math.min(h * 0.5, 380)
    return Math.max(0, h - peekVisible)
  }

  /** Open the drawer at peek height with a slide-up animation. */
  private openMobileOverlayAtPeek(): void {
    // Start just off the bottom, then animate up to the peek on the next frame.
    this.overlayExpanded = false
    this.overlayTranslateY = this.overlayHeight() || window.innerHeight
    requestAnimationFrame(() => {
      this.overlayPeekT = this.computeOverlayPeek()
      this.overlayTranslateY = this.overlayPeekT
    })
  }

  private onOverlayDragStart(e: PointerEvent): void {
    // Let the close button click through instead of starting a drag.
    if ((e.target as HTMLElement).closest('.context-overlay-close')) return
    this.overlayDragStartY = e.clientY
    this.overlayDragBaseT = this.overlayTranslateY
    this.overlayDragging = true
    this.overlayLastY = e.clientY
    this.overlayLastT = performance.now()
    this.overlayVY = 0
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  }

  private onOverlayDragMove(e: PointerEvent): void {
    if (this.overlayDragStartY === null) return
    const h = this.overlayHeight()
    const next = Math.min(
      h,
      Math.max(0, this.overlayDragBaseT + (e.clientY - this.overlayDragStartY))
    )
    const now = performance.now()
    const dt = now - this.overlayLastT
    if (dt > 0) this.overlayVY = (e.clientY - this.overlayLastY) / dt
    this.overlayLastY = e.clientY
    this.overlayLastT = now
    this.overlayTranslateY = next
  }

  private onOverlayDragEnd(e: PointerEvent): void {
    if (this.overlayDragStartY === null) return
    this.overlayDragStartY = null
    this.overlayDragging = false
    try {
      ;(e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId)
    } catch {
      /* pointer already released */
    }

    const peekT = this.overlayPeekT || this.computeOverlayPeek()
    const peekVisible = this.overlayHeight() - peekT
    const FLICK = 0.6 // px/ms
    const vy = this.overlayVY

    if (vy < -FLICK) {
      this.expandOverlay()
    } else if (vy > FLICK) {
      // Flick down: from well above the peek, settle to peek; otherwise close.
      if (this.overlayTranslateY < peekT * 0.6) this.settleOverlayAtPeek()
      else this.closeMobileOverlay()
    } else if (this.overlayTranslateY <= peekT / 2) {
      this.expandOverlay()
    } else if (this.overlayTranslateY > peekT + peekVisible * 0.5) {
      this.closeMobileOverlay()
    } else {
      this.settleOverlayAtPeek()
    }
  }

  private expandOverlay(): void {
    this.overlayExpanded = true
    this.overlayTranslateY = 0
  }

  private settleOverlayAtPeek(): void {
    this.overlayExpanded = false
    this.overlayPeekT = this.computeOverlayPeek()
    this.overlayTranslateY = this.overlayPeekT
  }

  /** Slide the drawer down, then consume its history entry to remove it. */
  private closeMobileOverlay(): void {
    this.overlayExpanded = false
    this.overlayTranslateY = this.overlayHeight() || window.innerHeight
    window.setTimeout(() => {
      if (this.contextOverlayEl) window.history.back()
    }, 200)
  }

  // ─── Route Planning ──────────────────────────────────────────────────

  private static readonly ROUTE_COLORS: Record<RouteStrategy, string> = {
    fastest: '#2563eb',
    'most-trail': '#16a34a',
    'most-direct': '#ea580c',
  }

  private static readonly ROUTE_LABELS: Record<RouteStrategy, string> = {
    fastest: 'Fastest',
    'most-trail': 'Most Trail',
    'most-direct': 'Most Direct',
  }

  private startRoutePlanning(objective: [number, number]): void {
    this.cleanupRoutePlan()
    this.routePlanObjective = objective
    this.routePlanningActive = true

    const el = document.createElement('div')
    el.style.cssText =
      'width:14px;height:14px;background:#ef4444;border:2px solid white;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,0.4);'
    const marker = new maplibregl.Marker({ element: el }).setLngLat(objective).addTo(this.map!)
    this.routePlanMarkers.push(marker)

    showToast('info', 'Click the map to set your start point')
  }

  private addRoutePlanPoint(point: [number, number]): void {
    if (!this.map) return

    if (!this.routePlanStart) {
      this.routePlanStart = point
      this.routePlanAddingWaypoints = true

      const el = document.createElement('div')
      el.style.cssText =
        'width:14px;height:14px;background:#2563eb;border:2px solid white;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,0.4);'
      const marker = new maplibregl.Marker({ element: el }).setLngLat(point).addTo(this.map)
      this.routePlanMarkers.push(marker)

      showToast('info', 'Click to add waypoints, or click "Plan Route" to compute')
    } else {
      this.routePlanWaypoints.push(point)

      const el = document.createElement('div')
      el.style.cssText =
        'width:12px;height:12px;background:#16a34a;border:2px solid white;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,0.4);'
      const marker = new maplibregl.Marker({ element: el }).setLngLat(point).addTo(this.map)
      this.routePlanMarkers.push(marker)
    }
  }

  private undoLastWaypoint(): void {
    if (this.routePlanWaypoints.length === 0) return
    this.routePlanWaypoints.pop()
    const marker = this.routePlanMarkers.pop()
    marker?.remove()
  }

  private async executeRoutePlan(): Promise<void> {
    if (!this.routePlanObjective || !this.routePlanStart || !this.map) return

    this.routePlanningActive = false
    this.routePlanAddingWaypoints = false
    this.routePlanComputing = true

    const waypoints: [number, number][] = [
      this.routePlanStart,
      ...this.routePlanWaypoints,
      this.routePlanObjective,
    ]

    try {
      const results = await routePlannerService.planAlternatives(waypoints, (strategy, percent) => {
        log.debug(`Route ${strategy}: ${percent.toFixed(0)}%`)
      })

      if (results.length === 0) {
        showToast('error', 'No route found between these points')
        this.cleanupRoutePlan()
        return
      }

      this.routePlanResults = results
      this.routePlanSelectedIndex = 0
      this.displayRouteResults(results)
    } catch (err) {
      log.error('Route planning failed:', err)
      showToast('error', `Route planning failed: ${(err as Error).message}`)
      this.cleanupRoutePlan()
    } finally {
      this.routePlanComputing = false
    }
  }

  private displayRouteResults(results: RouteResult[]): void {
    if (!this.map) return

    for (const [i, result] of results.entries()) {
      const sourceId = `route-plan-${result.strategy}`
      const layerId = `route-plan-layer-${result.strategy}`
      const isSelected = i === 0

      this.map.addSource(sourceId, {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: result.path,
          },
        },
      })

      this.map.addLayer({
        id: layerId,
        type: 'line',
        source: sourceId,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': CairnMap.ROUTE_COLORS[result.strategy],
          'line-width': isSelected ? 5 : 3,
          'line-opacity': isSelected ? 0.9 : 0.5,
        },
      })

      this.routePlanSourceIds.push(sourceId)
      this.routePlanLayerIds.push(layerId)
    }
  }

  private selectRouteAlternative(index: number): void {
    if (!this.map || index < 0 || index >= this.routePlanResults.length) return

    this.routePlanSelectedIndex = index

    for (const [i, result] of this.routePlanResults.entries()) {
      const layerId = `route-plan-layer-${result.strategy}`
      if (!this.map.getLayer(layerId)) continue

      const isSelected = i === index
      this.map.setPaintProperty(layerId, 'line-width', isSelected ? 5 : 3)
      this.map.setPaintProperty(layerId, 'line-opacity', isSelected ? 0.9 : 0.5)
    }
  }

  private async saveSelectedRoute(): Promise<void> {
    const result = this.routePlanResults[this.routePlanSelectedIndex]
    if (!result) return

    const points: FeatureLatLng[] = result.path.map(([lng, lat]) => ({ lat, lng }))
    const hours = Math.floor(result.estimatedTimeMinutes / 60)
    const mins = Math.round(result.estimatedTimeMinutes % 60)
    const timeStr = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`
    const distKm = (result.distanceMeters / 1000).toFixed(1)

    await tripFeatureManager.createLineFeature(points, {
      name: `Route (${CairnMap.ROUTE_LABELS[result.strategy]})`,
      properties: {
        routeType: 'planned',
        routeStrategy: result.strategy,
        estimatedTimeMinutes: result.estimatedTimeMinutes,
        elevationGain: result.elevationGainMeters,
        elevationLoss: result.elevationLossMeters,
        distanceMeters: result.distanceMeters,
        algorithm: 'astar-tobler',
      },
      description: `${distKm} km, ${timeStr}, +${Math.round(result.elevationGainMeters)}m / -${Math.round(result.elevationLossMeters)}m`,
    })

    showToast('success', `${CairnMap.ROUTE_LABELS[result.strategy]} route saved to plan`)
    this.cleanupRoutePlan()
  }

  private cancelRoutePlan(): void {
    routePlannerService.cancel()
    this.cleanupRoutePlan()
  }

  private cleanupRoutePlan(): void {
    for (const marker of this.routePlanMarkers) marker.remove()
    this.routePlanMarkers = []

    if (this.map) {
      for (const layerId of this.routePlanLayerIds) {
        if (this.map.getLayer(layerId)) this.map.removeLayer(layerId)
      }
      for (const sourceId of this.routePlanSourceIds) {
        if (this.map.getSource(sourceId)) this.map.removeSource(sourceId)
      }
    }
    this.routePlanLayerIds = []
    this.routePlanSourceIds = []

    this.routePlanObjective = null
    this.routePlanStart = null
    this.routePlanWaypoints = []
    this.routePlanningActive = false
    this.routePlanAddingWaypoints = false
    this.routePlanComputing = false
    this.routePlanResults = []
    this.routePlanSelectedIndex = -1
  }

  private handleFeaturesImported(e: CustomEvent<{ features: Feature[] }>): void {
    this.showImportPanel = false
    const features = e.detail.features
    for (const feature of features) {
      this.renderFeatureOnMap(feature)
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cairn-map': CairnMap
  }
}
